import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import FloatingNavButton from '@/components/FloatingNavButton';
import { seedTestSurvey } from './seedTestSurvey';
import { BrandButton as Button, SearchableSelect, RolesSelector, SurveySearchFilters } from "@/components";
import { useAuthenticatedApi } from "@/utils/callApi";
import Toast from "@/components/Toast";
import useToast from "@/components/useToast";
import ConfirmModal from "@/components/ConfirmModal";
import PublishSurveyDialog from "@/components/PublishSurveyDialog";
import AdvancedTextEditor from "@/components/AdvancedTextEditor";
import SurveyQuestionPreview from "@/components/SurveyQuestionPreview";
import FormulaBuilder from "@/components/FormulaBuilder";
import useFormulaBuilder from "@/hooks/useFormulaBuilder";
import { FaArrowUp, FaFileAlt, FaListOl, FaQuestionCircle } from "react-icons/fa";
import TagsInput from "@/components/TagsInput";
import { Accordion } from "react-bootstrap";
import { repairInputListQuestionHeritage } from "./heritageUtils";

// Fast tooltip badge — shows tooltip instantly on hover (title attr has ~500ms OS delay)
function TipBadge({ tooltip, badgeStyle, children }) {
  const [visible, setVisible] = React.useState(false);
  return (
    <span
      style={{ position: 'relative', display: 'inline-block' }}
      onMouseEnter={() => setVisible(true)}
      onMouseLeave={() => setVisible(false)}
    >
      <span style={badgeStyle}>{children}</span>
      {visible && (
        <span style={{
          position: 'absolute',
          bottom: 'calc(100% + 6px)',
          left: '50%',
          transform: 'translateX(-50%)',
          background: '#492c28',
          color: '#fff',
          padding: '5px 9px',
          borderRadius: '6px',
          fontSize: 'var(--font-base)',
          whiteSpace: 'normal',
          minWidth: '300px',
          maxWidth: '500px',
          zIndex: 9999,
          pointerEvents: 'none',
          textAlign: 'center',
          lineHeight: '1.4',
          boxShadow: '0 2px 8px rgba(0,0,0,0.25)',
        }}>
          {tooltip}
        </span>
      )}
    </span>
  );
}

// Format numeric strings with thousands separators for display
const formatNumberDisplay = (value) => {
  if (value === '' || value === null || value === undefined) return '';
  const str = String(value).replace(/,/g, '');
  if (str === '' || str === '-') return str;
  const parts = str.split('.');
  const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
  return parts.length > 1 ? `${intPart}.${parts.slice(1).join('.')}` : intPart;
};

export default function SurveyBuilder() {
  const navigate = useNavigate();
  const { secureHelpers, isLoading: _isLoading, error: _error } = useAuthenticatedApi();
  const { toast, showToast, hideToast } = useToast();
  const [surveyTitle, setSurveyTitle] = useState("");
  const [questions, setQuestions] = useState([]);
  const [steps, setSteps] = useState([]);
  const [stepTitle, setStepTitle] = useState("");
  const [stepDescription, setStepDescription] = useState("");
  const [selectedStep, setSelectedStep] = useState(null);
  const [savedSurveys, setSavedSurveys] = useState([]);
  const [selectedSurveyId, setSelectedSurveyId] = useState(null);
  const [showLoadDialog, setShowLoadDialog] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [dialogType, setDialogType] = useState(""); // "current" or "closed"
  const [currentSurveyId, setCurrentSurveyId] = useState(null);
  const [surveySaved, setSurveySaved] = useState(false);
  const [isSaving, setIsSaving] = useState(false); // Prevent double-clicks
  const [isAddingStep, setIsAddingStep] = useState(false); // Prevent double-clicks on Add Step
  const [isAddingQuestion, setIsAddingQuestion] = useState(false); // Prevent double-clicks on Add Question
  const [surveyInstructions, setSurveyInstructions] = useState("");
  const [instructionsSaved, setInstructionsSaved] = useState(false);
  const [questionSaved, setQuestionSaved] = useState(false);
  const [isCreatingStub, setIsCreatingStub] = useState(false);
  const [surveyTags, setSurveyTags] = useState([]);
  const [availableTags, setAvailableTags] = useState([]);
  const [surveyRoles, setSurveyRoles] = useState([]);
  // Survey list search/filter state (used by load dialog)
  const [surveyListLoading, setSurveyListLoading] = useState(false);
  const [activeTagFilter, setActiveTagFilter] = useState([]);
  const [currentDateRange, setCurrentDateRange] = useState({ modifiedAfter: null, modifiedBefore: null });
  
  // Publish states
  const [showPublishDialog, setShowPublishDialog] = useState(false);
  const [hospices, setHospices] = useState([]);
  const [selectedHospices, setSelectedHospices] = useState([]);
  const [publishToAll, setPublishToAll] = useState(true);
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [autoClose, setAutoClose] = useState(false);

  // Form state for building a question
  const [questionType, setQuestionType] = useState("singleInput");
  const [questionText, setQuestionText] = useState("");
  const [questionDescription, setQuestionDescription] = useState("");
  const [isRequired, setIsRequired] = useState(false);
  const [editingQuestionId, setEditingQuestionId] = useState(null); // Track if editing existing question
  const [editingOriginalStepIndex, setEditingOriginalStepIndex] = useState(null); // Track which step question came from
  const [isEditing, setIsEditing] = useState(false); // Store flag to check for if a question is being edited for use in setting UI.
  
  // Type-specific fields
  const [currentSubQuestion, setCurrentSubQuestion] = useState(""); // Input for single subquestion
  const [subQuestions, setSubQuestions] = useState([]); // Array of added subquestions
  const [inputType, setInputType] = useState(""); // Start blank - user must select
  const [showTotal, setShowTotal] = useState(false);
  const [showGrandTotal, setShowGrandTotal] = useState(false); // For multi-column: show overall total (sum of all columns)
  const [grandTotalColumns, setGrandTotalColumns] = useState([]); // Array of column indices to include in Grand Total
  const [multipleSelectAllowed, setMultipleSelectAllowed] = useState(false);
  const [isOrderedSelection, setIsOrderedSelection] = useState(false); // For ranked/ordered MultipleChoice
  const [multipleSelectLimit, setMultipleSelectLimit] = useState(""); // Max selections allowed (e.g., "3" for "top 3")
  const [includeInTitle, setIncludeInTitle] = useState(false); // Whether selected choices update the survey context title
  const [showFloatingMenu, setShowFloatingMenu] = useState(false);
  const [expandedButton, setExpandedButton] = useState(null);

  // Constraints and Conditions state
  const [constraints, setConstraints] = useState([]);
  const [conditions, setConditions] = useState([]);
  const [minValue, setMinValue] = useState("");
  const [maxValue, setMaxValue] = useState("");
  const [sumEqualsQuestionId, setSumEqualsQuestionId] = useState("");
  const [sumEqualsMode, setSumEqualsMode] = useState("question"); // 'question' | 'fixed'
  const [sumEqualsFixedValue, setSumEqualsFixedValue] = useState("");
  const [sumEqualsQuestionType, setSumEqualsQuestionType] = useState(""); // Type of selected question for sum validation
  const [sumEqualsQuestionItems, setSumEqualsQuestionItems] = useState([]); // Items if inputList
  const [sumEqualsReferenceType, setSumEqualsReferenceType] = useState("total"); // 'total', 'grandtotal', or 'item-{index}'
  const [sumEqualsHasGrandTotal, setSumEqualsHasGrandTotal] = useState(false); // Whether selected question has Custom Total
  const [sumEqualsValidationScope, setSumEqualsValidationScope] = useState("entire"); // 'entire' or 'item'
  const [sumEqualsItemIndex, setSumEqualsItemIndex] = useState("0"); // Which row/item to validate when scope is 'item'
  const [equalsQuestionId, setEqualsQuestionId] = useState("");
  const [equalsQuestionType, setEqualsQuestionType] = useState(""); // Type of selected question for equals validation
  const [equalsQuestionItems, setEqualsQuestionItems] = useState([]); // Items if inputList
  const [equalsQuestionReferenceType, setEqualsQuestionReferenceType] = useState("total"); // 'total' or 'item-{index}' // For cross-question validation (e.g., 2025 headcount must equal establishment)
  const [conditionQuestionId, setConditionQuestionId] = useState("");
  const [conditionQuestionItems, setConditionQuestionItems] = useState([]); // Items/choices if InputList/MultipleChoice
  const [conditionReferenceMode, setConditionReferenceMode] = useState("direct"); // "direct", "item", "sum"
  const [conditionItemIndex, setConditionItemIndex] = useState(""); // Which specific item (for "item" mode)
  const [conditionSumIndices, setConditionSumIndices] = useState([]); // Which items to sum (for "sum" mode)
  const [conditionOperator, setConditionOperator] = useState("equals");
  const [conditionValue, setConditionValue] = useState("");
  const [conditionValueType, setConditionValueType] = useState(""); // empty = show placeholder, text, number, or boolean
  const [columnHeadings, setColumnHeadings] = useState([]); // For inputList tabular display
  const [layoutDirection, setLayoutDirection] = useState("vertical"); // "vertical" or "horizontal" for inputList
  const [totalFieldLabel, setTotalFieldLabel] = useState(""); // Label for Total field (e.g., "Medical Total")
  const [totalFieldNote, setTotalFieldNote] = useState(""); // Note/comment for Total field (e.g., "Should equal total staff count")
  const [sectionHeading, setSectionHeading] = useState(""); // Optional section heading that appears above the question
  const [displayAsRowLabel, setDisplayAsRowLabel] = useState(false); // Display question as row label in table instead of above
  const [displayAsHeading, setDisplayAsHeading] = useState(false); // Display question text as orange heading (like section heading)
  const [displayAsInformational, setDisplayAsInformational] = useState(false); // Display as informational text only (no input field)
  const [displayAsHorizontal, setDisplayAsHorizontal] = useState(true); // Display input horizontally (table row: label left, input right)
  
  // Formula builders — each hook instance owns its own source-picker + formula-parts state
  const refFormula = useFormulaBuilder(); // Reference Value multi-source calculator
  const [constantInput, setConstantInput] = useState(""); // Constants are unique to reference formula
  const createReferenceColumn = (heading = "") => ({
    Heading: heading,
    ReferenceSources: [],
    ReferenceFormulaParts: [],
    building: false,
    sourceQuestionId: "",
    sourceQuestionType: "",
    sourceReferenceType: "total",
    sourceItems: [],
    sourceHasGrandTotal: false,
    sourceColumnHeadings: [],
    constantInput: ""
  });
  const [referenceDisplayMode, setReferenceDisplayMode] = useState("single"); // "single" | "tableRow"
  const [referenceRowLabel, setReferenceRowLabel] = useState("");
  const [referenceColumns, setReferenceColumns] = useState([createReferenceColumn()]);
  const [referenceTemplateQuestionId, setReferenceTemplateQuestionId] = useState("");

  // Column formula validation state (column-index based, different from source-based builders)
  const [formulaParts, setFormulaParts] = useState([]);
  const [buildingFormula, setBuildingFormula] = useState(false);

  const cfFormula = useFormulaBuilder();  // inputList  "sum_formula_equals" constraint
  const sifFormula = useFormulaBuilder(); // singleInput "formula_equals"   constraint

  // Generate label for a source with questionId and referenceType
  const getSourceLabel = (source, index, fallbackText = null) => {
    const questionId = source.questionId || source.QuestionId;
    const referenceType = source.referenceType || source.ReferenceType || "total";

    const question = getAllPreviousQuestions().find(q => q.id === parseInt(questionId));
    const questionText = question
      ? `${question.text.substring(0, 30)}${question.text.length > 30 ? '...' : ''}`
      : `Question ${questionId} (not found in this survey)`;

    if (referenceType === 'grandtotal') return `${questionText} — Custom Total`;
    if (referenceType === 'total') return `${questionText} — Total`;
    if (referenceType?.startsWith('column-total-')) {
      const columnIndex = parseInt(referenceType.split('-')[2] || '0');
      let heading = `Column ${columnIndex + 1}`;
      if (question) {
        try {
          const layoutData = typeof question.layout === 'string'
            ? JSON.parse(question.layout)
            : question.layout || {};
          heading = layoutData.columnHeadings?.[columnIndex] || heading;
        } catch {
          // Source labels are best-effort only.
        }
      }
      return `${questionText} — Total of ${heading}`;
    }

    // For item references, look up the actual item label from typeParameters
    if (referenceType?.startsWith('item-')) {
      const itemIndex = parseInt(referenceType.split('-')[1] || '0');
      if (question) {
        try {
          const typeParams = typeof question.typeParameters === 'string'
            ? JSON.parse(question.typeParameters)
            : (question.typeParameters || {});
          const item = (typeParams.Questions || [])[itemIndex];
          if (item) {
            const label = typeof item === 'string' ? item : (item.Label || item.label || item.text);
            if (label) return label;
          }
        } catch {
          // Source labels are best-effort only.
        }
      }
      return fallbackText || `Source ${index + 1}`;
    }

    return fallbackText || questionText || `Source ${index + 1}`;
  };

  const normalizeReferenceSources = (sources = []) => (
    sources.map(source => ({
      QuestionId: source.QuestionId || source.questionId,
      ReferenceType: source.ReferenceType || source.referenceType || "total"
    }))
  );

  const buildReferenceFormulaString = (formulaParts = []) => (
    formulaParts.map(part => part.value).join("")
  );

  const getColumnFormulaDisplay = (column) => {
    const parts = column.ReferenceFormulaParts || [];
    const sources = column.ReferenceSources || [];
    if (parts.length === 0) return "Build your formula...";

    return parts.map(part => {
      if (part.type === "constant") return ` ${part.value} `;
      if (part.type === "source") {
        const source = sources[part.value];
        if (!source) return `Source${part.value + 1}`;
        return getSourceLabel(source, part.value);
      }
      const opMap = { "*": "×", "-": "−" };
      return ` ${opMap[part.value] ?? part.value} `;
    }).join("");
  };

  const updateReferenceColumn = (index, updater) => {
    setReferenceColumns(prev => prev.map((column, columnIndex) => {
      if (columnIndex !== index) return column;
      return typeof updater === "function" ? updater(column) : { ...column, ...updater };
    }));
  };

  const resetReferenceColumnPicker = (column) => ({
    ...column,
    sourceQuestionId: "",
    sourceQuestionType: "",
    sourceReferenceType: "total",
    sourceItems: [],
    sourceHasGrandTotal: false,
    sourceColumnHeadings: []
  });

  const getQuestionColumnHeadings = (questionId) => {
    const question = getAllPreviousQuestions().find(q => q.id === parseInt(questionId));
    if (!question) return [];
    try {
      const layoutData = typeof question.layout === "string"
        ? JSON.parse(question.layout)
        : question.layout || {};
      return layoutData.columnHeadings || [];
    } catch {
      return [];
    }
  };

  const getSavedSourceLabel = (source, allSavedQuestions = []) => {
    const questionId = source.QuestionId || source.questionId;
    const referenceType = source.ReferenceType || source.referenceType || "total";
    const question = allSavedQuestions.find(savedQuestion => savedQuestion.id === parseInt(questionId));
    const questionText = question
      ? `${question.text.substring(0, 34)}${question.text.length > 34 ? '...' : ''}`
      : `Question ${questionId} (not found in this survey)`;

    if (referenceType === "total") return `${questionText} - Total`;
    if (referenceType === "grandtotal") return `${questionText} - Custom Total`;
    if (referenceType.startsWith("column-total-")) {
      const columnIndex = parseInt(referenceType.split("-")[2] || "0", 10);
      let columnHeading = `Column ${columnIndex + 1}`;
      if (question) {
        try {
          const layoutData = typeof question.layout === "string"
            ? JSON.parse(question.layout)
            : question.layout || {};
          columnHeading = layoutData.columnHeadings?.[columnIndex] || columnHeading;
        } catch {
          columnHeading = `Column ${columnIndex + 1}`;
        }
      }
      return `${questionText} - Total of ${columnHeading}`;
    }
    if (referenceType.startsWith("item-")) {
      const itemIndex = parseInt(referenceType.split("-")[1] || "0", 10);
      return `${questionText} - Item ${itemIndex + 1}`;
    }

    return `${questionText} - ${referenceType}`;
  };

  const getInputListQuestionsWithColumns = () => (
    getAllPreviousQuestions().filter(question => {
      if (!(question.type === "inputList" || question.type === "InputList")) return false;
      return getQuestionColumnHeadings(question.id).length > 0;
    })
  );

  const createColumnsFromInputList = (questionId) => {
    const headings = getQuestionColumnHeadings(questionId);
    if (headings.length === 0) {
      showToast({
        title: "No Columns Found",
        message: "Select an InputList question that has column headings.",
        variant: "warning"
      });
      return;
    }

    setReferenceColumns(headings.map((heading, index) => ({
      ...createReferenceColumn(heading),
      ReferenceSources: [{
        questionId,
        referenceType: `column-total-${index}`
      }]
    })));
    showToast({
      title: "Columns Created",
      message: `Created ${headings.length} columns and added matching totals from the selected table.`,
      variant: "success"
    });
  };

  const addMatchingColumnTotalsFromInputList = (questionId) => {
    const headings = getQuestionColumnHeadings(questionId);
    if (headings.length === 0) {
      showToast({
        title: "No Columns Found",
        message: "Select an InputList question that has column headings.",
        variant: "warning"
      });
      return;
    }

    setReferenceColumns(prev => {
      const nextColumns = prev.length > 0 ? [...prev] : headings.map(heading => createReferenceColumn(heading));

      return nextColumns.map((column, index) => {
        if (index >= headings.length) return column;
        const nextHeading = column.Heading || headings[index] || `Column ${index + 1}`;
        return {
          ...column,
          Heading: nextHeading,
          ReferenceSources: [
            ...(column.ReferenceSources || []),
            {
              questionId,
              referenceType: `column-total-${index}`
            }
          ]
        };
      });
    });
    showToast({
      title: "Totals Added",
      message: `Added matching column totals from the selected table.`,
      variant: "success"
    });
  };

  // Helper function: Transform row labels + column headings into expanded sub-questions array
  // This separates data storage logic from UI rendering logic
  const expandMultiColumnSubQuestions = (rowLabels, columnHeadings, inputType, editingQuestionId) => {
    if (!columnHeadings || columnHeadings.length === 0) {
      // Single column - return original format
      return rowLabels.map((sq, index) => {
        let questionId = null;
        if (editingQuestionId && sq.id && sq.id < 10000) {
          questionId = sq.id;
        }
        return {
          Id: questionId,
          Label: sq.label || sq.Label,
          InputType: inputType,
          HeritageId: sq.heritageId || sq.HeritageId || null,
          Order: index
        };
      });
    }
    
    // Multi-column: create one sub-question per cell (row × column)
    const expanded = [];
    rowLabels.forEach((row, rowIdx) => {
      columnHeadings.forEach((col, colIdx) => {
        const flatIndex = rowIdx * columnHeadings.length + colIdx;
        
        // Try to preserve existing IDs when editing
        let questionId = null;
        if (editingQuestionId && row[`id_col${colIdx}`] && row[`id_col${colIdx}`] < 10000) {
          questionId = row[`id_col${colIdx}`];
        }
        
        expanded.push({
          Id: questionId,
          Label: `${row.label || row.Label} - ${col}`,
          InputType: inputType,
          // Preserve heritage per cell, not per row, so each column keeps its own chain.
          HeritageId: row[`heritage_col${colIdx}`] ?? row.heritageId ?? row.HeritageId ?? null,
          Order: flatIndex
        });
      });
    });
    return expanded;
  };

  // Use Id's with an appropriate length for the back end when creating them for new questions without existing back end id's.
  const [subQuestionIdCounter, setSubQuestionIdCounter] = useState(10000); // Start at 10000 to avoid conflicts with real IDs

  const getAdminScrollContainer = () => document.getElementById("router-outlet-wrapper");

  const scrollAdminToTop = () => {
    const scrollContainer = getAdminScrollContainer();
    if (scrollContainer) {
      scrollContainer.scrollTo({ top: 0, behavior: "smooth" });
      return;
    }

    window.scrollTo({ top: 0, behavior: "smooth" });
  };

  // Show/hide floating menu based on the admin content panel scroll.
  useEffect(() => {
    const scrollContainer = getAdminScrollContainer();
    const scrollTarget = scrollContainer || window;

    const handleScroll = () => {
      const scrollTop = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
      setShowFloatingMenu(scrollTop > 300);
    };

    handleScroll();
    scrollTarget.addEventListener("scroll", handleScroll, { passive: true });
    return () => scrollTarget.removeEventListener("scroll", handleScroll);
  }, []);

 // Load the survey ID from sessionStorage if present
      useEffect(() => {
        const loadSurveyId = sessionStorage.getItem("loadSurveyId");
        if (loadSurveyId) {
          // Clear it so it doesn't load again
          sessionStorage.removeItem("loadSurveyId");

          // Load the survey with this ID
         const loadSurveyById = async () => {
            try {
              const survey = await secureHelpers.get(`/api/surveys/${loadSurveyId}`);
            loadSurvey(survey); // use existing function to load survey data into builder
            } catch (error) {
              console.error("Failed to load survey with ID:", loadSurveyId, error);
            }
          };
          loadSurveyById();
        } else {
          // Check if returning from preview - restore survey from sessionStorage
          const previewData = sessionStorage.getItem("previewSurvey");
          const previewTimestamp = sessionStorage.getItem("previewTimestamp");
          
          if (previewData && previewTimestamp) {
            const now = Date.now();
            const age = now - parseInt(previewTimestamp, 10);
            const MAX_AGE = 10 * 60 * 1000; // 10 minutes
            
            // Only restore if preview data is recent (< 10 minutes old)
            if (age < MAX_AGE) {
              try {
                const savedSurvey = JSON.parse(previewData);
                
                // Restore the survey state
                setCurrentSurveyId(savedSurvey.id);
                setSurveyTitle(savedSurvey.title || "");
                setSteps(savedSurvey.steps || []);
                setQuestions(savedSurvey.questions || []);
                setSurveySaved(true);
                
                // Keep sessionStorage for potential re-preview
              } catch (error) {
                console.error("Failed to restore survey from preview:", error);
                sessionStorage.removeItem("previewSurvey");
                sessionStorage.removeItem("previewTimestamp");
              }
            } else {
              // Stale preview data - clear it
              sessionStorage.removeItem("previewSurvey");
              sessionStorage.removeItem("previewTimestamp");
            }
          } else if (previewData) {
            // Old preview data without timestamp - clear it
            sessionStorage.removeItem("previewSurvey");
          }
        }
      }, []); //Empty dependency array to run only once on mount

  // Confirmation modal state
  const [confirmModal, setConfirmModal] = useState({
    show: false,
    title: "",
    message: "",
    onConfirm: null,
    variant: "warning"
  });
  
  // Helper to show confirmation modal
  const showConfirm = ({ title, message, onConfirm, variant = "warning" }) => {
    return new Promise((resolve) => {
      setConfirmModal({
        show: true,
        title,
        message,
        variant,
        onConfirm: () => {
          setConfirmModal(prev => ({ ...prev, show: false }));
          resolve(true);
          if (onConfirm) onConfirm();
        }
      });
    });
  };
  
  const hideConfirm = () => {
    setConfirmModal(prev => ({ ...prev, show: false }));
  };
  
  // Clear all survey builder state (used when starting new survey or after publishing)
  const clearSurveyBuilderState = () => {
    sessionStorage.removeItem("previewSurvey");
    sessionStorage.removeItem("previewTimestamp");
    
    // Survey-level state
    setCurrentSurveyId(null);
    setSurveyTitle("");
    setSurveyInstructions("");
    setSurveyTags([]);
    setSurveyRoles([]);
    setInstructionsSaved(false);
    setSurveySaved(false);
    setSteps([]);
    setQuestions([]);
    setSelectedStep(null);
    
    // Question form state — delegate to avoid duplication
    clearQuestionForm();
  };
  
  // Add subquestion to temporary list
  const addSubQuestion = () => {
  if (!currentSubQuestion.trim()) return;
  
  // Use counter instead of timestamp (safe for Int32)
  const newId = subQuestionIdCounter;
  setSubQuestionIdCounter(prev => prev + 1);
  
  const newSubQ = {
    id: newId,
    label: currentSubQuestion.trim()
  };
  setSubQuestions([...subQuestions, newSubQ]);
  setCurrentSubQuestion("");
};

  // Remove subquestion from temporary list (index-based to avoid ID issues with loaded items)
  const removeSubQuestion = (index) => {
    setSubQuestions(prev => prev.filter((_, i) => i !== index));
  };

  // Add constraint
  const addConstraint = (type) => {
    let constraintString = "";
    
    switch(type) {
      case "required":
        if (constraints.includes("required")) return; // Already added
        setConstraints([...constraints, "required"]);
        setIsRequired(true);
        return;
        
      case "min": {
        if (!minValue.trim()) {
          showToast({ 
            title: "Validation",
            message: "Please enter a minimum value",
            variant: "warning"
          });
          return;
        }
        // Remove any existing min constraint before adding new one
        const filteredMinConstraints = constraints.filter(c => !c.startsWith('min;'));
        constraintString = `min;${minValue}`;
        setConstraints([...filteredMinConstraints, constraintString]);
        setMinValue("");
        break;
      }
        
      case "max": {
        if (!maxValue.trim()) {
          showToast({ 
            title: "Validation",
            message: "Please enter a maximum value",
            variant: "warning"
          });
          return;
        }
        // Remove any existing max constraint before adding new one
        const filteredMaxConstraints = constraints.filter(c => !c.startsWith('max;'));
        constraintString = `max;${maxValue}`;
        setConstraints([...filteredMaxConstraints, constraintString]);
        setMaxValue("");
        break;
      }
        
      case "sum_equals": {
        if (!sumEqualsQuestionId) {
          showToast({ 
            title: "Validation",
            message: "Please select a question to compare the sum against",
            variant: "warning"
          });
          return;
        }
        
        // Check if this is item-level validation (specific row)
        if (sumEqualsValidationScope === "item") {
          // Item-level validation: item_equals;{itemIndex};{questionId};{referenceType}
          const referenceType = sumEqualsQuestionItems.length > 0 ? sumEqualsReferenceType : "total";
          constraintString = `item_equals;${sumEqualsItemIndex};${sumEqualsQuestionId};${referenceType}`;
        } else {
          // Entire question validation (existing logic)
          // Build constraint string: sum_equals;{questionId} or sum_equals;{questionId};total or sum_equals;{questionId};grandtotal or sum_equals;{questionId};item-{index}
          if (sumEqualsQuestionItems.length > 0) {
            // InputList question - include reference type
            if (sumEqualsReferenceType === "total") {
              constraintString = `sum_equals;${sumEqualsQuestionId};total`;
            } else if (sumEqualsReferenceType === "grandtotal") {
              constraintString = `sum_equals;${sumEqualsQuestionId};grandtotal`;
            } else if (sumEqualsReferenceType.startsWith("item-")) {
              const itemIndex = sumEqualsReferenceType.split("-")[1];
              constraintString = `sum_equals;${sumEqualsQuestionId};item-${itemIndex}`;
            }
          } else {
            // Simple singleInput question
            constraintString = `sum_equals;${sumEqualsQuestionId}`;
          }
        }
        
        setConstraints([...constraints, constraintString]);
        setSumEqualsQuestionId("");
        setSumEqualsQuestionItems([]);
        setSumEqualsHasGrandTotal(false);
        setSumEqualsReferenceType("total");
        setSumEqualsValidationScope("entire");
        setSumEqualsItemIndex("0");
        setSumEqualsMode("question");
        setSumEqualsFixedValue("");
        break;
      }

      case "sum_equals_fixed": {
        const trimmed = sumEqualsFixedValue.trim();
        if (!trimmed || isNaN(parseFloat(trimmed))) {
          showToast({ title: "Validation", message: "Please enter a valid number", variant: "warning" });
          return;
        }
        constraintString = `sum_equals_fixed;${trimmed}`;
        setConstraints([...constraints, constraintString]);
        setSumEqualsFixedValue("");
        setSumEqualsMode("question");
        break;
      }
        
      case "equals_question": {
        if (!equalsQuestionId) {
          showToast({ 
            title: "Validation",
            message: "Please select a question to match against",
            variant: "warning"
          });
          return;
        }
        // Build constraint string: equals_question;{questionId} or equals_question;{questionId};total or equals_question;{questionId};item-{index}
        if (equalsQuestionItems.length > 0) {
          // InputList question - include reference type
          if (equalsQuestionReferenceType === "total") {
            constraintString = `equals_question;${equalsQuestionId};total`;
          } else if (equalsQuestionReferenceType.startsWith("item-")) {
            const itemIndex = equalsQuestionReferenceType.split("-")[1];
            constraintString = `equals_question;${equalsQuestionId};item-${itemIndex}`;
          }
        } else {
          // Simple singleInput question
          constraintString = `equals_question;${equalsQuestionId}`;
        }
        setConstraints([...constraints, constraintString]);
        setEqualsQuestionId("");
        setEqualsQuestionItems([]);
        setEqualsQuestionReferenceType("total");
        break;
      }

      case "sum_formula_equals": {
        if (cfFormula.sources.length === 0) {
          showToast({ title: "Validation", message: "Please add at least one source to the formula", variant: "warning" });
          return;
        }
        const formulaString = cfFormula.formulaParts.map(p => p.type === 'source' ? p.sourceIndex.toString() : p.value).join('');
        const sourcesString = cfFormula.sources.map(s => `${s.questionId},${s.referenceType}`).join(';');
        constraintString = `sum_formula_equals;${formulaString};${sourcesString}`;
        clearCf();
        cfFormula.setBuilding(false);
        cfFormula.setSourceQuestionId("");
        cfFormula.setSourceReferenceType("total");
        cfFormula.setSourceItems([]);
        setConstraints([...constraints, constraintString]);
        return;
      }

      case "formula_equals": {
        if (sifFormula.sources.length === 0) {
          showToast({ title: "Validation", message: "Please add at least one source to the formula", variant: "warning" });
          return;
        }
        const formulaString = sifFormula.formulaParts.map(p => p.type === 'source' ? p.sourceIndex.toString() : p.value).join('');
        const sourcesString = sifFormula.sources.map(s => `${s.questionId},${s.referenceType}`).join(';');
        constraintString = `formula_equals;${formulaString};${sourcesString}`;
        clearSif();
        sifFormula.setBuilding(false);
        sifFormula.setSourceQuestionId("");
        sifFormula.setSourceReferenceType("total");
        sifFormula.setSourceItems([]);
        setConstraints([...constraints, constraintString]);
        return;
      }

      case "column_formula": {
        // Build formula from formulaParts array
        // Example: [{type: 'column', value: 1}, {type: 'operator', value: '+'}, {type: 'column', value: 3}, {type: 'operator', value: '-'}, {type: 'column', value: 2}, {type: 'operator', value: '='}, {type: 'column', value: 0}]
        // Results in: "column_formula;1+3-2=0"
        if (formulaParts.length === 0) {
          showToast({ 
            title: "Validation",
            message: "Please build a formula first",
            variant: "warning"
          });
          return;
        }
        
        // Validate formula has a comparison or equals operator
        const comparisonOps = ['=', '<', '>', '<=', '>='];
        const hasComparison = formulaParts.some(part => part.type === 'operator' && comparisonOps.includes(part.value));
        if (!hasComparison) {
          showToast({ 
            title: "Validation",
            message: "Formula must include a comparison operator (=, <, >, ≤, ≥)",
            variant: "warning"
          });
          return;
        }
        
        // Build the formula string
        const formulaString = formulaParts.map(part => part.value).join('');
        constraintString = `column_formula;${formulaString}`;
        setConstraints([...constraints, constraintString]);
        setFormulaParts([]);
        setBuildingFormula(false);
        break;
      }
    }
  };
  
  // Column formula helper functions
  const addFormulaColumn = (columnIndex) => {
    setFormulaParts([...formulaParts, { type: 'column', value: columnIndex }]);
  };
  
  const addFormulaOperator = (operator) => {
    // Prevent adding operator as first element or consecutive operators
    if (formulaParts.length === 0) {
      showToast({ 
        title: "Formula Builder",
        message: "Start with a column first",
        variant: "warning"
      });
      return;
    }
    const lastPart = formulaParts[formulaParts.length - 1];
    if (lastPart.type === 'operator') {
      showToast({ 
        title: "Formula Builder",
        message: "Can't add consecutive operators",
        variant: "warning"
      });
      return;
    }
    setFormulaParts([...formulaParts, { type: 'operator', value: operator }]);
  };
  
  const clearFormula = () => {
    setFormulaParts([]);
  };
  
  const getFormulaDisplay = () => {
    if (formulaParts.length === 0) return "Build your formula...";
    const colCount = columnHeadings.length;
    return formulaParts.map(part => {
      if (part.type === 'column') {
        if (colCount > 0 && subQuestions.length > 0) {
          const rowIdx = Math.floor(part.value / colCount);
          const colIdx = part.value % colCount;
          const rowLabel = subQuestions[rowIdx]?.label || subQuestions[rowIdx]?.Label || `Row ${rowIdx + 1}`;
          const colLabel = columnHeadings[colIdx] || `Col${colIdx}`;
          return `${rowLabel} — ${colLabel}`;
        }
        return columnHeadings[part.value] || `Col${part.value}`;
      }
      const opMap = { '*': '×', '-': '−', '<=': '≤', '>=': '≥' };
      const displayOp = opMap[part.value] ?? part.value;
      return ` ${displayOp} `;
    }).join('');
  };

  // Reference formula helper functions
  const addReferenceFormulaSource = (sourceIndex) => {
    refFormula.setFormulaParts([...refFormula.formulaParts, { type: 'source', value: sourceIndex }]);
  };
  
  const addReferenceFormulaOperator = (operator) => {
    // Prevent adding operator as first element or consecutive operators
    if (refFormula.formulaParts.length === 0) {
      showToast({ 
        title: "Formula Builder",
        message: "Start with a source or number first",
        variant: "warning"
      });
      return;
    }
    const lastPart = refFormula.formulaParts[refFormula.formulaParts.length - 1];
    if (lastPart.type === 'operator') {
      showToast({ 
        title: "Formula Builder",
        message: "Can't add consecutive operators",
        variant: "warning"
      });
      return;
    }
    refFormula.setFormulaParts([...refFormula.formulaParts, { type: 'operator', value: operator }]);
  };
  
  const addReferenceFormulaConstant = () => {
    const num = parseFloat(constantInput);
    if (isNaN(num)) {
      showToast({ 
        title: "Formula Builder",
        message: "Please enter a valid number",
        variant: "warning"
      });
      return;
    }
    // Prevent adding constant after another value without operator
    const lastPart = refFormula.formulaParts[refFormula.formulaParts.length - 1];
    if (lastPart && lastPart.type !== 'operator') {
      showToast({ 
        title: "Formula Builder",
        message: "Add an operator (+, -, ×) before adding a number",
        variant: "warning"
      });
      return;
    }
    refFormula.setFormulaParts([...refFormula.formulaParts, { type: 'constant', value: constantInput }]);
    setConstantInput(""); // Clear input after adding
  };
  
  const clearReferenceFormula = () => {
    refFormula.setFormulaParts([]);
    setConstantInput("");
  };

  const selectReferenceColumnSourceQuestion = (columnIndex, selectedId) => {
    updateReferenceColumn(columnIndex, column => {
      if (!selectedId) {
        return resetReferenceColumnPicker({
          ...column,
          sourceQuestionId: ""
        });
      }

      const selectedQ = getAllPreviousQuestions().find(q => q.id === parseInt(selectedId));
      let sourceItems = [];
      let sourceHasGrandTotal = false;
      let sourceColumnHeadings = [];

      if (selectedQ) {
        try {
          const params = typeof selectedQ.typeParameters === "string"
            ? JSON.parse(selectedQ.typeParameters)
            : selectedQ.typeParameters || {};
          sourceItems = params.Questions || [];
        } catch {
          sourceItems = [];
        }

        try {
          const layoutData = typeof selectedQ.layout === "string"
            ? JSON.parse(selectedQ.layout)
            : selectedQ.layout || {};
          sourceHasGrandTotal = layoutData.showGrandTotal || false;
          sourceColumnHeadings = layoutData.columnHeadings || [];
        } catch {
          sourceHasGrandTotal = false;
        }
      }

      return {
        ...column,
        sourceQuestionId: selectedId,
        sourceQuestionType: selectedQ?.type || "",
        sourceReferenceType: "total",
        sourceItems,
        sourceHasGrandTotal,
        sourceColumnHeadings
      };
    });
  };

  const addReferenceColumnSource = (columnIndex) => {
    updateReferenceColumn(columnIndex, column => {
      if (!column.sourceQuestionId) {
        showToast({
          title: "Select Question",
          message: "Please select a question to add as a source",
          variant: "warning"
        });
        return column;
      }

      const newSource = {
        questionId: column.sourceQuestionId,
        referenceType: (column.sourceQuestionType === "inputList" || column.sourceQuestionType === "InputList") && column.sourceItems.length > 0
          ? column.sourceReferenceType
          : "total"
      };

      return resetReferenceColumnPicker({
        ...column,
        ReferenceSources: [...(column.ReferenceSources || []), newSource]
      });
    });
  };

  const addReferenceColumnFormulaSource = (columnIndex, sourceIndex) => {
    updateReferenceColumn(columnIndex, column => ({
      ...column,
      ReferenceFormulaParts: [
        ...(column.ReferenceFormulaParts || []),
        { type: "source", value: sourceIndex }
      ]
    }));
  };

  const addReferenceColumnFormulaOperator = (columnIndex, operator) => {
    updateReferenceColumn(columnIndex, column => {
      const parts = column.ReferenceFormulaParts || [];
      if (parts.length === 0) {
        showToast({
          title: "Formula Builder",
          message: "Start with a source or number first",
          variant: "warning"
        });
        return column;
      }
      if (parts[parts.length - 1].type === "operator") {
        showToast({
          title: "Formula Builder",
          message: "Can't add consecutive operators",
          variant: "warning"
        });
        return column;
      }
      return {
        ...column,
        ReferenceFormulaParts: [...parts, { type: "operator", value: operator }]
      };
    });
  };

  const addReferenceColumnFormulaConstant = (columnIndex) => {
    updateReferenceColumn(columnIndex, column => {
      const num = parseFloat(column.constantInput);
      if (isNaN(num)) {
        showToast({
          title: "Formula Builder",
          message: "Please enter a valid number",
          variant: "warning"
        });
        return column;
      }
      const parts = column.ReferenceFormulaParts || [];
      if (parts.length > 0 && parts[parts.length - 1].type !== "operator") {
        showToast({
          title: "Formula Builder",
          message: "Add an operator (+, -, ×) before adding a number",
          variant: "warning"
        });
        return column;
      }
      return {
        ...column,
        ReferenceFormulaParts: [...parts, { type: "constant", value: column.constantInput }],
        constantInput: ""
      };
    });
  };

  // Constraint formula builder — thin wrappers over cfFormula hook instance
  const getCfFormulaDisplay = () => cfFormula.getDisplay((source, idx) => getSourceLabel(source, idx));
  const addCfSource = () => {
    if (!cfFormula.sourceQuestionId) {
      showToast({
        title: "Formula Builder",
        message: "Please select a source",
        variant: "warning"
      });
      return;
    }

    cfFormula.addSource();
  };
  const addCfOperator = (op) => cfFormula.addOperator(op);
  const clearCf = () => cfFormula.clear();

  // Single input formula builder — thin wrappers over sifFormula hook instance
  const getSifFormulaDisplay = () => sifFormula.getDisplay((source, idx) => getSourceLabel(source, idx));
  const addSifSource = () => {
    if (!sifFormula.sourceQuestionId) {
      showToast({
        title: "Formula Builder",
        message: "Please select a source",
        variant: "warning"
      });
      return;
    }

    sifFormula.addSource();
  };
  const addSifOperator = (op) => sifFormula.addOperator(op);
  const clearSif = () => sifFormula.clear();
  
  const getReferenceFormulaDisplay = () => {
    if (refFormula.formulaParts.length === 0) return "Build your formula...";
    return refFormula.formulaParts.map(part => {
      if (part.type === 'constant') {
        // Display constant as bold number
        return ` ${part.value} `;
      }
      if (part.type === 'source') {
        const source = refFormula.sources[part.value];
        if (!source) return `Source${part.value + 1}`;
        return getSourceLabel(source, part.value);
      }
      const displayOp = part.value === '*' ? '×' : part.value;
      return ` ${displayOp} `;
    }).join('');
  };

  // Remove constraint
  const removeConstraint = (constraintToRemove) => {
    if (constraintToRemove === "required") {
      setIsRequired(false);
    }
    setConstraints(constraints.filter(c => c !== constraintToRemove));
  };

  // Add condition
  const addCondition = () => {
    // Validation
    if (!conditionQuestionId) {
      showToast({ 
        title: "Validation",
        message: "Please select a question for the condition",
        variant: "warning"
      });
      return;
    }
    
    // For sum mode, validate indices selected
    if (conditionReferenceMode === "sum") {
      if (conditionSumIndices.length === 0) {
        showToast({ 
          title: "Validation",
          message: "Please select at least one item/choice to sum",
          variant: "warning"
        });
        return;
      }
      if (!conditionOperator || !conditionValueType || !conditionValue.trim()) {
        showToast({ 
          title: "Validation",
          message: "Please select operator, value type, and value for the sum condition",
          variant: "warning"
        });
        return;
      }
      
      // Build sum condition: if;{qId};sum;{indices};{operator};{valueType};{value}
      const indicesStr = conditionSumIndices.join(',');
      const conditionString = `if;${conditionQuestionId};sum;${indicesStr};${conditionOperator};${conditionValueType};${conditionValue}`;
      setConditions([...conditions, conditionString]);
    }
    // For item mode (specific item/choice)
    else if (conditionReferenceMode === "item") {
      if (!conditionItemIndex && conditionItemIndex !== 0) {
        showToast({ 
          title: "Validation",
          message: "Please select which item/choice to reference",
          variant: "warning"
        });
        return;
      }
      if (!conditionOperator || !conditionValueType || !conditionValue.trim()) {
        showToast({ 
          title: "Validation",
          message: "Please select operator, value type, and value for the condition",
          variant: "warning"
        });
        return;
      }
      
      // Build item condition: if;{qId};item-{index};{operator};{valueType};{value}
      const conditionString = `if;${conditionQuestionId};item-${conditionItemIndex};${conditionOperator};${conditionValueType};${conditionValue}`;
      setConditions([...conditions, conditionString]);
    }
    // Direct mode (default - for SingleInput or direct value comparison)
    else {
      if (!conditionValueType || !conditionValue.trim()) {
        showToast({ 
          title: "Validation",
          message: "Please select value type and value for the condition",
          variant: "warning"
        });
        return;
      }
      
      // Build direct condition: if;{qId};{operator};{valueType};{value}
      const conditionString = `if;${conditionQuestionId};${conditionOperator};${conditionValueType};${conditionValue}`;
      setConditions([...conditions, conditionString]);
    }
    
    // Reset form
    setConditionQuestionId("");
    setConditionQuestionItems([]);
    setConditionReferenceMode("direct");
    setConditionItemIndex("");
    setConditionSumIndices([]);
    setConditionOperator("equals");
    setConditionValue("");
    setConditionValueType("");
  };

  // Remove condition
  const removeCondition = (conditionToRemove) => {
    setConditions(conditions.filter(c => c !== conditionToRemove));
  };

  // Check if a question has any broken references (referenced question IDs no longer exist)
  const hasBrokenRef = (question) => {
    const allQIds = new Set(steps.flatMap(s => (s.questions || []).map(q => q.id)));
    const constraints = Array.isArray(question.constraints)
      ? question.constraints
      : (typeof question.constraints === 'string' ? JSON.parse(question.constraints || '[]') : []);
    const conditions = Array.isArray(question.conditions)
      ? question.conditions
      : (typeof question.conditions === 'string' ? JSON.parse(question.conditions || '[]') : []);
    for (const c of constraints) {
      const parts = c.split(';');
      const type = parts[0];
      if (type === 'item_equals' && !allQIds.has(parseInt(parts[2]))) return true;
      if ((type === 'sum_equals' || type === 'equals_question') && !allQIds.has(parseInt(parts[1]))) return true;
      if (type === 'sum_formula_equals' || type === 'formula_equals') {
        for (const pair of parts.slice(2)) {
          if (!allQIds.has(parseInt(pair.split(',')[0]))) return true;
        }
      }
    }
    for (const cond of conditions) {
      const parts = cond.split(';');
      if (parts[0] === 'if' && !allQIds.has(parseInt(parts[1]))) return true;
    }
    return false;
  };

  // Parse constraint for display
  const parseConstraint = (constraint, headings, rowLabels) => {
    const resolvedHeadings = headings ?? columnHeadings;
    const resolvedRowLabels = rowLabels ?? [];
    if (constraint === "required") return "Required";
    if (constraint === "sum_equals_last_field") return "Sum of fields must equal last field (Total)";
    
    const parts = constraint.split(";");
    const type = parts[0];
    
    switch(type) {
      case "min":
        return `Minimum: ${parts[1]}`;
      case "max":
        return `Maximum: ${parts[1]}`;
      case "item_equals": {
        const itemIndex = parseInt(parts[1]);
        const questionId = parts[2];
        const referenceType = parts[3]; // 'total', 'grandtotal', or 'item-{index}'
        
        // Get the item label from subQuestions
        let itemLabel = `Item ${itemIndex + 1}`;
        if (subQuestions && subQuestions[itemIndex]) {
          itemLabel = subQuestions[itemIndex].Label || subQuestions[itemIndex].label || itemLabel;
        }
        
        // Find question text and number if possible
        let questionInfo = null;
        let globalNumber = 0;
        
        for (const step of steps) {
          for (const q of step.questions || []) {
            globalNumber++;
            if (q.id === parseInt(questionId)) {
              const shortText = q.text.substring(0, 50) + (q.text.length > 50 ? "..." : "");
              questionInfo = `Q${globalNumber} (${shortText})`;
              break;
            }
          }
          if (questionInfo !== null) break;
        }
        if (questionInfo === null) questionInfo = 'Q?';
        
        // Add reference type description
        let referenceDesc = "";
        if (referenceType === "grandtotal") {
          referenceDesc = " [Custom Total]";
        } else if (referenceType === "total") {
          referenceDesc = " [Total]";
        } else if (referenceType && referenceType.startsWith("item-")) {
          referenceDesc = ` [Item ${parseInt(referenceType.split("-")[1]) + 1}]`;
        }
        
        return `Item ${itemIndex + 1} (${itemLabel}) must equal ${questionInfo}${referenceDesc}`;
      }
      case "sum_equals_fixed": {
        return `Sum must equal fixed value: ${parts[1]}`;
      }
      case "sum_equals": {
        const questionId = parts[1];
        const referenceType = parts[2]; // 'total', 'grandtotal', or 'item-{index}'
        // Find question text and number if possible
        let questionInfo = null;
        let globalNumber = 0;
        
        for (const step of steps) {
          for (const q of step.questions || []) {
            globalNumber++;
            if (q.id === parseInt(questionId)) {
              const shortText = q.text.substring(0, 50) + (q.text.length > 50 ? "..." : "");
              questionInfo = `Q${globalNumber} (${shortText})`;
              break;
            }
          }
          if (questionInfo !== null) break;
        }
        if (questionInfo === null) questionInfo = 'Q?';
        
        // Add reference type description
        let referenceDesc = "";
        if (referenceType === "grandtotal") {
          referenceDesc = " [Custom Total]";
        } else if (referenceType === "total") {
          referenceDesc = " [Total]";
        } else if (referenceType && referenceType.startsWith("item-")) {
          referenceDesc = ` [Item ${parseInt(referenceType.split("-")[1]) + 1}]`;
        }
        
        return `Sum must equal ${referenceDesc ? 'value from' : 'total from'} ${questionInfo}${referenceDesc}`;
      }
      case "equals_question": {
        const questionId = parts[1];
        // Find question text and number if possible
        let questionInfo = null;
        let globalNumber = 0;
        
        for (const step of steps) {
          for (const q of step.questions || []) {
            globalNumber++;
            if (q.id === parseInt(questionId)) {
              const shortText = q.text.substring(0, 50) + (q.text.length > 50 ? "..." : "");
              questionInfo = `Q${globalNumber} (${shortText})`;
              break;
            }
          }
          if (questionInfo !== null) break;
        }
        if (questionInfo === null) questionInfo = 'Q?';
        return `Must equal answer from ${questionInfo}`;
      }
      case "sum_formula_equals": {
        const formulaStr = parts[1];
        const sourcePairs = parts.slice(2);
        const sourceLabels = sourcePairs.map((pair, idx) => {
          const [questionId, refType] = pair.split(',');
          let qLabel = `Q?`;
          let n = 0;
          for (const step of steps) {
            for (const q of step.questions || []) {
              n++;
              if (q.id === parseInt(questionId)) { qLabel = `Q${n}`; break; }
            }
            if (qLabel !== 'Q?') break;
          }
          const typeLabel = refType === 'grandtotal' ? '[Custom Total]'
            : refType === 'total' ? '[Total]'
            : `[Item ${parseInt((refType || '').split('-')[1] || '0') + 1}]`;
          return `${qLabel}${typeLabel}`;
        });
        const humanFormula = formulaStr.replace(/(<=|>=|[+\-=<>*]|\d+)/g, token => {
          const n = parseInt(token);
          return (!isNaN(n) && sourceLabels[n] !== undefined) ? sourceLabels[n] : token;
        });
        return `Sum must equal: ${humanFormula}`;
      }
      case "formula_equals": {
        const formulaStr = parts[1];
        const sourcePairs = parts.slice(2);
        const sourceLabels = sourcePairs.map((pair, idx) => {
          const [questionId, refType] = pair.split(',');
          let qLabel = `Q?`;
          let n = 0;
          for (const step of steps) {
            for (const q of step.questions || []) {
              n++;
              if (q.id === parseInt(questionId)) { qLabel = `Q${n}`; break; }
            }
            if (qLabel !== 'Q?') break;
          }
          const typeLabel = refType === 'grandtotal' ? '[Custom Total]'
            : refType === 'total' ? '[Total]'
            : `[Item ${parseInt((refType || '').split('-')[1] || '0') + 1}]`;
          return `${qLabel}${typeLabel}`;
        });
        const humanFormula = formulaStr.replace(/(<=|>=|[+\-=<>*]|\d+)/g, token => {
          const n = parseInt(token);
          return (!isNaN(n) && sourceLabels[n] !== undefined) ? sourceLabels[n] : token;
        });
        return `Answer must match: ${humanFormula}`;
      }
      case "column_formula": {
        // Parse formula like "0<3" and convert to human-readable format using flat indices
        const formulaString = parts[1];
        if (!formulaString || resolvedHeadings.length === 0) return "Column formula (incomplete)";

        const colCount = resolvedHeadings.length;
        // Split by operators while preserving them (multi-char operators first to avoid splitting <=/>= wrongly)
        const formulaParts = formulaString.split(/(<=|>=|[+\-=*<>])/);
        const displayFormula = formulaParts.map(part => {
          const trimmed = part.trim();
          if (/^\d+$/.test(trimmed)) {
            const flatIdx = parseInt(trimmed);
            const rowIdx = Math.floor(flatIdx / colCount);
            const colIdx = flatIdx % colCount;
            const rowLabel = resolvedRowLabels[rowIdx] || null;
            const colLabel = resolvedHeadings[colIdx] || `Col${colIdx}`;
            return rowLabel ? `${rowLabel} — ${colLabel}` : colLabel;
          }
          // Map stored operator values to display symbols
          const opMap = { '*': '×', '-': '−', '<=': '≤', '>=': '≥' };
          return opMap[trimmed] ?? trimmed;
        }).join(' ');
        
        return `Column formula: ${displayFormula}`;
      }
      default:
        return constraint;
    }
  };

  // Parse condition for display
  const parseCondition = (condition) => {
    const parts = condition.split(";");
    if (parts[0] !== "if") return condition;
    
    const questionId = parts[1];
    let mode = "direct"; // "direct", "item", "sum"
    let modeDetail = ""; // For item mode: item index; for sum mode: indices
    let operator, valueType, value;
    
    // Detect if it's item or sum mode
    if (parts[2]?.startsWith("item-")) {
      mode = "item";
      modeDetail = parts[2].split("-")[1]; // Extract index
      operator = parts[3];
      valueType = parts[4] || "text";
      value = parts[5];
    } else if (parts[2] === "sum") {
      mode = "sum";
      modeDetail = parts[3]; // comma-separated indices
      operator = parts[4];
      valueType = parts[5] || "text";
      value = parts[6];
    } else {
      // Direct mode (old format)
      operator = parts[2];
      valueType = parts[3] || "text";
      value = parts[4];
    }
    
    // Find question text and items
    let questionText = null;
    let questionItems = [];
    for (const step of steps) {
      const q = step.questions?.find(q => q.id === parseInt(questionId));
      if (q) {
        questionText = q.text.substring(0, 30) + "...";
        // Try to get items/choices
        try {
          const typeParams = typeof q.typeParameters === 'string'
            ? JSON.parse(q.typeParameters)
            : q.typeParameters || {};
          questionItems = typeParams.Questions || typeParams.Choices || [];
        } catch (e) {
          // Ignore
        }
        break;
      }
    }
    if (questionText === null) questionText = 'Q?';
    
    const operatorText = {
      equals: "equals",
      not_equals: "does not equal",
      greater_than: "is greater than",
      less_than: "is less than"
    }[operator] || operator;
    
    // Format value display based on type
    let displayValue = value;
    if (valueType === "boolean") {
      displayValue = value === "true" ? "Yes" : "No";
    } else if (valueType === "number") {
      displayValue = `${formatNumberDisplay(value)} (number)`;
    }
    
    // Build display string based on mode
    if (mode === "item") {
      const itemIdx = parseInt(modeDetail);
      const itemLabel = questionItems[itemIdx]
        ? (typeof questionItems[itemIdx] === 'string' ? questionItems[itemIdx] : (questionItems[itemIdx].Label || questionItems[itemIdx].label || `Item ${itemIdx + 1}`))
        : `Item ${itemIdx}`;
      return `Show if "${itemLabel}" from ${questionText} ${operatorText} "${displayValue}"`;
    } else if (mode === "sum") {
      const indices = modeDetail.split(',').map(i => parseInt(i));
      const itemLabels = indices.map(idx => {
        const item = questionItems[idx];
        return item
          ? (typeof item === 'string' ? item : (item.Label || item.label || `Item ${idx + 1}`))
          : `Item ${idx}`;
      });
      return `Show if sum of [${itemLabels.join(' + ')}] from ${questionText} ${operatorText} "${displayValue}"`;
    } else {
      return `Show if ${questionText} ${operatorText} "${displayValue}"`;
    }
  };

  // Build question-number lookup and condition dependency map for the Check section
  const previewQNumberMap = {};
  let _pqn = 0;
  for (const _s of steps) {
    for (const _q of (_s.questions || [])) {
      _pqn++;
      previewQNumberMap[_q.id] = _pqn;
    }
  }
  // conditionTargetMap: sourceQuestionId → [Q-numbers of questions that depend on it]
  const conditionTargetMap = {};
  for (const _s of steps) {
    for (const _q of (_s.questions || [])) {
      if (!_q.conditions?.length) continue;
      for (const _cond of _q.conditions) {
        const _parts = _cond.split(';');
        if (_parts[0] !== 'if') continue;
        const _srcId = parseInt(_parts[1]);
        if (!isNaN(_srcId)) {
          if (!conditionTargetMap[_srcId]) conditionTargetMap[_srcId] = [];
          const _depNum = previewQNumberMap[_q.id];
          if (_depNum && !conditionTargetMap[_srcId].includes(_depNum))
            conditionTargetMap[_srcId].push(_depNum);
        }
      }
    }
  }

  // Short condition label for badges — full description shown as hover tooltip
  const parseConditionShort = (condition) => {
    const parts = condition.split(';');
    if (parts[0] !== 'if') return condition.substring(0, 20);
    const sourceId = parseInt(parts[1]);
    const sourceQNum = previewQNumberMap[sourceId] ?? '?';
    let operator;
    if (parts[2]?.startsWith('item-')) operator = parts[3];
    else if (parts[2] === 'sum') operator = parts[4];
    else operator = parts[2];
    const opSymbol = { equals: '=', not_equals: '≠', greater_than: '>', less_than: '<' }[operator] || operator;
    const rawValue = parts[parts.length - 1];
    const valueType = parts.length > 4 ? parts[parts.length - 2] : 'text';
    const displayValue = valueType === 'boolean'
      ? (rawValue === 'true' ? 'Yes' : 'No')
      : valueType === 'number'
        ? formatNumberDisplay(rawValue)
        : `"${rawValue}"`;
    return `Q${sourceQNum} ${opSymbol} ${displayValue}`;
  };

  // Get all previous questions for dropdowns
  const getAllPreviousQuestions = () => {
    const allQuestions = [];
    for (const step of steps) {
      if (step.questions) {
        for (const q of step.questions) {
          allQuestions.push({
            id: q.id,
            text: q.text,
            stepTitle: step.stepTitle,
            type: q.type,
            typeParameters: q.typeParameters,
            layout: q.layout
          });
        }
      }
    }
    return allQuestions;
  };

  // Create survey stub with title, description, and tags
  const createSurveyStub = async () => {
    if (isCreatingStub) return;
    
    if (!surveyTitle.trim()) {
      showToast({
        title: "Survey Title Required",
        message: "Please enter a survey title before creating the survey.",
        variant: "warning"
      });
      return;
    }
    
    if (currentSurveyId) {
      showToast({
        title: "Survey Already Created",
        message: "This survey has already been created. You can now add steps.",
        variant: "info"
      });
      return;
    }
    
    setIsCreatingStub(true);
    try {
      // ✅ CLEAR ALL OLD STATE BEFORE CREATING NEW SURVEY
      clearSurveyBuilderState();
      
      const payload = {
        title: surveyTitle.trim(),
        description: surveyInstructions.trim() || null,
        heritageId: null,
        tags: surveyTags,
        roles: surveyRoles,
        isFinal: false,
      };
      
      const surveyResult = await secureHelpers.post('/api/surveys', payload);
      
      setCurrentSurveyId(surveyResult.id);
      
      setSurveyTitle(surveyResult.title || "Untitled Survey");
      setSurveyInstructions(surveyResult.description || "");
      setSurveyTags(surveyResult.tags || []);
      setSurveyRoles(surveyResult.roles || []);
      setInstructionsSaved(true);
      
      showToast({
        title: "Success",
        message: `Survey "${surveyResult.title}" created! You can now add steps and questions.`,
        variant: "success",
        delay: 5000
      });
    } catch (error) {
      console.error("Error creating survey stub:", error);
      showToast({ 
        title: "Error",
        message: `Failed to create survey: ${error.message}`,
        variant: "error"
      });
    } finally {
      setIsCreatingStub(false);
    }
  };

  const addStep = async () => {
    if (!stepTitle.trim()) return;
    if (isAddingStep) return; // Prevent double-clicks
    
    // Require survey to be created first
    if (!currentSurveyId) {
      showToast({
        title: "Create Survey First",
        message: "Please click 'Create Survey' button above to save your title, description, and tags before adding steps.",
        variant: "warning"
      });
      return;
    }
    
    setIsAddingStep(true);
    try {
      const surveyId = currentSurveyId;
      
      // Create the step and description on the server
      const stepPayload = {
        surveyId: surveyId,
        name: stepTitle.trim(),
        description: stepDescription.trim() || null,
        order: steps.length + 1
      };
      
      const stepResult = await secureHelpers.post('/api/steps', stepPayload);
      
      // Use full API response, add questions array if not present
      const newStep = {
        ...stepResult,
        title: stepResult.name || stepResult.title,
        description: stepResult.description, // API uses 'name', UI uses 'title'
        questions: stepResult.questions || []
      };
      
      const updatedSteps = [...steps, newStep];
      setSteps(updatedSteps);
      
      // Auto-select the newly created step so users can immediately add questions
      setSelectedStep(updatedSteps.length - 1);
      
      setStepTitle("");
      setStepDescription("");
      
    } catch (error) {
      console.error("Error adding step:", error);
      console.error("Error message:", error.message);
      console.error("ERROR STACK:", error.stack);
      showToast({ 
        title: "Error",
        message: `Failed to add step: ${error.message}`,
        variant: "error"
      });
    } finally {
      setIsAddingStep(false);
    }
  };
  
  // Clear question form - resets all question fields without deleting saved questions
  const clearQuestionForm = () => {
    // Reset basic question fields
    setQuestionType("singleInput");
    setQuestionText("");
    setQuestionDescription("");
    setIsRequired(false);
    setInputType("");
    
    // Reset subquestions/choices
    setSubQuestions([]);
    setCurrentSubQuestion("");
    
    // Reset multiple choice options
    setMultipleSelectAllowed(false);
    setIsOrderedSelection(false);
    setMultipleSelectLimit("");
    setIncludeInTitle(false);
    
    // Reset validation constraints
    setConstraints([]);
    setMinValue("");
    setMaxValue("");
    setSumEqualsQuestionId("");
    setSumEqualsMode("question");
    setSumEqualsFixedValue("");
    setSumEqualsQuestionType("");
    setSumEqualsQuestionItems([]);
    setSumEqualsReferenceType("total");
    setSumEqualsHasGrandTotal(false);
    setSumEqualsValidationScope("entire");
    setSumEqualsItemIndex("0");
    
    // Reset equals validation
    setEqualsQuestionId("");
    setEqualsQuestionType("");
    setEqualsQuestionItems([]);
    setEqualsQuestionReferenceType("total");
    
    // Reset conditions
    setConditions([]);
    setConditionQuestionId("");
    setConditionQuestionItems([]);
    setConditionReferenceMode("direct");
    setConditionItemIndex("");
    setConditionSumIndices([]);
    setConditionOperator("equals");
    setConditionValue("");
    setConditionValueType("");
    
    // Reset layout options
    setColumnHeadings([]);
    setLayoutDirection("vertical");
    setShowTotal(false);
    setShowGrandTotal(false);
    setGrandTotalColumns([]);
    setTotalFieldLabel("");
    setTotalFieldNote("");
    setSectionHeading("");
    setDisplayAsRowLabel(false);
    setDisplayAsHeading(false);
    setDisplayAsInformational(false);
    setDisplayAsHorizontal(true);
    
    // Reset reference value sources
    refFormula.setSources([]);
    refFormula.setSourceQuestionId("");
    refFormula.setSourceQuestionType("");
    refFormula.setSourceReferenceType("total");
    refFormula.setSourceItems([]);
    refFormula.setSourceHasGrandTotal(false);
    refFormula.setFormulaParts([]);
    refFormula.setBuilding(false);
    setConstantInput("");
    setReferenceDisplayMode("single");
    setReferenceRowLabel("");
    setReferenceColumns([createReferenceColumn()]);
    setReferenceTemplateQuestionId("");
    
    // Reset formula builders
    setFormulaParts([]);
    setBuildingFormula(false);
    cfFormula.setBuilding(false);
    cfFormula.setSources([]);
    cfFormula.setFormulaParts([]);
    cfFormula.setSourceQuestionId("");
    cfFormula.setSourceQuestionType("");
    cfFormula.setSourceReferenceType("total");
    cfFormula.setSourceItems([]);
    cfFormula.setSourceHasGrandTotal(false);
    sifFormula.setBuilding(false);
    sifFormula.setSources([]);
    sifFormula.setFormulaParts([]);
    sifFormula.setSourceQuestionId("");
    sifFormula.setSourceReferenceType("total");
    sifFormula.setSourceItems([]);
    
    // Reset editing state
    
    setIsEditing(false);
    setEditingQuestionId(null);
    setEditingOriginalStepIndex(null);
    
  };
  
  const addQuestion = async () => {
    
    if (selectedStep === null) {
      showToast({
        title: "Select a Step",
        message: "Please select which step this question belongs to from the dropdown.",
        variant: "warning"
      });
      return;
    }
    
    if (!questionText.trim()) {
      showToast({
        title: "Question Text Required",
        message: "Please enter the question text.",
        variant: "warning"
      });
      return;
    }
    
    if (isAddingQuestion) return; // Prevent double-clicks

    // Validate subquestions exist for types that need them
    if ((questionType === "multipleChoice" || questionType === "inputList") && subQuestions.length === 0) {
      showToast({ 
        title: "Validation",
        message: "Please add at least one subquestion",
        variant: "warning"
      });
      return;
    }

    // Prevent singleInput with exactly 1 column heading — causes save/load errors
    if (questionType === "singleInput" && columnHeadings.length === 1) {
      showToast({
        title: "Validation",
        message: "A single column heading on a Single Input question causes errors. Either add a second column heading, or remove the existing one.",
        variant: "warning"
      });
      return;
    }

    setIsAddingQuestion(true);
    try {
      let typeParameters = {};
      let layout = {};
      const newSteps = [...steps];
      
      // Safety check: Validate selected step exists
      if (!newSteps[selectedStep]) {
        throw new Error("Selected step not found. Please refresh and try again.");
      }
      
      if (!newSteps[selectedStep].id) {
        throw new Error("Step ID is missing. Please ensure the step was saved properly.");
      }    
      
      // Build typeParameters (data back end parses) and layout (presentation) based on question type
      switch (questionType) {
case "multipleChoice": {
  // Get existing backend IDs if editing
  let existingChoices = [];
  let existingQuestions = [];
  let existingItemsSource = null;
  if (editingQuestionId) {
    const editingQuestion = steps[editingOriginalStepIndex]?.questions.find(q => q.id === editingQuestionId);
    if (editingQuestion?.typeParameters) {
      try {
        const existingParams = typeof editingQuestion.typeParameters === 'string'
          ? JSON.parse(editingQuestion.typeParameters)
          : editingQuestion.typeParameters;
        existingChoices = existingParams.Choices || existingParams.choices || [];
        existingQuestions = existingParams.Questions || existingParams.questions || [];
        existingItemsSource = existingQuestions.length > 0 ? "Questions" : "Choices";
      } catch (e) {
        console.warn('Failed to parse existing params:', e);
      }
    }
  }

  const existingItems = existingQuestions.length > 0 ? existingQuestions : existingChoices;
  const getExistingItem = (sq, index) => {
    const sourceIndex = sq.sourceIndex ?? index;
    return existingItems[sourceIndex] || existingItems.find(item => (item.Id ?? item.id) === (sq.originalId ?? sq.id));
  };
  const isRealBackendId = (id) => Number.isInteger(Number(id)) && Number(id) > 0 && Number(id) < 10000;
  
  typeParameters = {
    //  Use REAL backend IDs if they exist in subQuestions (from editing)
    Choices: subQuestions.map((sq, index) => {
      const existingQuestion = getExistingItem(sq, index);
      const existingChoiceId = existingQuestion?.Id ?? existingQuestion?.id;
      const choiceId = sq.id ?? sq.originalId ?? existingChoiceId;
      
      return {
        Id: choiceId ?? index,
        Label: sq.label || sq.Label,
        HeritageId: editingQuestionId 
          ? (existingQuestion?.HeritageId ?? existingQuestion?.heritageId ?? sq.heritageId ?? sq.originalHeritageId ?? choiceId ?? null)
          : null  // Creating new: null so backend assigns this question's own ID
      };
    }),
    
    // Questions array - full data structure (matches Choices for tracking)
    Questions: subQuestions.map((sq, index) => {
      const existingQuestion = getExistingItem(sq, index);
      const existingQuestionId = existingQuestion?.Id ?? existingQuestion?.id;
      const questionId = existingItemsSource === "Questions" && isRealBackendId(existingQuestionId)
        ? existingQuestionId
        : null;
      
      return {
        Id: questionId,
        Label: sq.label || sq.Label,
        InputType: null,
        Order: index,
        HeritageId: editingQuestionId 
          ? (existingQuestion?.HeritageId ?? existingQuestion?.heritageId ?? sq.heritageId ?? sq.originalHeritageId ?? existingQuestionId ?? null)
          : null  // Creating new: null so backend assigns this question's own ID
      };
    }),
    
    MultipleSelectAllowed: multipleSelectAllowed,
    IsOrderedSelection: isOrderedSelection,
    MultipleSelectLimit: multipleSelectLimit ? parseInt(multipleSelectLimit) : 0,
    IncludeInTitle: includeInTitle || undefined
  };
  
  layout = {
    sectionHeading: sectionHeading.trim() || null,
    columnHeadings: columnHeadings.length > 0 ? columnHeadings : null,
    displayAsHeading: displayAsHeading || false,
    displayAsHorizontal: displayAsHorizontal !== false  // Default to true (horizontal) if not set
  };
  break;
}
        
      case "singleInput": {
        // Save InputType for singleInput questions
        typeParameters = {
          Questions: [{
            Id: null,  // Backend generates new IDs - our timestamps are too large for C# Int32
            Label: questionText.trim(),  // Use question text as the label
            InputType: inputType,  // Each question needs InputType too
            HeritageId: null,  // New questions have no heritage
            Order: 0
          }],
          InputType: inputType  // Capital I to match C# backend
        };
        
        layout = {
          columnHeadings: columnHeadings.length > 0 ? columnHeadings : null,
          displayAsRowLabel: displayAsRowLabel || false,
          displayAsHeading: displayAsHeading || false,
          displayAsInformational: displayAsInformational || false,
          displayAsHorizontal: displayAsHorizontal !== false  // Default to true if not explicitly set to false
        };
        break;
      }
      
      case "referenceValue": {
        // Build formula string from parts if present, otherwise use simple addition
        const formulaString = buildReferenceFormulaString(refFormula.formulaParts);
        
        // ✅ Normalize sources to PascalCase for backend (backend expects QuestionId/ReferenceType, not questionId/referenceType)
        const normalizedSources = normalizeReferenceSources(refFormula.sources);
        
        typeParameters = {
          ReferenceSources: normalizedSources,
          ReferenceFormula: formulaString // e.g., "0+1*2-3" or "" for simple sum
        };

        layout = {
          displayAsHeading: displayAsHeading || false
        };

        if (referenceDisplayMode === "tableRow") {
          const tableReferenceColumns = referenceColumns
            .filter(column => (column.Heading || "").trim() || (column.ReferenceSources || []).length > 0)
            .map((column, index) => ({
              Heading: (column.Heading || "").trim() || `Column ${index + 1}`,
              ReferenceSources: normalizeReferenceSources(column.ReferenceSources || []),
              ReferenceFormula: buildReferenceFormulaString(column.ReferenceFormulaParts || [])
            }));

          typeParameters.ReferenceDisplayMode = "tableRow";
          typeParameters.ReferenceRowLabel = referenceRowLabel.trim() || questionText.trim();
          typeParameters.ReferenceColumns = tableReferenceColumns;
          layout.referenceDisplayMode = "tableRow";
          layout.referenceRowLabel = referenceRowLabel.trim() || questionText.trim();
          layout.referenceColumns = tableReferenceColumns;
        }
        break;
      }

// For InputList
case "inputList": {
        // Get existing backend IDs if editing
        let existingQuestions = [];
        if (editingQuestionId) {
          const editingQuestion = steps[editingOriginalStepIndex]?.questions.find(q => q.id === editingQuestionId);
          if (editingQuestion?.typeParameters) {
            try {
              const existingParams = typeof editingQuestion.typeParameters === 'string'     ? JSON.parse(editingQuestion.typeParameters)
                : editingQuestion.typeParameters;
              existingQuestions = existingParams.Questions || [];
            } catch (e) {
              console.warn('Failed to parse existing InputList params:', e);
            }
          }
        }

        // Build Questions array with ID preservation
        const expandedQuestions = expandMultiColumnSubQuestions(subQuestions, columnHeadings, inputType, editingQuestionId);
        const repairedExistingHeritage = repairInputListQuestionHeritage(existingQuestions, columnHeadings);
        
        typeParameters = {
          InputType: inputType,
          Questions: expandedQuestions.map((q, index) => {
            const existingQuestionIndex = existingQuestions.findIndex(eq => (
              eq.Label === q.Label || (q.Id && (eq.Id || eq.id) === q.Id)
            ));
            const existingQuestion = existingQuestionIndex >= 0 ? existingQuestions[existingQuestionIndex] : null;
            const hasRealId = q.Id && q.Id < 10000;
            
            return {
              Id: hasRealId ? q.Id : (existingQuestion?.Id || null),
              Label: q.Label,
              InputType: q.InputType || inputType,
              Order: index,
              HeritageId: editingQuestionId
                ? (q.HeritageId
                  ?? repairedExistingHeritage[existingQuestionIndex]?.heritageId
                  ?? existingQuestion?.HeritageId
                  ?? existingQuestion?.heritageId
                  ?? q.Id)  // Editing: preserve existing or repair older corrupted rows
                : null  // Creating new: null so backend assigns this question's own ID
            };
          })
        };
        
        layout = {
          sectionHeading: sectionHeading.trim() || null,
          showTotal: showTotal || false,
          showGrandTotal: showGrandTotal || false,
          grandTotalColumns: grandTotalColumns.length > 0 ? grandTotalColumns : null,
          columnHeadings: columnHeadings.length > 0 ? columnHeadings : null,
          layoutDirection: layoutDirection || "vertical",
          totalFieldLabel: totalFieldLabel.trim() || null,
          totalFieldNote: totalFieldNote.trim() || null,
          displayAsHeading: displayAsHeading || false,
          displayAsHorizontal: displayAsHorizontal !== false
        };
        
        break;
      }

default:
  typeParameters = {};
  layout = {};
      }

      // 🔍 DEBUG: Check typeParameters right after switch
      const effectiveConstraints = questionType === "referenceValue" ? [] : constraints;

      // Check if we're editing or creating
      if (editingQuestionId) {
        // UPDATE existing question - find the existing question to get all required fields
        
        const newSteps = [...steps];
        const originalStepIndex = editingOriginalStepIndex;
        const targetStepIndex = selectedStep;
        const existingQuestion = newSteps[originalStepIndex]?.questions.find(q => q.id === editingQuestionId);
        
        if (!existingQuestion) {
          throw new Error("Question not found for editing");
        }
        
        // Check if moving to a different step
        const isMovingSteps = originalStepIndex !== targetStepIndex;
        
        const targetStepId = newSteps[targetStepIndex].id;
        
        // ✅ Convert frontend type to backend type (camelCase -> PascalCase)
        const backendType = questionType === "referenceValue" ? "ReferenceValue"
                          : questionType === "multipleChoice" ? "MultipleChoice"
                          : questionType === "singleInput" ? "SingleInput"
                          : questionType === "inputList" ? "InputList"
                          : questionType;
        
        const updatePayload = {
          id: editingQuestionId,
          surveyId: existingQuestion.surveyId || currentSurveyId,
          stepId: targetStepId,
          stepDescription: newSteps[targetStepIndex].description, // Use target step (might be different from original)
          order: isMovingSteps ? newSteps[targetStepIndex].questions.length + 1 : existingQuestion.order,
          text: questionText.trim(),
          description: questionDescription.trim() || null,
          type: backendType,  // ✅ Send SingleInput for referenceValue
          typeParameters: JSON.stringify(typeParameters),
          layout: JSON.stringify(layout),
          conditions: conditions,
          constraints: effectiveConstraints
        };
        
        const questionResult = await secureHelpers.put(`/api/questions/${editingQuestionId}`, updatePayload);
        const returnedTypeParameters = questionResult.typeParameters || JSON.stringify(typeParameters);
        const returnedLayout = questionResult.layout || JSON.stringify(layout);
        const localTypeParameters = questionType === "referenceValue" && referenceDisplayMode === "tableRow"
          ? JSON.stringify(typeParameters)
          : returnedTypeParameters;
        const localLayout = questionType === "referenceValue" && referenceDisplayMode === "tableRow"
          ? JSON.stringify(layout)
          : returnedLayout;
       
        // Check if backend returned typeParameters with InputType
        try {
          const returnedParams = typeof returnedTypeParameters === 'string' ? JSON.parse(returnedTypeParameters || "{}") : (returnedTypeParameters || {});
        } catch (e) {
          console.error("Failed to parse returned typeParameters:", e);
        }
        
        // Update local state
        if (isMovingSteps) {
          // Remove from original step
          const questionIndex = newSteps[originalStepIndex].questions.findIndex(q => q.id === editingQuestionId);
          newSteps[originalStepIndex].questions.splice(questionIndex, 1);
          
          // Check if original step is now empty - if so, delete it
          const originalStepNowEmpty = newSteps[originalStepIndex].questions.length === 0;
          
          // Add to target step
          newSteps[targetStepIndex].questions.push({
            ...existingQuestion,
            stepId: targetStepId,
            stepDescription: questionDescription.trim() || null,
            text: questionText.trim(),
            description: questionDescription.trim() || null,
            type: questionType,
            typeParameters: localTypeParameters,
            layout: localLayout,
            constraints: effectiveConstraints,
            conditions: conditions,
            order: newSteps[targetStepIndex].questions.length
          });
          
          // Delete empty step
          if (originalStepNowEmpty) {
            try {
              await secureHelpers.delete(`/api/steps/${newSteps[originalStepIndex].id}`, { confirmed: true });
              newSteps.splice(originalStepIndex, 1);
              showToast({ 
                title: "Info",
                message: "Empty step was automatically removed",
                variant: "success"
              });
            } catch (error) {
              console.error('Failed to delete empty step:', error);
              // Don't fail the whole operation if step deletion fails
            }
          }
        } else {
          // Just update in place
          const questionIndex = newSteps[originalStepIndex].questions.findIndex(q => q.id === editingQuestionId);
          if (questionIndex !== -1) {
            newSteps[originalStepIndex].questions[questionIndex] = {
              ...newSteps[originalStepIndex].questions[questionIndex],
              text: questionText.trim(),
              description: questionDescription.trim() || null,
              type: questionType,
              typeParameters: localTypeParameters,
              layout: localLayout,
              constraints: effectiveConstraints,
              conditions: conditions
            };
          }
        }
        
        setSteps(newSteps);
        
        // Clear editing mode
        setIsEditing(false);
        setEditingQuestionId(null);
        setEditingOriginalStepIndex(null);
      } else {
        // CREATE new question
        const stepId = newSteps[selectedStep].id;
        const questionOrder = newSteps[selectedStep].questions.length + 1;
        
        // ✅ Convert frontend type to backend type (camelCase -> PascalCase)
        const backendType = questionType === "referenceValue" ? "ReferenceValue"
                          : questionType === "multipleChoice" ? "MultipleChoice"
                          : questionType === "singleInput" ? "SingleInput"
                          : questionType === "inputList" ? "InputList"
                          : questionType;
        
        // Create question on server
      const createPayload = {
        surveyId: currentSurveyId,
        stepId: stepId,
        stepDescription: newSteps[selectedStep].description,
        text: questionText.trim(),
        description: questionDescription.trim() || null,
        order: questionOrder,
        type: backendType,  // ✅ Send SingleInput for referenceValue  
        typeParameters: JSON.stringify(typeParameters),
        layout: JSON.stringify(layout),
        conditions: conditions,
        constraints: effectiveConstraints
      };
      
      const questionResult = await secureHelpers.post('/api/questions', createPayload);
      const localTypeParameters = questionType === "referenceValue" && referenceDisplayMode === "tableRow"
        ? JSON.stringify(typeParameters)
        : (questionResult.typeParameters || JSON.stringify(typeParameters));
      const localLayout = questionType === "referenceValue" && referenceDisplayMode === "tableRow"
        ? JSON.stringify(layout)
        : (questionResult.layout || JSON.stringify(layout));

      // Debug: GET the question back to verify referenceValue typeParameters saved correctly
      if (questionType === "referenceValue" && questionResult?.id) {
        try {
          const savedQuestion = await secureHelpers.get(`/api/questions/${questionResult.id}`);
          try {
            const parsedParams = typeof savedQuestion.typeParameters === 'string' ? JSON.parse(savedQuestion.typeParameters || '{}') : (savedQuestion.typeParameters || {});
          } catch (e) {
          }
        } catch (e) {
        }
      }
      
      // Add to local state with server ID and use the typeParameters returned from server
      // (backend adds Questions array with IDs)
      const newQuestion = {
        id: questionResult.id, // Use server ID
        surveyId: currentSurveyId,
        stepId: stepId,
        stepDescription: newSteps[selectedStep].description,
        type: questionType,
        text: questionText.trim(),
        description: questionDescription.trim() || null,
        order: questionResult.order || questionOrder, // ✅ Use backend's order, fallback to frontend calculation
        group: null,
        typeParameters: localTypeParameters,
        layout: localLayout,
        conditions: conditions,
        constraints: effectiveConstraints
      };

      setQuestions([...questions, newQuestion]);
      newSteps[selectedStep].questions.push(newQuestion);
        setSteps(newSteps);
      }
    
      // Reset form
      clearQuestionForm();
      setQuestionSaved(true); // Show saved indicator
      
      // Auto-hide success message after 3 seconds
      setTimeout(() => {
        setQuestionSaved(false);
      }, 3000);
      
    } catch (error) {
      const action = editingQuestionId ? "updating" : "adding";
      console.error(`Error ${action} question:`, error);
      showToast({ 
        title: "Error",
        message: `Failed to ${editingQuestionId ? "update" : "add"} question: ${error.message}`,
        variant: "error"
      });
    } finally {
      setIsAddingQuestion(false);
    }
  };

  // Load question into form for editing
  const editQuestion = (stepIndex, questionIndex) => {

    const question = steps[stepIndex].questions[questionIndex];
    // Set editing mode and remember original step
    setIsEditing(true);
    setEditingQuestionId(question.id);
    setEditingOriginalStepIndex(stepIndex);
    
    // Set form fields
    setQuestionText(question.text);
    setQuestionDescription(question.description || "");
    setSelectedStep(stepIndex);
    
    // Parse constraints and conditions
    const questionConstraints = Array.isArray(question.constraints) ? question.constraints : [];
    const questionConditions = Array.isArray(question.conditions) ? question.conditions : [];
    setConstraints(questionConstraints);
    setConditions(questionConditions);
    setIsRequired(questionConstraints.includes("required"));
    
    // Parse type parameters
    let params = {};
    try {
      params = typeof question.typeParameters === 'string' 
        ? JSON.parse(question.typeParameters) 
        : question.typeParameters;
    } catch (e) {
      console.warn('Failed to parse typeParameters:', e);
    }
    
    // Parse layout
    let layoutData = {};
    try {
      layoutData = typeof question.layout === 'string' 
        ? JSON.parse(question.layout) 
        : question.layout;
    } catch (e) {
      console.warn('Failed to parse layout:', e);
    }
    
    // Normalize type name
    let typeName;
    if (typeof question.type === 'number') {
      switch(question.type) {
        case 0: typeName = "multipleChoice"; break;
        case 1: typeName = "singleInput"; break;
        case 2: typeName = "inputList"; break;
        case 3: typeName = "referenceValue"; break;  // Fixed: was "Matrix", should be "referenceValue"
        default: 
          console.warn('Unknown numeric question type:', question.type);
          typeName = "singleInput"; // Safe fallback
      }
    } else {
      typeName = question.type;
    }
    
    // Normalize type to match frontend naming (lowercase for consistency)
    const normalizedType = typeName === "SingleInput" ? "singleInput" 
                         : typeName === "InputList" ? "inputList"
                         : typeName === "ReferenceValue" ? "referenceValue"
                         : typeName === "MultipleChoice" ? "multipleChoice"
                         : typeName;
    
    // Check if this is a referenceValue question:
    // - Either stored as "singleInput" with ReferenceSources (correct backend format)
    // - OR stored as "referenceValue" directly (legacy from accidental save)
    const hasReferenceSources = params.ReferenceSources || params.referenceSources;
    const hasReferenceColumns = params.ReferenceColumns || params.referenceColumns || layoutData.referenceColumns;
    const isReferenceValue = ((normalizedType === "singleInput") && (hasReferenceSources || hasReferenceColumns)) 
                          || (normalizedType === "referenceValue");
    const actualType = isReferenceValue ? "referenceValue" : normalizedType;
    
    setQuestionType(actualType);
    
    // Reset all type-specific fields first
    setSubQuestions([]);  // ✅ Reset to empty
    setInputType("text");
    setMultipleSelectAllowed(false);
    setIsOrderedSelection(false);
    setMultipleSelectLimit("");
    setIncludeInTitle(false);
    setShowTotal(false);
    setShowGrandTotal(false);
    setGrandTotalColumns([]);
    
    // ✅ NOW load type-specific data using actualType (which detects referenceValue)
   if (actualType === "multipleChoice") {
  
  const questionsArray = params.Questions || params.Choices || params.choices || [];
  const normalizedQuestions = questionsArray.map((q, index) => ({
    id: q.Id ?? q.id,
    originalId: q.Id ?? q.id,
    sourceIndex: index,
    label: q.Label || q.label,
    heritageId: q.HeritageId ?? q.heritageId,
    originalHeritageId: q.HeritageId ?? q.heritageId
  }));

  setSubQuestions(normalizedQuestions);  // ✅ Set AFTER creating the array
  setMultipleSelectAllowed(params.MultipleSelectAllowed || params.multipleSelectAllowed || false);
  setIsOrderedSelection(params.IsOrderedSelection || params.isOrderedSelection || false);
  setIncludeInTitle(params.IncludeInTitle || params.includeInTitle || false);
  const limit = params.MultipleSelectLimit || params.multipleSelectLimit || 0;
  setMultipleSelectLimit(limit > 0 ? limit.toString() : "");
  setSectionHeading(layoutData.sectionHeading || "");
  setColumnHeadings(layoutData.columnHeadings || []);
  setDisplayAsHeading(layoutData.displayAsHeading || false);
  setDisplayAsHorizontal(layoutData.displayAsHorizontal !== false);  // Default to true (horizontal) if not set
}
     else if (normalizedType === "singleInput") {
      // Handle legacy questions that might not have inputType stored
      // Backend uses capital I: InputType
      // Also check Questions array as backend stores it there too
      let detectedInputType = params.InputType || params.inputType;
      
      // Fallback: check Questions array for InputType
      if (!detectedInputType && params.Questions && params.Questions.length > 0) {
        detectedInputType = params.Questions[0].InputType || params.Questions[0].inputType;
      }
      
      // If no inputType, try to detect from items or default to text
      if (!detectedInputType) {
        console.warn('Question has no inputType, defaulting to "text"');
        detectedInputType = "text";
      }
      
      setInputType(detectedInputType);
      // Normalize backend Questions to have consistent lowercase properties
      const normalizedQuestions = (params.Questions || []).map(q => ({
        id: q.Id || q.id,
        label: q.Label || q.label,
        inputType: q.InputType || q.inputType,
        heritageId: q.HeritageId || q.heritageId,
        order: q.Order || q.order
      }));
      setSubQuestions(normalizedQuestions);
      
      // Load column headings and display option for single-row table rendering
      setColumnHeadings(layoutData.columnHeadings || []);
      setDisplayAsRowLabel(layoutData.displayAsRowLabel || false);
      setDisplayAsHeading(layoutData.displayAsHeading || false);
      setDisplayAsInformational(layoutData.displayAsInformational || false);
      setDisplayAsHorizontal(layoutData.displayAsHorizontal !== false);  // Default to true if not set
    } else if (normalizedType === "inputList") {
      setInputType(params.InputType || params.inputType || "number");
      
      // Check if this is multi-column (expanded format)
      const columnHeadingsFromLayout = layoutData.columnHeadings || [];
      const isMultiColumn = columnHeadingsFromLayout.length > 0;
      
      let rowsToEdit = [];
      if (isMultiColumn && params.Questions && params.Questions.length > 0) {
        // Multi-column: collapse back to unique rows
        // Questions are labeled like "Medical Director - Adult Services Headcount"
        // We need to extract unique row labels and preserve IDs for each column
        const columnCount = columnHeadingsFromLayout.length;
        const rowCount = Math.floor(params.Questions.length / columnCount);
        const repairedHeritage = repairInputListQuestionHeritage(params.Questions, columnHeadingsFromLayout);
        
        for (let rowIdx = 0; rowIdx < rowCount; rowIdx++) {
          const firstItemInRow = params.Questions[rowIdx * columnCount];
          const rowLabel = firstItemInRow.Label.includes(' - ') 
            ? firstItemInRow.Label.split(' - ')[0] 
            : firstItemInRow.Label;
          
          // Store IDs for each column so we can preserve them when saving
          const rowItem = {
            label: rowLabel,
            inputType: firstItemInRow.InputType || firstItemInRow.inputType,
            heritageId: repairedHeritage[rowIdx * columnCount]?.heritageId
              ?? firstItemInRow.HeritageId
              ?? firstItemInRow.heritageId,
            order: rowIdx
          };
          
          // Store column IDs: id_col0, id_col1, id_col2, id_col3
          for (let colIdx = 0; colIdx < columnCount; colIdx++) {
            const item = params.Questions[rowIdx * columnCount + colIdx];
            rowItem[`id_col${colIdx}`] = item.Id || item.id;
            rowItem[`heritage_col${colIdx}`] = repairedHeritage[rowIdx * columnCount + colIdx]?.heritageId
              ?? item.HeritageId
              ?? item.heritageId
              ?? null;
          }
          
          rowsToEdit.push(rowItem);
        }
      } else {
        // Single column: use questions as-is
        rowsToEdit = (params.Questions || []).map(q => ({
          id: q.Id || q.id,
          label: q.Label || q.label,
          inputType: q.InputType || q.inputType,
          heritageId: q.HeritageId || q.heritageId,
          order: q.Order || q.order
        }));
      }
      
      setSubQuestions(rowsToEdit);
      setSectionHeading(layoutData.sectionHeading || "");
      setShowTotal(layoutData.showTotal || false);
      setShowGrandTotal(layoutData.showGrandTotal || false);
      setGrandTotalColumns(layoutData.grandTotalColumns || []);
      setColumnHeadings(layoutData.columnHeadings || []);
      setLayoutDirection(layoutData.layoutDirection || "vertical");
      setTotalFieldLabel(layoutData.totalFieldLabel || "");
      setTotalFieldNote(layoutData.totalFieldNote || "");
      setDisplayAsHeading(layoutData.displayAsHeading || false);
    } else if (normalizedType === "referenceValue") {
      // Load reference sources array
      const sources = params.ReferenceSources || params.referenceSources || [];
      refFormula.setSources(sources);
      const savedReferenceColumns = params.ReferenceColumns || params.referenceColumns || layoutData.referenceColumns || [];
      const savedReferenceDisplayMode = params.ReferenceDisplayMode || params.referenceDisplayMode || layoutData.referenceDisplayMode;
      setReferenceDisplayMode(savedReferenceDisplayMode === "tableRow" ? "tableRow" : "single");
      setReferenceRowLabel(params.ReferenceRowLabel || params.referenceRowLabel || layoutData.referenceRowLabel || "");
      setReferenceColumns(savedReferenceColumns.length > 0
        ? savedReferenceColumns.map(column => ({
            ...createReferenceColumn(column.Heading || column.heading || ""),
            Heading: column.Heading || column.heading || "",
            ReferenceSources: column.ReferenceSources || column.referenceSources || [],
            ReferenceFormulaParts: (column.ReferenceFormula || column.referenceFormula || "").split(/([+*/-])/)
              .filter(Boolean)
              .map(part => {
                if (/^\d+(\.\d+)?$/.test(part)) return { type: "source", value: parseInt(part, 10) };
                if (/^[+*/-]$/.test(part)) return { type: "operator", value: part };
                return null;
              })
              .filter(Boolean)
          }))
        : [createReferenceColumn()]);
      
      // Load formula if present
      const formulaString = params.ReferenceFormula || params.referenceFormula || "";
      if (formulaString) {
        // Parse formula string back into parts
        const parts = formulaString.split(/(\+|\-|\*|\/)/);
        const formulaParts = [];
        for (const part of parts) {
          if (part.match(/^\d+$/)) {
            // It's a source index
            formulaParts.push({ type: 'source', value: parseInt(part) });
          } else if (part.match(/^[\+\-\*\/]$/)) {
            // It's an operator
            formulaParts.push({ type: 'operator', value: part });
          }
        }
        refFormula.setFormulaParts(formulaParts);
        if (formulaParts.length > 0) {
          refFormula.setBuilding(true); // Show formula builder if formula exists
        }
      }
      
      setDisplayAsHeading(layoutData.displayAsHeading || false);
    }
    
    // Scroll to question form
    const questionForm = document.getElementById('question-form');
    if (questionForm) {
      questionForm.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  };

 // Helper: find all other questions that reference the given question ID in their
  // conditions (visibility), constraints (validation), or referenceValue typeParameters.
  const findReferencesToQuestion = (questionId) => {
    const qIdStr = questionId.toString();
    const refs = [];
    for (const step of steps) {
      for (const q of (step.questions || [])) {
        if (q.id === questionId) continue;
        const usages = [];

        // Visibility conditions: "if;{questionId};..."
        for (const cond of (q.conditions || [])) {
          const parts = cond.split(';');
          if (parts[0] === 'if' && parts[1] === qIdStr) {
            usages.push('visibility condition');
            break;
          }
        }

        // Validation constraints
        for (const constraint of (q.constraints || [])) {
          const parts = constraint.split(';');
          const type = parts[0];
          if ((type === 'equals_question' || type === 'sum_equals') && parts[1] === qIdStr) {
            usages.push('validation rule');
          } else if (type === 'item_equals' && parts[2] === qIdStr) {
            // item_equals;{itemIndex};{questionId};{referenceType}
            usages.push('validation rule (item-level)');
          } else if ((type === 'sum_formula_equals' || type === 'formula_equals') && parts.length > 2) {
            if (parts.slice(2).some(s => s.split(',')[0] === qIdStr)) {
              usages.push('validation rule (formula)');
            }
          }
        }

        // ReferenceValue typeParameters sources
        if (q.type === 'referenceValue' || q.type === 'ReferenceValue') {
          try {
            const tp = typeof q.typeParameters === 'string' ? JSON.parse(q.typeParameters) : (q.typeParameters || {});
            const sources = tp.ReferenceSources || tp.refFormula.sources || [];
            if (sources.some(s => (s.QuestionId ?? s.questionId)?.toString() === qIdStr)) {
              usages.push('reference value source');
            }
          } catch { /* ignore parse errors */ }
        }

        if (usages.length > 0) refs.push({ question: q, step, usages });
      }
    }
    return refs;
  };

  const removeQuestion = async (stepIndex, questionIndex) => {
  const question = steps[stepIndex].questions[questionIndex];
  
  const refs = findReferencesToQuestion(question.id);

  // Build a map of questionId → sequential Q-number for display
  const qNumberMap = {};
  let qNum = 0;
  for (const s of steps) {
    for (const q of (s.questions || [])) {
      qNum++;
      qNumberMap[q.id] = qNum;
    }
  }

  const confirmMessage = refs.length > 0 ? (
    <div>
      <p style={{ marginBottom: '0.75rem' }}>
        <strong>⚠️ This question is referenced by {refs.length} other question{refs.length > 1 ? 's' : ''}.</strong>
        {' '}Deleting it will break those references — they will show as <em>Q?</em> until manually fixed.
      </p>
      <ul style={{ paddingLeft: '1.25rem', marginBottom: '0.75rem', fontSize: '0.9rem' }}>
        {refs.map((r, i) => (
          <li key={i}>
            <strong>Q{qNumberMap[r.question.id] ?? '?'}</strong>
            {' "'}
            {r.question.text.length > 50 ? r.question.text.substring(0, 47) + '…' : r.question.text}
            {'" '}— {r.usages.join(', ')}
          </li>
        ))}
      </ul>
      <p style={{ marginBottom: 0 }}>Are you sure you want to delete <strong>"{question.text}"</strong>?</p>
    </div>
  ) : `Are you sure you want to delete the question "${question.text}"?`;

  showConfirm({
    title: refs.length > 0 ? "Delete Referenced Question" : "Delete Question",
    message: confirmMessage,
    variant: "danger",
    onConfirm: async () => {
  
  try {
    // Delete from backend
    await secureHelpers.delete(`/api/questions/${question.id}`, { confirmed: true });
    
    // Update local state
    const newSteps = [...steps];
    newSteps[stepIndex].questions.splice(questionIndex, 1);
    setSteps(newSteps);
    
    // Also update the flat questions array
    const allQuestions = newSteps.flatMap(step => step.questions);
    setQuestions(allQuestions);
    
    showToast({ 
      title: "Success",
      message: "Question deleted successfully!",
      variant: "success"
    });
    
  } catch (error) {
    console.error("Error deleting question:", error);
    showToast({ 
      title: "Error",
      message: `Failed to delete question: ${error.message}`,
      variant: "error"
    });
  }
    }
  });
};

  // Move question up or down within a step
  const moveQuestion = async (stepIndex, questionIndex, direction) => {
  const newSteps = [...steps];
  const questions = newSteps[stepIndex].questions;
  
  // Calculate new position
  const newIndex = direction === 'up' ? questionIndex - 1 : questionIndex + 1;
  
  // Check bounds
  if (newIndex < 0 || newIndex >= questions.length) {
    return;
  }
  
  // Swap questions
  const temp = questions[questionIndex];
  questions[questionIndex] = questions[newIndex];
  questions[newIndex] = temp;
  
  // Update order numbers for both questions
  const question1 = questions[questionIndex];
  const question2 = questions[newIndex];
  
  try {
    // Update both questions on backend with their new order
    await Promise.all([
      secureHelpers.put(`/api/questions/${question1.id}`, {
        id: question1.id,
        surveyId: question1.surveyId,
        stepId: question1.stepId,
        text: question1.text,
        description: question1.description,
        order: questionIndex + 1,
        type: question1.type, // Keep as string
        typeParameters: typeof question1.typeParameters === 'string' 
          ? question1.typeParameters 
          : JSON.stringify(question1.typeParameters),
        layout: typeof question1.layout === 'string' 
          ? question1.layout 
          : JSON.stringify(question1.layout),
        conditions: question1.conditions || [],
        constraints: Array.isArray(question1.constraints) 
          ? question1.constraints 
          : []
      }),
      secureHelpers.put(`/api/questions/${question2.id}`, {
        id: question2.id,
        surveyId: question2.surveyId,
        stepId: question2.stepId,
        text: question2.text,
        description: question2.description,
        order: newIndex + 1,
        type: question2.type, // Keep as string
        typeParameters: typeof question2.typeParameters === 'string' 
          ? question2.typeParameters 
          : JSON.stringify(question2.typeParameters),
        layout: typeof question2.layout === 'string' 
          ? question2.layout 
          : JSON.stringify(question2.layout),
        conditions: question2.conditions || [],
        constraints: Array.isArray(question2.constraints) 
          ? question2.constraints 
          : []
      })
    ]);
    
    // Update local state with new orders
    questions[questionIndex].order = questionIndex + 1;
    questions[newIndex].order = newIndex + 1;
    
    setSteps(newSteps);
  } catch (error) {
    console.error('Failed to reorder questions:', error);
    showToast({ 
      title: "Error",
      message: `Failed to reorder questions: ${error.message}`,
      variant: "error"
    });
  }
};

  // Move step up or down
  const moveStep = async (stepIndex, direction) => {
  const newSteps = [...steps];
  const newIndex = direction === 'up' ? stepIndex - 1 : stepIndex + 1;
  
  // Check bounds
  if (newIndex < 0 || newIndex >= newSteps.length) {
    return;
  }
  
  // Swap steps
  const temp = newSteps[stepIndex];
  newSteps[stepIndex] = newSteps[newIndex];
  newSteps[newIndex] = temp;

  // Assign consecutive order values to all steps (not just the two swapped)
  // This auto-corrects any previously corrupted order indices in the DB
  const stepsWithFixedOrders = newSteps.map((step, idx) => ({ ...step, order: idx + 1 }));

  try {
    // Write all step orders unconditionally to prevent/fix index corruption
    for (const step of stepsWithFixedOrders) {
      await secureHelpers.put(`/api/steps/${step.id}`, {
        id: step.id,
        surveyId: currentSurveyId,
        name: step.name || step.title,
        description: step.description,
        order: step.order
      });
    }

    setSteps(stepsWithFixedOrders);
  } catch (error) {
    console.error('Failed to reorder steps:', error);
    showToast({ 
      title: "Error",
      message: `Failed to reorder steps: ${error.message}`,
      variant: "error"
    });
  }
};

  // Delete a step
  const deleteStep = async (stepIndex) => {
  const step = steps[stepIndex];
  const hasQuestions = step.questions && step.questions.length > 0;
  
  showConfirm({
    title: "Delete Step",
    message: hasQuestions 
      ? `Are you sure you want to delete the step "${step.title}"?\n\nThis will also delete ${step.questions.length} question(s) in this step.`
      : `Are you sure you want to delete the step "${step.title}"?`,
    variant: "danger",
    onConfirm: async () => {
      try {
        // Delete the step (backend should cascade delete questions)
        await secureHelpers.delete(`/api/steps/${step.id}`, { confirmed: true });
        
        // Update local state
        const newSteps = steps.filter((_, index) => index !== stepIndex);
        setSteps(newSteps);
        
        // Update questions list
        const allQuestions = newSteps.flatMap(s => s.questions || []);
        setQuestions(allQuestions);
        
        showToast({ 
          title: "Success",
          message: `Step "${step.title}" deleted successfully`,
          variant: "success"
        });
      } catch (error) {
        console.error('Failed to delete step:', error);
        showToast({ 
          title: "Error",
          message: `Failed to delete step: ${error.message}`,
          variant: "error"
        });
      }
    }
  });
};

  // Get saved surveys (current/draft surveys): isFinal = false or missing
  const loadSavedSurveys = async () => {
    setSurveyListLoading(true);
    setActiveTagFilter([]);
    try {
      const draftSurveys = await secureHelpers.get('/api/surveys?isFinal=false');
      setSavedSurveys(draftSurveys);
      setDialogType("current");
      setShowLoadDialog(true);
    } catch (error) {
      console.error("Load error:", error);
      showToast({ title: "Error", message: "Error loading surveys", variant: "error" });
    } finally {
      setSurveyListLoading(false);
    }
  };

  const loadSurveysForDelete = async () => {
    try {
      const draftSurveys = await secureHelpers.get('/api/surveys?isFinal=false');
      setSavedSurveys(draftSurveys);
      setShowDeleteDialog(true);
    } catch (error) {
      console.error("Load error:", error);
      showToast({ 
        title: "Error",
        message: "Error loading surveys",
        variant: "error"
      });
    }
  };

   const loadClosedSurveys = async () => {
    setSurveyListLoading(true);
    setActiveTagFilter([]);
    try {
      const lockedSurveys = await secureHelpers.get('/api/surveys?isFinal=true');
      setSavedSurveys(lockedSurveys);
      setDialogType("closed");
      setShowLoadDialog(true);
    } catch (error) {
      console.error("Load error:", error);
      showToast({ 
        title: "Error",
        message: "Error loading surveys",
        variant: "error"
      });
    } finally {
      setSurveyListLoading(false);
    }
  };

  const fetchSurveysWithFilters = async ({ modifiedAfter, modifiedBefore }, tags) => {
    setSurveyListLoading(true);
    try {
      const params = new URLSearchParams();
      params.set('isFinal', dialogType === 'closed' ? 'true' : 'false');
      if (modifiedBefore) params.set('modifiedBefore', modifiedBefore);
      if (modifiedAfter) params.set('modifiedAfter', modifiedAfter);
      (tags || []).forEach(t => params.append('tags', t));
      const surveys = await secureHelpers.get(`/api/surveys?${params.toString()}`);
      setSavedSurveys(surveys);
    } catch (error) {
      console.error("Survey search error:", error);
      showToast({ title: "Error", message: "Error searching surveys", variant: "error" });
    } finally {
      setSurveyListLoading(false);
    }
  };

  // Re-fetch surveys when user changes date filter in the load dialog
  const handleSurveyDateSearch = (dateRange) => {
    setCurrentDateRange(dateRange);
    fetchSurveysWithFilters(dateRange, activeTagFilter);
  };

  const handleTagFilterChange = (newTags) => {
    setActiveTagFilter(newTags);
    fetchSurveysWithFilters(currentDateRange, newTags);
  };

  const deleteSurvey = async (survey) => {
    showConfirm({
      title: "Delete Survey",
      message: `Are you sure you want to permanently delete the survey "${survey.title}"?\n\nThis action cannot be undone!`,
      variant: "danger",
      onConfirm: async () => {
    try {
      await secureHelpers.delete(`/api/surveys/${survey.id}`, { confirmed: true });
      showToast({ 
        title: "Success",
        message: `Survey "${survey.title}" has been deleted successfully.`,
        variant: "success"
      });
      
      // Refresh the list
      const draftSurveys = await secureHelpers.get('/api/surveys?isFinal=false');
      setSavedSurveys(draftSurveys);
      
      // If the list is empty, close the dialog
      if (draftSurveys.length === 0) {
        setShowDeleteDialog(false);
      }
    } catch (error) {
      console.error("Delete error:", error);
      showToast({ 
        title: "Error",
        message: `Error deleting survey: ${error.message}`,
        variant: "error"
      });
    }
      }
    });
  };

  // Helper: normalise a question's type — detects ReferenceValue stored as SingleInput
  const normalizeQuestionType = (q) => {
    try {
      const params = typeof q.typeParameters === 'string' ? JSON.parse(q.typeParameters) : q.typeParameters;
      const normalizedType = q.type === "SingleInput" ? "singleInput"
                           : q.type === "InputList" ? "inputList"
                           : q.type === "MultipleChoice" ? "multipleChoice"
                           : q.type === "ReferenceValue" ? "referenceValue"
                           : q.type;
      const isReferenceValue = ((normalizedType === "singleInput") && params.ReferenceSources)
                            || (normalizedType === "referenceValue");
      return { ...q, type: isReferenceValue ? "referenceValue" : normalizedType };
    } catch (e) {
      return q; // If parsing fails, return original
    }
  };

  // Load a specific survey
  const loadSurvey = async (survey) => {
    try {
      // ✅ CLEAR ALL OLD STATE BEFORE LOADING NEW SURVEY
      clearSurveyBuilderState();
      
      // Fetch steps for this survey
      const stepsData = await secureHelpers.get(`/api/steps?surveyId=${survey.id}`);
      // Sort steps by order to ensure correct display
      stepsData.sort((a, b) => a.order - b.order);
      
      // Fetch questions for this survey
      const questionsData = await secureHelpers.get(`/api/questions?surveyId=${survey.id}`);
      
      // Associate questions with their steps manually and sort questions by order
      const stepsWithQuestions = stepsData.map(step => {
        const stepQuestions = questionsData.filter(q => q.stepId === step.id);
        stepQuestions.sort((a, b) => a.order - b.order);
        return {
          ...step,
          questions: stepQuestions
        };
      });
      
      // If loading a "closed" (locked) survey, create a new copy
      if (dialogType === "closed") {

        // Create new survey
        const newSurvey = await secureHelpers.post('/api/surveys', {
          title: `${survey.title} (Copy)`,
          heritageId: survey.heritageId || survey.id,  // Use original's heritageId, or its ID if it's the first
          description: survey.description || null,
          tags: survey.tags || [],
          roles: survey.roles || [],
          isFinal: false
        });
        
        const newSurveyId = newSurvey.id;
        
        // Create new steps for the new survey
        const newSteps = [];
        for (const step of stepsWithQuestions) {
          const newStep = await secureHelpers.post('/api/steps', {
            surveyId: newSurveyId,
            name: step.name,
            description: step.description,
            order: step.order
          });
          
          // replace the old step id's with new temp ones
          newSteps.push({ oldId: step.id, newId: newStep.id, ...newStep, questions: step.questions });
        }
        
        // Create new questions for the new steps
        const newQuestions = [];
        const oldToNewQuestionIds = {}; // Track ID mapping for constraint updates
        const remapReferenceSource = (source) => {
          const oldSourceQuestionId = parseInt(source.questionId || source.QuestionId);
          const newSourceQuestionId = oldToNewQuestionIds[oldSourceQuestionId];

          if (!newSourceQuestionId) {
            console.warn(`⚠️ ReferenceSources: Could not find new ID for source question ${oldSourceQuestionId}`);
            console.warn(`⚠️ Available keys in oldToNewQuestionIds:`, Object.keys(oldToNewQuestionIds));
            return source;
          }

          return {
            QuestionId: newSourceQuestionId.toString(),
            ReferenceType: source.referenceType || source.ReferenceType || 'total'
          };
        };
        
        for (const step of newSteps) {
          for (const question of step.questions) {
            // Parse old typeParameters and add heritage tracking
            const oldParams = typeof question.typeParameters === 'string' 
              ? JSON.parse(question.typeParameters) 
              : question.typeParameters;
            const oldLayout = typeof question.layout === 'string'
              ? (() => { try { return JSON.parse(question.layout || '{}'); } catch { return {}; } })()
              : (question.layout || {});
            const repairedInputListHeritage = (question.type === "inputList" || question.type === "InputList")
              ? repairInputListQuestionHeritage(oldParams.Questions || [], oldLayout.columnHeadings || [])
              : [];

            let newParams = { ...oldParams };
            
            // Handle Questions array first (used by InputList and MultipleChoice)
            if (oldParams.Questions) {
              newParams.Questions = oldParams.Questions.map((q, index) => {
                const repairedHeritageId = repairedInputListHeritage[index]?.heritageId;
                const usedQuestionIdFallback = repairedInputListHeritage[index]?.usedQuestionIdFallback;
                // Detect bad HeritageId values (< 100 are likely index mistakes, including 0)
                let heritageId = repairedHeritageId ?? q.HeritageId ?? q.Id;
                if (!usedQuestionIdFallback && heritageId !== null && heritageId !== undefined && heritageId < 100) {
                  console.warn(`Detected suspicious HeritageId ${heritageId} for question "${q.Label}" - setting to null for backend to assign`);
                  heritageId = null;
                }
                return {
                  ...q,
                  Id: null,  // Backend will generate new IDs
                  HeritageId: heritageId  // Preserve heritage chain, or null if bad data
                };
              });
            }
            
            // Handle MultipleChoice: Choices array - SYNC with Questions array
            if (oldParams.Choices) {
              newParams.Choices = oldParams.Choices.map((choice, index) => {
                // For MultipleChoice, Choices and Questions must have matching HeritageIds
                // Use the HeritageId from the corresponding Question entry (already cleaned)
                let heritageId = newParams.Questions?.[index]?.HeritageId || choice.HeritageId || choice.Id;
                
                // Double-check for bad values in case Questions array didn't exist
                if (heritageId !== null && heritageId !== undefined && heritageId < 100) {
                  console.warn(`Detected suspicious HeritageId ${heritageId} for choice "${choice.Label}" - setting to null for backend to assign`);
                  heritageId = null;
                }
                
                return {
                  ...choice,
                  Id: index,  // Choices need fake sequential IDs (0, 1, 2...), NOT null!
                  HeritageId: heritageId  // Match Questions array for consistency
                };
              });
            }
            
            // Handle ReferenceSources array (used by ReferenceValue)
            // IMPORTANT: Remove ReferenceSources during initial POST to avoid validation errors
            // They will be added back with correct new question IDs in the second pass
            if (oldParams.ReferenceSources) {
              newParams.ReferenceSources = [];  // Empty array for initial POST
              newParams.ReferenceFormula = oldParams.ReferenceFormula || "";  // Preserve formula
            }
            
            // Preserve ReferenceFormula if present
            if (oldParams.ReferenceFormula !== undefined) {
              newParams.ReferenceFormula = oldParams.ReferenceFormula;
            }
            
            // Remap constraint question IDs (e.g., sum_equals;359 -> sum_equals;460)
            const remappedConstraints = (question.constraints || []).map(constraint => {
              // sum_equals;{oldQuestionId} or sum_equals;{oldQuestionId};total or sum_equals;{oldQuestionId};item-{index}
              if (constraint.startsWith('sum_equals;')) {
                const parts = constraint.split(';');
                const oldQuestionId = parseInt(parts[1]);
                const newQuestionId = oldToNewQuestionIds[oldQuestionId];
                if (newQuestionId) {
                  parts[1] = newQuestionId.toString();
                  return parts.join(';');
                }
              }
              // equals_question;{oldQuestionId} or equals_question;{oldQuestionId};total
              if (constraint.startsWith('equals_question;')) {
                const parts = constraint.split(';');
                const oldQuestionId = parseInt(parts[1]);
                const newQuestionId = oldToNewQuestionIds[oldQuestionId];
                if (newQuestionId) {
                  parts[1] = newQuestionId.toString();
                  return parts.join(';');
                }
              }
              // item_equals;{itemIndex};{oldQuestionId};{referenceType}
              if (constraint.startsWith('item_equals;')) {
                const parts = constraint.split(';');
                const oldQuestionId = parseInt(parts[2]);
                const newQuestionId = oldToNewQuestionIds[oldQuestionId];
                if (newQuestionId) {
                  parts[2] = newQuestionId.toString();
                  return parts.join(';');
                }
              }
              // sum_formula_equals;{formula};{oldQ1},{ref1};{oldQ2},{ref2};...
              if (constraint.startsWith('sum_formula_equals;')) {
                const parts = constraint.split(';');
                const remappedSources = parts.slice(2).map(sourcePair => {
                  const [oldQId, refType] = sourcePair.split(',');
                  const newQId = oldToNewQuestionIds[parseInt(oldQId)];
                  return newQId ? `${newQId},${refType}` : sourcePair;
                });
                return [parts[0], parts[1], ...remappedSources].join(';');
              }
              // formula_equals;{formula};{oldQ1},{ref1};{oldQ2},{ref2};...
              if (constraint.startsWith('formula_equals;')) {
                const parts = constraint.split(';');
                const remappedSources = parts.slice(2).map(sourcePair => {
                  const [oldQId, refType] = sourcePair.split(',');
                  const newQId = oldToNewQuestionIds[parseInt(oldQId)];
                  return newQId ? `${newQId},${refType}` : sourcePair;
                });
                return [parts[0], parts[1], ...remappedSources].join(';');
              }
              return constraint;
            });
            
            // Remap condition question IDs (e.g., if;359;equals;text;Yes)
            const remappedConditions = (question.conditions || []).map(condition => {
              if (condition.startsWith('if;')) {
                const parts = condition.split(';');
                const oldQuestionId = parseInt(parts[1]);
                const newQuestionId = oldToNewQuestionIds[oldQuestionId];
                if (newQuestionId) {
                  parts[1] = newQuestionId.toString();
                  return parts.join(';');
                }
              }
              return condition;
            });
            // THIS is what all questions should be created with - same text, type, parameters, but new stepId and new surveyId, and heritageId pointing to the original question
            const questionPayload = {
              surveyId: newSurveyId,
              heritageId: question.id,
              stepId: step.newId,
              text: question.text,
              description: question.description,
              order: question.order,
              type: question.type,
              typeParameters: JSON.stringify(newParams),
              layout: question.layout,
              constraints: remappedConstraints,
              conditions: remappedConditions
            };
            
            const newQuestion = await secureHelpers.post('/api/questions', questionPayload);
            
            // Track old -> new question ID mapping
            oldToNewQuestionIds[question.id] = newQuestion.id;
            
            // Store heritageId for later PUT operations
            newQuestion.storedHeritageId = question.id;
            newQuestions.push(newQuestion);
          }
        }
        
        // Remap ReferenceSources questionIds in ReferenceValue questions
        
        for (const step of newSteps) {
          for (const question of step.questions) {
            
            const oldParams = typeof question.typeParameters === 'string' 
              ? JSON.parse(question.typeParameters) 
              : question.typeParameters;
            const oldLayout = typeof question.layout === 'string'
              ? (() => { try { return JSON.parse(question.layout || '{}'); } catch { return {}; } })()
              : (question.layout || {});
            
            // Check if this question has ReferenceSources/ReferenceColumns (ReferenceValue question)
            const hasReferenceSources = oldParams.ReferenceSources && oldParams.ReferenceSources.length > 0;
            const oldReferenceColumns = oldParams.ReferenceColumns || oldParams.referenceColumns || oldLayout.referenceColumns || [];
            const hasReferenceColumns = oldReferenceColumns.some(column => (column.ReferenceSources || column.referenceSources || []).length > 0);
            if (hasReferenceSources || hasReferenceColumns) {
              
              const newQuestionId = oldToNewQuestionIds[question.id];
              if (!newQuestionId) {
                console.warn(`⚠️ Cannot remap ReferenceSources - new question ID not found for ${question.id}`);
                console.warn(`⚠️ Available keys in oldToNewQuestionIds:`, Object.keys(oldToNewQuestionIds));
                continue;
              }
              
              const remappedSources = hasReferenceSources
                ? oldParams.ReferenceSources.map(remapReferenceSource)
                : [];
              const remappedReferenceColumns = oldReferenceColumns.map(column => ({
                ...column,
                ReferenceSources: (column.ReferenceSources || column.referenceSources || []).map(remapReferenceSource)
              }));
              
              // Update the question with remapped sources
              const updatedParams = {
                ...oldParams,
                ReferenceSources: remappedSources,
                ReferenceDisplayMode: oldParams.ReferenceDisplayMode || oldParams.referenceDisplayMode || oldLayout.referenceDisplayMode,
                ReferenceRowLabel: oldParams.ReferenceRowLabel || oldParams.referenceRowLabel || oldLayout.referenceRowLabel,
                ReferenceColumns: remappedReferenceColumns
              };
              
              // Update the newQuestion object in our array so the UI shows correct data
              const questionInArray = newQuestions.find(q => q.id === newQuestionId);
              if (questionInArray) {
                const existingLayout = typeof questionInArray.layout === 'string'
                  ? (() => { try { return JSON.parse(questionInArray.layout || '{}'); } catch { return {}; } })()
                  : (questionInArray.layout || {});
                const updatedLayout = {
                  ...existingLayout,
                  ...(remappedReferenceColumns.length > 0 ? { referenceColumns: remappedReferenceColumns } : {})
                };
                const putPayload = {
                  id: questionInArray.id,
                  surveyId: questionInArray.surveyId,
                  heritageId: questionInArray.storedHeritageId,
                  stepId: questionInArray.stepId,
                  text: questionInArray.text,
                  description: questionInArray.description || null,
                  isRequired: questionInArray.isRequired ?? false,
                  order: questionInArray.order,
                  type: questionInArray.type,
                  typeParameters: JSON.stringify(updatedParams),
                  layout: JSON.stringify(updatedLayout),
                  constraints: questionInArray.constraints || [],
                  conditions: questionInArray.conditions || []
                };
                
                try {
                  // Update with all required fields for PUT
                  await secureHelpers.put(`/api/questions/${newQuestionId}`, putPayload);
                  
                  // Update local state
                  questionInArray.typeParameters = JSON.stringify(updatedParams);
                  questionInArray.layout = JSON.stringify(updatedLayout);
                } catch (putError) {
                  console.error(`❌ Failed to update ReferenceSources for question ${newQuestionId}:`, putError);
                  // Don't throw - continue with other questions
                }
              }
            }
          }
        }
        
        setCurrentSurveyId(newSurveyId);
        setSurveyTitle(`${survey.title} (Copy)`);
        setSurveyInstructions(survey.description || "");
        setSurveyTags(survey.tags || []);
        setSurveyRoles(survey.roles || []);
        
        // ✅ Transform SingleInput+ReferenceSources to referenceValue type before displaying
        const transformedNewQuestions = newQuestions.map(q => {
          try {
            return normalizeQuestionType(q);
          } catch (e) {
            return q; // If parsing fails, return original
          }
        });

        // Rebuild steps with their questions using new IDs
        const rebuiltSteps = newSteps.map(step => {
          const stepQuestions = transformedNewQuestions.filter(q => q.stepId === step.newId);
          return {
            id: step.newId,
            title: step.name,
            description: step.description,
            order: step.order,
            questions: stepQuestions
          };
        });
        
        // Sort questions by order before setting state (safety check)
        transformedNewQuestions.sort((a, b) => a.order - b.order);
        
        setSteps(rebuiltSteps);
        setQuestions(transformedNewQuestions);
        setShowLoadDialog(false);
        
        // Immediately prompt user to rename the copy
        showToast({
          title: "Survey Copied",
          message: 'Please rename this survey before saving. Remove "(Copy)" from the title and give it a unique name.',
          variant: "info",
          delay: 8000
        });
        
      } else {
        // Loading a current/draft survey - just load it normally
        setCurrentSurveyId(survey.id);
        setSurveyTitle(survey.title);
        setSurveyInstructions(survey.description || "");
        setSurveyTags(survey.tags || []);
        setSurveyRoles(survey.roles || []);
        
        // ✅ Transform SingleInput+ReferenceSources to referenceValue type
        const transformedQuestionsData = questionsData.map(q => {
          try {
            return normalizeQuestionType(q);
          } catch (e) {
            return q; // If parsing fails, return original
          }
        });
        
        // Rebuild steps with their questions (already associated) - apply transformation
        const rebuiltSteps = stepsWithQuestions.map(step => {
          // Transform questions in this step too
          const transformedStepQuestions = step.questions.map(normalizeQuestionType);
          
          return {
            id: step.id,
            title: step.name,
            description: step.description,
            order: step.order,
            questions: transformedStepQuestions
          };
        });
        
        // Sort questions by order before setting state
        transformedQuestionsData.sort((a, b) => a.order - b.order);
        
        setSteps(rebuiltSteps);
        setQuestions(transformedQuestionsData);
        setShowLoadDialog(false);
      }
    
    } catch (error) {
      console.error("Load survey error:", error);
      showToast({ 
        title: "Error",
        message: `Error loading survey details: ${error.message}`,
        variant: "error"
      });
    }
  };

  // Update survey title (steps and questions are auto-saved as they're added)
  const saveSurvey = async () => {
    if (isSaving) return; // Prevent double-clicks
    
    if (!surveyTitle.trim()) {
      showToast({ 
        title: "Validation",
        message: "Please enter a survey title",
        variant: "warning"
      });
      return;
    }
    
    // Warn if trying to save with "(Copy)" in title
    if (surveyTitle.includes("(Copy)")) {
      showToast({
        title: "Please Rename Survey",
        message: 'Survey titles containing "(Copy)" can be confusing. Please give this survey a unique, descriptive name.',
        variant: "warning"
      });
      return;
    }
    
    // Auto-fix step orders to be consecutive starting from 1
    const stepsWithFixedOrders = steps.map((step, index) => ({
      ...step,
      order: index + 1
    }));
    
    // Only update backend if orders changed
    const ordersChanged = stepsWithFixedOrders.some((step, index) => step.order !== steps[index].order);
    if (ordersChanged) {
      for (const step of stepsWithFixedOrders) {
        try {
          await secureHelpers.put(`/api/steps/${step.id}`, {
            id: step.id,
            surveyId: currentSurveyId,
            name: step.title,
            description: step.description,
            order: step.order
          });
        } catch (error) {
          console.error(`Failed to update step ${step.id} order:`, error);
        }
      }
      setSteps(stepsWithFixedOrders);
      showToast({
        title: "Step Orders Fixed",
        message: "Step order numbers have been automatically corrected to be consecutive.",
        variant: "success"
      });
    }
    
    // Validate step orders
    const stepOrders = stepsWithFixedOrders.map(s => s.order).sort((a, b) => a - b);
    const duplicateStepOrders = stepOrders.filter((order, idx) => stepOrders.indexOf(order) !== idx);
    if (duplicateStepOrders.length > 0) {
      showToast({
        title: "Validation Error",
        message: `Duplicate step order numbers found: ${[...new Set(duplicateStepOrders)].join(', ')}. Please contact support on clinical@hospiceuk.org.`,
        variant: "error"
      });
      return;
    }
    
    // Check for consecutive step orders (accept either 0-based or 1-based)
    const isZeroBased = stepOrders[0] === 0;
    const startValue = isZeroBased ? 0 : 1;
    for (let i = 0; i < stepOrders.length; i++) {
      if (stepOrders[i] !== i + startValue) {
        showToast({
          title: "Validation Error",
          message: `Step order numbers are not consecutive. Expected ${i + startValue}, found ${stepOrders[i]}. Please contact support on clinical@hospiceuk.org.`,
          variant: "error"
        });
        return;
      }
    }
    
    // Ensure question orders within each step are consecutive starting from 1
    const stepsWithFixedQuestionOrders = stepsWithFixedOrders.map(step => ({
      ...step,
      questions: step.questions.map((q, qIndex) => ({ ...q, order: qIndex + 1 }))
    }));

    // Always write question orders to backend on Save Survey — ensures DB is in sync
    // even if a real-time reorder PUT failed silently during the editing session.
    for (const step of stepsWithFixedQuestionOrders) {
      for (const question of step.questions) {
        try {
          await secureHelpers.put(`/api/questions/${question.id}`, {
            id: question.id,
            surveyId: currentSurveyId,
            stepId: question.stepId,
            text: question.text,
            description: question.description,
            order: question.order,
            type: question.type,
            typeParameters: typeof question.typeParameters === 'string'
              ? question.typeParameters
              : JSON.stringify(question.typeParameters),
            layout: typeof question.layout === 'string'
              ? question.layout
              : JSON.stringify(question.layout),
            conditions: question.conditions || [],
            constraints: Array.isArray(question.constraints) ? question.constraints : []
          });
        } catch (error) {
          console.error(`Failed to save question ${question.id} order:`, error);
        }
      }
    }

    setSteps(stepsWithFixedQuestionOrders);
    
    setIsSaving(true);
    try {
      // Update the survey title (keep as draft)
      
      await secureHelpers.put(`/api/surveys/${currentSurveyId}`, {
        id: currentSurveyId,
        title: surveyTitle.trim(),
        description: surveyInstructions.trim() || null,
        tags: surveyTags,
        roles: surveyRoles,
        heritageId: currentSurveyId,  // First version uses its own ID as heritage
        isFinal: false  // ✅ CRITICAL: Always false when saving draft
      });
      
      const savedSurveyTitle = surveyTitle; // Store for the message
      const questionCount = steps.reduce((sum, step) => sum + step.questions.length, 0);
      
      // Clear the form after successful save
      sessionStorage.removeItem("previewSurvey"); // Clear preview data after save
      sessionStorage.removeItem("previewTimestamp");
      
      setQuestions([]);
      setSteps([]);
      setSurveyInstructions("");
      setInstructionsSaved(false);
      setQuestionSaved(false);
      setSurveyTitle("");
      setCurrentSurveyId(null);
      setSurveySaved(false);
      setSurveyTags([]);
      setSurveyRoles([]);
      
      showToast({ 
        title: "Success",
        message: `Survey "${savedSurveyTitle}" saved successfully!\n${steps.length} steps, ${questionCount} questions.\n\nYou can open it again from "Load Saved Survey".`,
        variant: "success",
        delay: 6000
      });
      
    } catch (error) {
      console.error("Save error:", error);
      showToast({ 
        title: "Error",
        message: `Error saving survey: ${error.message}`,
        variant: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Load hospices for publishing
  const loadHospices = async () => {
    try {
      const data = await secureHelpers.get('/api/hospices');
      setHospices(data);
    } catch (error) {
      console.error("Error loading hospices:", error);
      showToast({ title: "Error", message: "Error loading hospices", variant: "error" });
    }
  };

  // Load existing tags for autocomplete
  const loadTags = async () => {
    try {
      const tags = await secureHelpers.get('/api/surveys/tags');
      setAvailableTags(tags || []);
    } catch (error) {
      console.error("Error loading tags:", error);
      // Silently fail - tags are optional
    }
  };

  // Load tags on component mount when secureHelpers is ready
  useEffect(() => {
    if (secureHelpers) {
      loadTags();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Only run once on mount

  // Open publish dialog
  const openPublishDialog = async () => {
    if (!currentSurveyId) {
      showToast({ title: "Validation", message: "No survey to publish yet.", variant: "warning" });
      return;
    }
    
    // 💾 Auto-save survey before opening publish settings
    if (!surveyTitle.trim()) {
      showToast({ 
        title: "Validation",
        message: "Please enter a survey title before publishing",
        variant: "warning"
      });
      return;
    }
    
    // Warn if trying to save with "(Copy)" in title
    if (surveyTitle.includes("(Copy)")) {
      showToast({
        title: "Please Rename Survey",
        message: 'Survey titles containing "(Copy)" can be confusing. Please give this survey a unique, descriptive name before publishing.',
        variant: "warning"
      });
      return;
    }
    
    // Check if roles are assigned
    if (!surveyRoles || surveyRoles.length === 0) {
      showToast({
        title: "No Roles Assigned",
        message: "Please assign at least one role (e.g., CEO, Finance Director) before publishing. Without roles, no members will be able to see this survey.",
        variant: "warning"
      });
      return;
    }
    
    setIsSaving(true);
    showToast({
      title: "Saving...",
      message: "Saving survey before opening publish settings...",
      variant: "info",
      delay: 2000
    });
    
    try {
      // Update the survey (keep as draft for now - will be locked on actual publish)
      await secureHelpers.put(`/api/surveys/${currentSurveyId}`, {
        id: currentSurveyId,
        title: surveyTitle.trim(),
        description: surveyInstructions.trim() || null,
        tags: surveyTags,
        roles: surveyRoles,
        heritageId: currentSurveyId,
        isFinal: false  // Keep as draft until published
      });
      
      // Load hospices and open publish dialog
      await loadHospices();
      setShowPublishDialog(true);
      
      showToast({
        title: "Saved",
        message: "Survey saved successfully. Ready to publish!",
        variant: "success",
        delay: 3000
      });
      
    } catch (error) {
      console.error("Save error:", error);
      showToast({ 
        title: "Error",
        message: `Error saving survey: ${error.message}. Please try saving manually first.`,
        variant: "error"
      });
    } finally {
      setIsSaving(false);
    }
  };

  // Publish survey to create instances
  const publishSurvey = async () => {
    if (!currentSurveyId) {
      showToast({ title: "Validation", message: "No survey to publish.", variant: "warning" });
      return;
    }
    
    if (!endDate) {
      showToast({ title: "Validation", message: "Please select an end date", variant: "warning" });
      return;
    }
    
    if (!publishToAll && selectedHospices.length === 0) {
      showToast({ title: "Validation", message: "Please select at least one hospice", variant: "warning" });
      return;
    }
    
    // Show confirmation modal
    const hospiceCount = publishToAll ? hospices.length : selectedHospices.length;
    const confirmMessage = publishToAll 
      ? `You are about to publish this survey to ALL ${hospiceCount} hospices.\n\nAre you sure you want to continue?`
      : `You are about to publish this survey to ${hospiceCount} selected hospice(s).\n\nAre you sure you want to continue?`;
    
    showConfirm({
      title: publishToAll ? "⚠️ Publish to All Hospices" : "Confirm Publish",
      message: confirmMessage,
      variant: publishToAll ? "danger" : "warning",
      onConfirm: async () => {
        try {
          // First, mark the survey as final (locked)
          const updatePayload = {
            id: currentSurveyId,
            title: surveyTitle,
            description: surveyInstructions.trim() || null,
            tags: surveyTags,
            roles: surveyRoles.length > 0 ? surveyRoles : null,  // Send null if empty array
            heritageId: currentSurveyId,  // Preserve heritage when publishing
            isFinal: true
          };

          await secureHelpers.put(`/api/surveys/${currentSurveyId}`, updatePayload);

          const publishData = {
            audience: publishToAll ? 0 : 1,  // Maps to PublishingAudience enum (All=0, List=1)
            audienceHospiceCodes: publishToAll ? null : selectedHospices,
            startDate: startDate,
            endDate: endDate,
            autoClose: autoClose
          };
          
          const result = await secureHelpers.post(`/api/surveys/${currentSurveyId}/publish`, publishData);
          
          setShowPublishDialog(false);
          
          // Clear the survey builder since published surveys can't be edited
          clearSurveyBuilderState();
          
          showToast({
            title: "Success!",
            message: `${result}\n\nThe survey is now available in the Member Data Portal!`,
            variant: "success",
            delay: 5000
          });
          
        } catch (error) {
          console.error("Publish error:", error);
          showToast({ title: "Error", message: `Error publishing survey: ${error.message}`, variant: "error" });
        }
      }
    });
  };

  // Preview survey - save to sessionStorage and navigate in same window
  const previewSurvey = () => {
    if (!currentSurveyId) {
      showToast({ title: "Validation", message: "No survey to preview yet. Please save the survey first.", variant: "warning" });
      return;
    }

    // Save current survey state to sessionStorage for preview page
    const surveyData = {
      id: currentSurveyId,
      title: surveyTitle,
      steps: steps,
      questions: questions,
      roles: surveyRoles
    };
    
    sessionStorage.setItem("previewSurvey", JSON.stringify(surveyData));
    sessionStorage.setItem("previewTimestamp", Date.now().toString()); // ✅ Add timestamp
    
    // Navigate to preview in same window (user can use back button to return)
    navigate('/admin/surveyMaker/preview');
  };

  return (
    <div className="container py-4">
      <h1 className="h2">Survey Builder</h1>
      <p className="mb-3">Select a survey you've been working on ("Load saved survey") or select a closed survey to copy and edit as a new survey ("Load closed survey")</p>
      <div className="d-flex justify-content-end gap-2 mb-3">
        <Button onClick={loadSavedSurveys}>
          Load Saved Survey
        </Button>
        <Button variant="danger" onClick={loadClosedSurveys}>
          Load Closed Surveys
        </Button>
        <Button variant="danger" onClick={loadSurveysForDelete}>
          Delete Survey
        </Button>
      </div>
      {savedSurveys.length === 0 && showDeleteDialog && (
        <div className="alert alert-info">
          <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Note:</span> You can only delete surveys that have not yet been published.
        </div>
      )}
      {/* Load Survey Dialog */}
      {showLoadDialog && (
        <section className="card mb-4" aria-labelledby="load-survey-heading">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 id="load-survey-heading" className="h5">{dialogType === "closed" ? "Select Closed Survey to Copy" : "Select Survey to Load"}</h2>
              <button 
                type="button"
                className="btn-close" 
                onClick={() => {
                  setShowLoadDialog(false);
                  setSavedSurveys([]);
                  setSelectedSurveyId(null);
                  setDialogType("");
                  setActiveTagFilter([]);
                }}
                aria-label="Close"
              ></button>
            </div>

            <SurveySearchFilters
              availableTags={availableTags}
              onDateRangeChange={handleSurveyDateSearch}
              onTagsChange={handleTagFilterChange}
              isSearching={surveyListLoading}
            />

            {(() => {
              const displayed = savedSurveys;
              if (surveyListLoading) {
                return (
                  <div className="text-center py-2 text-muted">
                    <span className="spinner-border spinner-border-sm me-2" />
                    Loading surveys…
                  </div>
                );
              }
              if (displayed.length === 0) {
                return (
                  <p className="text-muted mb-0">
                    {activeTagFilter.length > 0
                      ? 'No surveys match the selected tags. Try removing some tag filters.'
                      : 'No surveys found for the selected filters.'}
                  </p>
                );
              }
              return (
                <>
                  <p className="text-muted small mb-2">
                    {displayed.length} survey{displayed.length !== 1 ? 's' : ''} found
                  </p>
                  <div className="mb-3">
                    <span className="form-label d-block">Select Survey:</span>
                    <SearchableSelect
                      options={[...displayed].sort((a, b) => a.title.localeCompare(b.title))}
                      value={selectedSurveyId || ''}
                      onChange={(surveyId) => setSelectedSurveyId(surveyId || '')}
                      placeholder="Search or select..."
                      getLabel={(survey) => `${survey.title} (Modified: ${new Date(survey.lastModified).toLocaleDateString()})`}
                      getValue={(survey) => survey.id}
                    />
                  </div>
                  <Button
                    onClick={() => {
                      const selectedSurvey = savedSurveys.find(s => s.id === selectedSurveyId);
                      if (selectedSurvey) {
                        loadSurvey(selectedSurvey);
                      } else {
                        console.warn("No survey found with id", selectedSurveyId);
                      }
                    }}
                    disabled={!selectedSurveyId}
                  >
                    {dialogType === "closed" ? "Copy and Edit Survey" : "Load Survey"}
                  </Button>
                </>
              );
            })()}
          </div>
        </section>
      )}

      {/* Delete Survey Dialog */}
      {showDeleteDialog && (
        <section className="card mb-4 border-danger" aria-labelledby="delete-survey-heading">
          <div className="card-body">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h2 id="delete-survey-heading" className="h5 text-danger">Delete Unpublished Survey</h2>
              <button 
                type="button"
                className="btn-close" 
                onClick={() => {
                  setShowDeleteDialog(false);
                  setSavedSurveys([]);
                }}
                aria-label="Close"
              ></button>
            </div>
            <div className="alert alert-warning">
              <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>⚠️ Warning:</span> You can only delete surveys that have not yet been published. Deleted surveys cannot be recovered.
            </div>
            {savedSurveys.length === 0 ? (
              <p className="text-muted">No unpublished surveys found to delete.</p>
            ) : (
              <div className="list-group">
                {savedSurveys.map((survey) => (
                  <div
                    key={survey.id}
                    className="list-group-item"
                  >
                    <div className="d-flex justify-content-between align-items-center">
                      <div>
                        <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>{survey.title}</span>
                        <br />
                        <small className="text-muted">
                          Last modified: {new Date(survey.lastModified).toLocaleDateString()}
                        </small>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-danger"
                        onClick={() => deleteSurvey(survey)}
                      >
                        Delete
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </section>
      )}

      {/* Survey Title */}
      <section className="mb-3" aria-labelledby="survey-title-heading">
        <h2 id="survey-title-heading" className="h4">Survey Title</h2>
        <input
          id="survey-title"
          type="text"
          className="form-control"
          value={surveyTitle}
          onChange={(e) => setSurveyTitle(e.target.value)}
          placeholder="e.g., Patient Safety Survey 2025"
        />
      </section>
    
      <section className="mb-3" aria-labelledby="survey-instructions-heading">
        <h2 id="survey-instructions-heading" className="h4">Survey Detailed Instructions (optional)</h2>
        <AdvancedTextEditor
          value={surveyInstructions}
          onChange={(value) => setSurveyInstructions(value)}
          placeholder="Provide detailed instructions with links if needed for survey respondents here..."
        />
        {instructionsSaved && (
          <small className="text-success d-block mt-2">
            ✓ Instructions saved
          </small>
        )}
      </section>
      <section aria-label="Survey access and tags">
        {/* Add roles */}
      <RolesSelector
        selectedRoles={surveyRoles}
        onRolesChange={(newRoles) => {
          setSurveyRoles(newRoles);
        }}
        title="Survey Roles (required)"
        description="Select which roles can access this survey"
      />
     
      {/* Add tags */}
      <TagsInput
        tags={surveyTags}
        onTagsChange={(newTags) => {
          setSurveyTags(newTags);
        }}
        availableTags={availableTags}
      />
      </section>

      {/* Create Survey Button */}
      {!currentSurveyId ? (
        <section className="mb-4 mt-4 p-3 border rounded" style={{ backgroundColor: "#f8f9fa" }} aria-label="Create survey">
          <p className="mb-3">
            <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Step 1:</span> Click the button below to create your survey with the title, description, and tags you've entered above.
          </p>
          <Button 
            onClick={createSurveyStub}
            disabled={!surveyTitle.trim() || isCreatingStub}
            variant="success"
          >
            {isCreatingStub ? "Creating..." : "Create Survey"}
          </Button>
          {!surveyTitle.trim() && (
            <small className="text-muted d-block mt-2">
              Please enter a survey title first
            </small>
          )}
        </section>
      ) : (
        <div className="alert mt-4" role="alert" style={{ 
          backgroundColor: 'rgba(241, 109, 9, 0.15)',
          border: '2px solid #f16d09',
          color: '#2e1719'
        }}>
          <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif", fontSize: 'var(--font-lg)' }}>
            ✓ Survey "{surveyTitle}" created! You can now add steps and questions below.
          </span>
        </div>
      )}
      
      <hr />
      
      {/* Step and step description creation - only show after survey is created */}
      {currentSurveyId && (
        <section className="card mb-4" style={{ border: steps.length === 0 ? "3px solid #f16d09" : "1px solid #ddd" }} aria-labelledby="add-survey-steps-heading">
          <div className="card-header" style={{ 
            backgroundColor: '#FFF3CD', 
            borderBottom: '2px solid #FFC60B',
            padding: '1rem'
          }}>
            <h2 id="add-survey-steps-heading" style={{ 
              margin: 0, 
              fontSize: 'var(--font-xl)',
              fontFamily: "'Neutraface-Demi', Arial, sans-serif",
              color: '#2e1719'
            }}>
              Step 1: Add Survey Steps
            </h2>
          </div>
          <div className="card-body">
            {steps.length === 0 && (
              <div className="alert" style={{ backgroundColor: '#FFF3CD', border: '1px solid #FFC60B', marginBottom: '1rem' }}>
                <p style={{ margin: 0, fontSize: 'var(--font-base)', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                  👉 Add your first step to begin organizing your survey questions
                </p>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label semibold-text" htmlFor="step-title">Step Title</label>
              <input
                id="step-title"
                type="text"
                className="form-control"
                value={stepTitle}
                onChange={(e) => setStepTitle(e.target.value)}
                placeholder="e.g., Demographics, Service Information"
              />
            </div>
            
            <div className="mb-3">
              <label className="form-label semibold-text" htmlFor="step-description">Step Description (optional)</label>
              <textarea
                id="step-description"
                className="form-control"
                value={stepDescription}
                onChange={(e) => setStepDescription(e.target.value)}
                placeholder="Describe what this step collects"
                rows="2"
              />
            </div>
            
            <Button onClick={addStep}>Add Step</Button>
          </div>
        </section>
      )}

    {steps.length > 0 && (<>
      <section id="question-form" className="card mb-4" style={{ border: '1px solid #ddd' }} aria-labelledby="build-question-details-heading">
        <div className="card-header" style={{ 
          backgroundColor: '#ffe5cc', 
          borderBottom: '2px solid #f16d09',
          padding: '1rem'
        }}>
          <h2 id="build-question-details-heading" style={{ 
            margin: 0, 
            fontSize: 'var(--font-xl)',
            fontFamily: "'Neutraface-Demi', Arial, sans-serif",
            color: '#2e1719'
          }}>
            Step 2: Build Question Details
          </h2>
        </div>
        <div className="card-body">
          <div className="mb-3">
            <span className="form-label semibold-text d-block">Select Step</span>
            <SearchableSelect
              options={steps}
              value={selectedStep !== null && steps[selectedStep] ? steps[selectedStep].id : null}
              onChange={(stepId) => {
                const stepIndex = steps.findIndex(s => s.id === stepId);
                setSelectedStep(stepIndex);
              }}
              placeholder="Select which step this question belongs to"
              getLabel={(step) => step.title || step.name}
              getValue={(step) => step.id}
            />
          </div>

          {!isEditing && (<div className="mb-3">
            <label className="form-label semibold-text" htmlFor="question-type">Question Type</label>
            <select
              id="question-type"
              className="form-select"
              value={questionType}
              onChange={(e) => setQuestionType(e.target.value)}
            >
              <option value="singleInput">Single Input - One field for user to type</option>
              <option value="multipleChoice">Multiple Choice - User SELECTS from options</option>
              <option value="inputList">Input List - Multiple fields for user to answer (age bands, staff breakdown)</option>
              <option value="referenceValue">Reference Value - Display a baseline number for comparison (no user input)</option>
            </select>
            <div style={{ 
              marginTop: '0.5rem', 
              padding: '0.75rem', 
              backgroundColor: '#FFF3CD', 
              borderLeft: '4px solid #FFC60B',
              fontSize: 'var(--font-sm)'
            }}>
              <p style={{ margin: 0, fontFamily: "'Neutraface-Demi', Arial, sans-serif", marginBottom: '0.25rem' }}>
                💡 Quick Guide:
              </p>
              <p style={{ margin: 0 }}>
                • <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Single Input:</span> "What is your total staff count?" → One number box<br/>
                • <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Multiple Choice:</span> "Do you collect age data?" → Yes/No dropdown<br/>
                • <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Input List:</span> "Staff by age band" → Under 20 [___], 20-24 [___], 25-29 [___], etc.
              </p>
            </div>
          </div>)}

          <div className="mb-3">
            <label className="form-label semibold-text" htmlFor="question-text">Question Text</label>
            <input
              id="question-text"
              type="text"
              className="form-control"
              defaultValue={questionText}
              key={questionText === '' ? 'empty-qt' : undefined}
              onBlur={(e) => setQuestionText(e.target.value)}
              placeholder="e.g., How many staff members do you have?"
            />
            {questionSaved && (
              <small className="text-success d-block mt-2">
                ✓ Question saved
              </small>
            )}
          </div>

          <div className="mb-3">
            <label className="form-label semibold-text" htmlFor="question-description">Description (optional)</label>
            <input
              id="question-description"
              type="text"
              className="form-control"
              defaultValue={questionDescription}
              key={questionDescription === '' ? 'empty-qd' : undefined}
              onBlur={(e) => setQuestionDescription(e.target.value)}
              placeholder="Additional context or instructions for respondents"
            />
          </div>

          {/* Display question as heading option */}
          <div className="mb-3">
            <div className="form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="displayAsHeading"
                checked={displayAsHeading}
                onChange={(e) => setDisplayAsHeading(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="displayAsHeading">
                <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Display question text as heading</span>
              </label>
            </div>
            <p className="text-muted ms-4" style={{ fontSize: 'var(--font-sm)', marginTop: '0.25rem' }}>
              Formats the question text with orange background and larger font (like section headings)
            </p>
          </div>

          {/* Display horizontally option - for InputList questions */}
          {questionType === "inputList" && (
            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="displayAsHorizontalInputList"
                  checked={displayAsHorizontal}
                  onChange={(e) => setDisplayAsHorizontal(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="displayAsHorizontalInputList">
                  <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Display horizontally (table row style)</span>
                </label>
              </div>
              <p className="text-muted ms-4" style={{ fontSize: 'var(--font-sm)', marginTop: '0.25rem' }}>
                When checked, displays question text on the left and input field on the right in a table row. Uncheck for traditional vertical layout (question above, input below).
              </p>
            </div>
          )}

          {/* Section Heading - for grouping questions visually */}
          {(questionType === "singleInput" || questionType === "inputList" || questionType === "multipleChoice") && (
            <div className="mb-3">
              <label className="form-label semibold-text">Section Heading (optional)</label>
              <input
                type="text"
                className="form-control"
                value={sectionHeading}
                onChange={(e) => setSectionHeading(e.target.value)}
                placeholder="e.g., Turnover and sickness, Workforce demographics"
              />
              <small className="text-muted d-block mt-1" style={{ fontSize: 'var(--font-sm)' }}>
                Displays as a prominent heading above this entire question section
              </small>
            </div>
          )}
        </div>
      </section>

          {/* Reference Value - multi-source dynamic calculation */}
          {questionType === "referenceValue" && (
            <section className="card mb-3" style={{ border: '2px solid #FFF3CD', borderLeft: '5px solid #FFC60B' }} aria-labelledby="reference-value-fields-heading">
              <div className="card-header" style={{
                backgroundColor: '#FFF3CD',
                borderBottom: '2px solid #FFC60B',
                padding: '0.75rem 1rem'
              }}>
                <h2 id="reference-value-fields-heading" style={{
                  fontSize: 'var(--font-lg)',
                  fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                  color: '#2e1719',
                  margin: 0,
                  fontWeight: 'normal'
                }}>
                  Step 3: Question config - Configure Reference Value
                </h2>
              </div>
              <div className="card-body">
              <div className="mb-3">
                <label className="form-label semibold-text">Display Mode</label>
                <small className="text-muted d-block mb-2" style={{ fontSize: 'var(--font-sm)' }}>Select whether you want a row of totals (as in a table row) to match the format of a previous question or questions, or whether you just want a single total</small>
                <select
                  className="form-select"
                  value={referenceDisplayMode}
                  onChange={(e) => setReferenceDisplayMode(e.target.value)}
                >
                  <option value="single">Single calculated value</option>
                  <option value="tableRow">Table row with calculated columns</option>
                </select>
              </div>

              <div style={{ display: referenceDisplayMode === "single" ? "block" : "none" }}>
                <label className="form-label semibold-text">Reference Sources</label>
                <small className="text-muted d-block mb-2" style={{ fontSize: 'var(--font-sm)' }}>
                  Pull values from other questions and calculate a sum to display as a reference.
                  For example: "Total staff from all departments" or "Combined budget from multiple line items".
                </small>

              {/* Add new source */}
              <div className="card p-3 mb-2" style={{ backgroundColor: 'var(--color-background-light)' }}>
                <div className="semibold-text mb-2">Add Source</div>
                
                {/* Question selector */}
                <div className="mb-2">
                  <label className="form-label" style={{ fontSize: 'var(--font-sm)' }}>Select Question</label>
                  <select
                    className="form-select form-select-sm"
                    value={refFormula.sourceQuestionId}
                    onChange={(e) => {
                      const selectedId = e.target.value;
                      refFormula.setSourceQuestionId(selectedId);
                      
                      // Find the selected question and check if it's an inputList with items
                      if (selectedId) {
                        const allQuestions = getAllPreviousQuestions();
                        const selectedQ = allQuestions.find(q => q.id === parseInt(selectedId));
                        if (selectedQ) {
                          // Store question type
                          refFormula.setSourceQuestionType(selectedQ.type);
                          
                          let params = {};
                          try {
                            params = typeof selectedQ.typeParameters === 'string' 
                              ? JSON.parse(selectedQ.typeParameters) 
                              : selectedQ.typeParameters;
                          } catch (e) {}
                          
                          const items = params.Questions || params.questions || params.Choices || params.choices || [];
                          refFormula.setSourceItems(items);
                          
                          // Check if has Custom Total (grandtotal)
                          let layoutData = {};
                          try {
                            layoutData = typeof selectedQ.layout === 'string' 
                              ? JSON.parse(selectedQ.layout) 
                              : selectedQ.layout;
                          } catch (e) {}
                          refFormula.setSourceHasGrandTotal(layoutData.showGrandTotal || false);
                        }
                      } else {
                        refFormula.setSourceQuestionType("");
                        refFormula.setSourceItems([]);
                        refFormula.setSourceHasGrandTotal(false);
                      }
                      refFormula.setSourceReferenceType("total"); // Reset to total
                    }}
                  >
                    <option value="">-- Select a question --</option>
                    {getAllPreviousQuestions().map((q, idx) => {
                      // Count global question number
                      let globalNum = 0;
                      for (const step of steps) {
                        for (const sq of step.questions || []) {
                          globalNum++;
                          if (sq.id === q.id) break;
                        }
                        if (globalNum > 0 && steps.flatMap(s => s.questions || []).filter((sq, i) => i < globalNum - 1).pop()?.id === q.id) break;
                      }
                      return (
                        <option key={q.id} value={q.id}>
                          Q{idx + 1}: {q.text.substring(0, 60)}{q.text.length > 60 ? '...' : ''}
                        </option>
                      );
                    })}
                  </select>
                </div>
                
                {/* Reference type selector - only show if question is InputList */}
                {refFormula.sourceQuestionId && (refFormula.sourceQuestionType === 'inputList' || refFormula.sourceQuestionType === 'InputList') && refFormula.sourceItems.length > 0 && (
                  <div className="mb-2">
                    <label className="form-label" style={{ fontSize: 'var(--font-sm)' }}>What to pull from this question</label>
                    <select
                      className="form-select form-select-sm"
                      value={refFormula.sourceReferenceType}
                      onChange={(e) => refFormula.setSourceReferenceType(e.target.value)}
                    >
                      <option value="total">Total (sum of all items)</option>
                      {getQuestionColumnHeadings(refFormula.sourceQuestionId).map((heading, idx) => (
                        <option key={idx} value={`column-total-${idx}`}>
                          Total of {heading} column
                        </option>
                      ))}
                      {refFormula.sourceHasGrandTotal && (
                        <option value="grandtotal">Custom Total (grand total calculation)</option>
                      )}
                      {refFormula.sourceItems.map((item, idx) => (
                        <option key={idx} value={`item-${idx}`}>
                          Item {idx + 1}: {item.Label || item.label || `Item ${idx + 1}`}
                        </option>
                      ))}
                    </select>
                  </div>
                )}
                
                {/* Add source button */}
                <button
                  type="button"
                  className="btn btn-sm btn-primary"
                  onClick={() => {
                    
                    if (!refFormula.sourceQuestionId) {
                      showToast({
                        title: "Select Question",
                        message: "Please select a question to add as a source",
                        variant: "warning"
                      });
                      return;
                    }
                    
                    // Add the source
                    const newSource = {
                      questionId: refFormula.sourceQuestionId,
                      // InputList: use selected referenceType (total, item-X, grandtotal)
                      // Other types (SingleInput, MultipleChoice): always use "total"
                      referenceType: (refFormula.sourceQuestionType === 'inputList' || refFormula.sourceQuestionType === 'InputList') && refFormula.sourceItems.length > 0 
                        ? refFormula.sourceReferenceType 
                        : 'total'  // Default to 'total' for simple question types
                    };
                    
                    refFormula.setSources([...refFormula.sources, newSource]);
                    
                    // Reset form
                    refFormula.setSourceQuestionId("");
                    refFormula.setSourceQuestionType("");
                    refFormula.setSourceReferenceType("total");
                    refFormula.setSourceItems([]);
                    refFormula.setSourceHasGrandTotal(false);
                  }}
                  disabled={!refFormula.sourceQuestionId}
                >
                  Add Source
                </button>
              </div>

              {/* List of added sources */}
              {refFormula.sources.length > 0 && (
                <div className="mb-3">
                  <div className="semibold-text mb-2">Sources ({refFormula.sources.length}):</div>
                  {refFormula.sources.map((source, index) => {
                    // Find question details for display
                    // Handle both PascalCase (from backend) and camelCase (from frontend) property names
                    const questionId = source.QuestionId || source.questionId;
                    const referenceType = source.ReferenceType || source.referenceType;

                    let questionText = `Question ID ${questionId}`;
                    let globalNumber = 0;
                    for (const step of steps) {
                      for (const q of step.questions || []) {
                        globalNumber++;
                        if (q.id === parseInt(questionId)) {
                          questionText = `Q${globalNumber}: ${q.text.substring(0, 40)}${q.text.length > 40 ? '...' : ''}`;
                          break;
                        }
                      }
                      if (!questionText.startsWith('Question ID')) break;
                    }

                    let referenceDesc = "";
                    if (referenceType === 'grandtotal') {
                      referenceDesc = " [Custom Total]";
                    } else if (referenceType === 'total') {
                      referenceDesc = " [Total]";
                    } else if (referenceType && referenceType.startsWith('column-total-')) {
                      const columnIndex = parseInt(referenceType.split('-')[2] || '0');
                      const heading = getQuestionColumnHeadings(questionId)[columnIndex] || `Column ${columnIndex + 1}`;
                      referenceDesc = ` [Total of ${heading}]`;
                    } else if (referenceType && referenceType.startsWith('item-')) {
                      referenceDesc = ` [Item ${parseInt(referenceType.split('-')[1]) + 1}]`;
                    }

                    return (
                      <div key={index} className="d-flex align-items-center mb-2 p-2 bg-light rounded">
                        <span className="flex-grow-1" style={{ fontSize: 'var(--font-sm)' }}>
                          {questionText}{referenceDesc}
                        </span>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-danger"
                          onClick={() => {
                            refFormula.setSources(refFormula.sources.filter((_, i) => i !== index));
                          }}
                        >
                          Remove
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}
              
              {refFormula.sources.length === 0 && (
                <div className="alert alert-info" style={{ fontSize: 'var(--font-sm)' }}>
                  No sources added yet. Add questions above to calculate a dynamic reference value.
                </div>
              )}
              
              {/* Formula Builder - show if multiple sources OR user wants to add constants */}
              {refFormula.sources.length >= 1 && (
                <div className="mt-3">
                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <label className="form-label semibold-text mb-0">Calculation Formula {refFormula.sources.length === 1 && '(optional)'}</label>
                    <Button
                      variant="outline-secondary"
                      size="sm"
                      onClick={() => refFormula.setBuilding(!refFormula.building)}
                    >
                      {refFormula.building ? 'Hide ▲' : 'Build Formula ▼'}
                    </Button>
                  </div>
                  <small className="text-muted d-block mb-2" style={{ fontSize: 'var(--font-sm)' }}>
                    {!refFormula.building && refFormula.sources.length > 1 && refFormula.formulaParts.length === 0 && "Default: All sources will be added together (+)"}
                    {!refFormula.building && refFormula.sources.length === 1 && refFormula.formulaParts.length === 0 && "Add constants (like 365 for days/year) or operators"}
                    {!refFormula.building && refFormula.formulaParts.length > 0 && "Custom formula defined ✓"}
                  </small>
                  {refFormula.building && (
                    <>
                      <div className="alert alert-info mb-3" style={{ fontSize: 'var(--font-sm)', padding: '0.5rem 0.75rem' }}>
                        <strong>Examples:</strong>
                        <ul className="mb-0 mt-1" style={{ paddingLeft: '1.25rem' }}>
                          <li>Sum sources: 0+1+2</li>
                          <li>Multiply by constant: 0×365</li>
                          <li>Complex: 0+1-2</li>
                        </ul>
                      </div>
                     <FormulaBuilder
                          getDisplayText={getReferenceFormulaDisplay}
                          elements={refFormula.sources}
                          getElementLabel={(source, index) => getSourceLabel(source, index)}
                          elementSectionLabel="Add Source:"
                          onAddElement={(_, index) => addReferenceFormulaSource(index)}
                          operators="arithmetic"
                          onAddOperator={addReferenceFormulaOperator}
                          onClear={clearReferenceFormula}
                          onCancel={() => { refFormula.setBuilding(false); clearReferenceFormula(); }}
                          onConfirm={() => refFormula.setBuilding(false)}
                          confirmLabel="✓ Confirm Formula"
                        />
                    </>
                  )}
                  
                  {/* Add Constants/Numbers section */}
                  {refFormula.building && (
                    <div className="mt-3 p-3" style={{ backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d9ff' }}>
                      <label className="form-label semibold-text" style={{ fontSize: 'var(--font-sm)' }}>
                        Add Number (constant):
                      </label>
                      <div className="d-flex align-items-center gap-2">
                        <input
                          type="number"
                          className="form-control"
                          style={{ maxWidth: '150px' }}
                          value={constantInput}
                          onChange={(e) => setConstantInput(e.target.value)}
                          placeholder="e.g., 365"
                          onKeyPress={(e) => {
                            if (e.key === 'Enter') {
                              e.preventDefault();
                              addReferenceFormulaConstant();
                            }
                          }}
                        />
                        <Button
                          variant="primary"
                          size="sm"
                          onClick={addReferenceFormulaConstant}
                          disabled={!constantInput}
                        >
                          Add Number
                        </Button>
                      </div>
                      <small className="text-muted d-block mt-1" style={{ fontSize: 'var(--font-xs)' }}>
                        Example: <em>[No Beds] × 365</em> (beds multiplied by days per year)
                      </small>
                    </div>
                  )}
                </div>
              )}
              </div>

              {referenceDisplayMode === "tableRow" && (
                <div className="mt-3">
                  <div className="mb-3">
                    <label className="form-label semibold-text">Row label</label>
                    <input
                      type="text"
                      className="form-control"
                      value={referenceRowLabel}
                      onChange={(e) => setReferenceRowLabel(e.target.value)}
                      placeholder={questionText || "e.g., Total expenditure"}
                    />
                  </div>

                  <div className="card p-3 mb-3" style={{ border: '2px solid #FFF3CD', borderLeft: '5px solid #FFC60B' }}>
                    <div className="d-flex align-items-center gap-2 mb-1">
                      <label className="form-label semibold-text mb-0">
                        Create grand totals by adding previous question totals
                      </label>
                      <TipBadge
                        tooltip={(
                          <div style={{ textAlign: 'left' }}>
                            <div style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif", marginBottom: '0.35rem' }}>
                              To make a grand total row:
                            </div>
                            <ol style={{ margin: 0, paddingLeft: '1.1rem' }}>
                              <li>Choose the first source question.</li>
                              <li>Click the button "create columns from this question".</li>
                              <li>Choose each additional source question.</li>
                              <li>Use the "Add this question's totals to existing columns" button to add them to your Reference Value question column totals.</li>
                              <li>When you have all the sources you want to include, click the "Add and save question" button and you're done!</li>
                            </ol>
                            <div style={{ marginTop: '0.35rem' }}>
                              Each displayed column will add the matching column totals from every selected source question - you should see each source question listed under "sources" in the Column sections of the Reference Value question, and you'll see the format in the "Live Preview" under the current question.
                            </div>
                          </div>
                        )}
                        badgeStyle={{
                          display: 'inline-flex',
                          alignItems: 'center',
                          justifyContent: 'center',
                          width: '1.35rem',
                          height: '1.35rem',
                          borderRadius: '50%',
                          backgroundColor: '#492c28',
                          color: '#fff',
                          fontSize: 'var(--font-sm)',
                          fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                          cursor: 'help'
                        }}
                      >
                        ?
                      </TipBadge>
                    </div>
                    <small className="text-muted d-block mb-2" style={{ fontSize: 'var(--font-sm)' }}>
                      Use this to create a read-only grand total row that matches the column layout of previous multi-column Input List questions. First create the column format you want to match (and that question's totals) from one question, then add totals from each other question of the same column format that should be included.
                    </small>
                    <div className="d-flex gap-2 align-items-start flex-wrap">
                      <select
                        className="form-select"
                        style={{ maxWidth: '520px' }}
                        value={referenceTemplateQuestionId}
                        onChange={(e) => setReferenceTemplateQuestionId(e.target.value)}
                      >
                        <option value="">-- Select an InputList with columns --</option>
                        {getInputListQuestionsWithColumns().map((q, idx) => (
                          <option key={q.id} value={q.id}>
                            Q{idx + 1}: {q.text.substring(0, 70)}{q.text.length > 70 ? '...' : ''}
                          </option>
                        ))}
                      </select>
                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => createColumnsFromInputList(referenceTemplateQuestionId)}
                        disabled={!referenceTemplateQuestionId}
                      >
                        Create columns from this question
                      </Button>
                      <Button
                        variant="secondary"
                        size="sm"
                        onClick={() => addMatchingColumnTotalsFromInputList(referenceTemplateQuestionId)}
                        disabled={!referenceTemplateQuestionId}
                      >
                        Add this question's totals to existing columns
                      </Button>
                    </div>
                  </div>

                  {referenceColumns.map((column, columnIndex) => (
                    <div key={columnIndex} className="card p-3 mb-3" style={{ backgroundColor: 'var(--color-background-light)' }}>
                      <div className="d-flex justify-content-between align-items-center mb-2">
                        <div className="semibold-text">Column {columnIndex + 1}</div>
                        {referenceColumns.length > 1 && (
                          <button
                            type="button"
                            className="btn btn-sm btn-outline-danger"
                            onClick={() => setReferenceColumns(referenceColumns.filter((_, idx) => idx !== columnIndex))}
                          >
                            Remove Column
                          </button>
                        )}
                      </div>

                      <div className="mb-2">
                        <label className="form-label" style={{ fontSize: 'var(--font-sm)' }}>Column heading</label>
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={column.Heading || ""}
                          onChange={(e) => updateReferenceColumn(columnIndex, { Heading: e.target.value })}
                          placeholder={`Column ${columnIndex + 1}`}
                        />
                      </div>

                      {(column.ReferenceSources || []).length > 0 && (
                        <div className="mb-2">
                          <div className="semibold-text mb-2" style={{ fontSize: 'var(--font-sm)' }}>Sources ({column.ReferenceSources.length})</div>
                          {column.ReferenceSources.map((source, sourceIndex) => (
                            <div key={sourceIndex} className="d-flex align-items-center mb-2 p-2 bg-white rounded">
                              <span className="flex-grow-1" style={{ fontSize: 'var(--font-sm)' }}>
                                {getSourceLabel(source, sourceIndex)}
                              </span>
                              <button
                                type="button"
                                className="btn btn-sm btn-outline-danger"
                                onClick={() => updateReferenceColumn(columnIndex, currentColumn => ({
                                  ...currentColumn,
                                  ReferenceSources: currentColumn.ReferenceSources.filter((_, idx) => idx !== sourceIndex)
                                }))}
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="mb-2">
                        <label className="form-label" style={{ fontSize: 'var(--font-sm)' }}>Select Question</label>
                        <select
                          className="form-select form-select-sm"
                          value={column.sourceQuestionId}
                          onChange={(e) => selectReferenceColumnSourceQuestion(columnIndex, e.target.value)}
                        >
                          <option value="">-- Select a question --</option>
                          {getAllPreviousQuestions().map((q, idx) => (
                            <option key={q.id} value={q.id}>
                              Q{idx + 1}: {q.text.substring(0, 60)}{q.text.length > 60 ? '...' : ''}
                            </option>
                          ))}
                        </select>
                      </div>

                      {column.sourceQuestionId && (column.sourceQuestionType === 'inputList' || column.sourceQuestionType === 'InputList') && column.sourceItems.length > 0 && (
                        <div className="mb-2">
                          <label className="form-label" style={{ fontSize: 'var(--font-sm)' }}>What to pull from this question</label>
                          <select
                            className="form-select form-select-sm"
                            value={column.sourceReferenceType}
                            onChange={(e) => updateReferenceColumn(columnIndex, { sourceReferenceType: e.target.value })}
                          >
                            <option value="total">Total (sum of all items)</option>
                            {(column.sourceColumnHeadings || []).map((heading, idx) => (
                              <option key={idx} value={`column-total-${idx}`}>
                                Total of {heading} column
                              </option>
                            ))}
                            {column.sourceHasGrandTotal && (
                              <option value="grandtotal">Custom Total (grand total calculation)</option>
                            )}
                            {column.sourceItems.map((item, idx) => (
                              <option key={idx} value={`item-${idx}`}>
                                Item {idx + 1}: {item.Label || item.label || `Item ${idx + 1}`}
                              </option>
                            ))}
                          </select>
                        </div>
                      )}

                      <Button
                        variant="primary"
                        size="sm"
                        onClick={() => addReferenceColumnSource(columnIndex)}
                        disabled={!column.sourceQuestionId}
                      >
                        Add Source
                      </Button>

                      {(column.ReferenceSources || []).length > 0 && (
                        <div className="mt-3">
                          <div className="d-flex justify-content-between align-items-center mb-2">
                            <label className="form-label semibold-text mb-0">Column Formula {(column.ReferenceSources || []).length === 1 && '(optional)'}</label>
                            <Button
                              variant="outline-secondary"
                              size="sm"
                              onClick={() => updateReferenceColumn(columnIndex, { building: !column.building })}
                            >
                              {column.building ? 'Hide ▲' : 'Build Formula ▼'}
                            </Button>
                          </div>
                          <small className="text-muted d-block mb-2" style={{ fontSize: 'var(--font-sm)' }}>
                            {!column.building && (column.ReferenceFormulaParts || []).length === 0 && "Default: all sources in this column will be added together"}
                            {!column.building && (column.ReferenceFormulaParts || []).length > 0 && "Custom formula defined ✓"}
                          </small>

                          {column.building && (
                            <>
                              <FormulaBuilder
                                getDisplayText={() => getColumnFormulaDisplay(column)}
                                elements={column.ReferenceSources || []}
                                getElementLabel={(source, index) => getSourceLabel(source, index)}
                                elementSectionLabel="Add Source:"
                                onAddElement={(_, index) => addReferenceColumnFormulaSource(columnIndex, index)}
                                operators="arithmetic"
                                onAddOperator={(operator) => addReferenceColumnFormulaOperator(columnIndex, operator)}
                                onClear={() => updateReferenceColumn(columnIndex, { ReferenceFormulaParts: [] })}
                                onCancel={() => updateReferenceColumn(columnIndex, { building: false, ReferenceFormulaParts: [] })}
                                onConfirm={() => updateReferenceColumn(columnIndex, { building: false })}
                                confirmLabel="✓ Confirm Formula"
                              />

                              <div className="mt-3 p-3" style={{ backgroundColor: '#e7f3ff', borderRadius: '4px', border: '1px solid #b3d9ff' }}>
                                <label className="form-label semibold-text" style={{ fontSize: 'var(--font-sm)' }}>
                                  Add Number (constant):
                                </label>
                                <div className="d-flex align-items-center gap-2">
                                  <input
                                    type="number"
                                    className="form-control"
                                    style={{ maxWidth: '150px' }}
                                    value={column.constantInput}
                                    onChange={(e) => updateReferenceColumn(columnIndex, { constantInput: e.target.value })}
                                    placeholder="e.g., 365"
                                  />
                                  <Button
                                    variant="primary"
                                    size="sm"
                                    onClick={() => addReferenceColumnFormulaConstant(columnIndex)}
                                    disabled={!column.constantInput}
                                  >
                                    Add Number
                                  </Button>
                                </div>
                              </div>
                            </>
                          )}
                        </div>
                      )}
                    </div>
                  ))}

                  <Button
                    variant="outline-secondary"
                    size="sm"
                    onClick={() => setReferenceColumns([...referenceColumns, createReferenceColumn(`Column ${referenceColumns.length + 1}`)])}
                  >
                    Add Column
                  </Button>
                </div>
              )}
            </div>
            </section>
)}                

        {/* Type-specific fields */}
        {(questionType === "multipleChoice" || questionType === "inputList" || questionType === "singleInput") && (
          <section className="card mb-3" style={{ border: '2px solid #FFF3CD', borderLeft: '5px solid #FFC60B' }} aria-labelledby="type-specific-fields-heading">
            <div className="card-header" style={{ 
              backgroundColor: '#FFF3CD',
              borderBottom: '2px solid #FFC60B',
              padding: '0.75rem 1rem'
            }}>
              <h2 id="type-specific-fields-heading" style={{ 
                fontSize: 'var(--font-lg)', 
                fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                color: '#2e1719',
                margin: 0,
                fontWeight: 'normal'
              }}>
                {questionType === "singleInput" ? "Step 3: Question config - Configure Input Type" : 
                 questionType === "multipleChoice" ? "Step 3: Question config - Add Choices" :
                 "Step 3: Question config - Add Input Fields"}
              </h2>
            </div>
            <div className="card-body">
        
        {questionType === "multipleChoice" && (
          <>
            <div className="mb-3">
              <label className="form-label semibold-text">Add Choices (one at a time)</label>
              <p className="text-muted" style={{ fontSize: 'var(--font-sm)' }}>
                These are the options users can select from (dropdown for single choice, checkboxes for multiple)
              </p>
              <div className="input-group mb-2">
                <input
                  type="text"
                  className="form-control"
                  value={currentSubQuestion}
                  onChange={(e) => setCurrentSubQuestion(e.target.value)}
                  placeholder="e.g., Yes"
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSubQuestion())}
                />
                <Button onClick={addSubQuestion}>Add Choice</Button>
              </div>
              
              {/* Show added subquestions */}
              {subQuestions.length > 0 && (
                <div className="border rounded p-2" style={{ backgroundColor: "var(--bs-warning-bg-subtle)" }}>
                  <div className="d-flex justify-content-between mb-2">
                    <p className="d-block mb-1">Added choices:</p>
                    <p className="d-block mb-1 text-muted" style={{ fontSize: 'var(--font-xs)', width: "15rem" }}>
                    Edit your choices here, or click the x to remove it.  Don't forget to click the 'update and save question' button when you're done
                    </p>

                  </div>
                  {subQuestions.map((sq, idx) => (
                    <div key={sq.id ?? idx} className="d-flex justify-content-between align-items-end mb-1">
                      <p style={{ fontSize: "1.25rem" }}>{sq.label || sq.Label}</p>
                      <div className="d-flex gap-1">
                      {/* Form input for editing choice label */}
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        value={sq.label || sq.Label}
                        onChange={(e) => {
                          const newLabel = e.target.value;
                          const updatedSubQuestions = [...subQuestions];
                          updatedSubQuestions[idx] = { ...updatedSubQuestions[idx], label: newLabel, Label: newLabel };
                          setSubQuestions(updatedSubQuestions);
                        }}
                        placeholder="Edit choice label"
                      />
                        <button
                          type="button"
                          aria-label={`Remove choice ${idx + 1}`}
                          className="btn btn-sm btn-outline-danger"
                          style={{ fontSize: "3rem", lineHeight: "1rem", paddingBottom: ".5rem" }}
                          onClick={() => removeSubQuestion(idx)}
                        >
                          ×
                        </button>
                       
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="multipleSelectAllowed"
                checked={multipleSelectAllowed}
                onChange={(e) => setMultipleSelectAllowed(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="multipleSelectAllowed">
                Allow multiple selections (checkboxes instead of dropdown)
              </label>
            </div>
            
            {multipleSelectAllowed && (
              <>
                <div className="mb-3">
                  <label className="form-label semibold-text">Maximum Selections Allowed</label>
                  <p className="text-muted" style={{ fontSize: 'var(--font-sm)' }}>
                    Limit how many choices users can select (e.g., "3" for "Select your top 3 reasons"). Leave blank for unlimited.
                  </p>
                  <input
                    type="number"
                    className="form-control"
                    value={multipleSelectLimit}
                    onChange={(e) => setMultipleSelectLimit(e.target.value)}
                    placeholder="e.g., 3"
                    min="1"
                    style={{ maxWidth: '200px' }}
                  />
                </div>
                
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="isOrderedSelection"
                    checked={isOrderedSelection}
                    onChange={(e) => setIsOrderedSelection(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="isOrderedSelection">
                    Order matters (Ranked selection)
                  </label>
                  <p className="text-muted" style={{ fontSize: 'var(--font-xs)', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                    Enable if the order of selection indicates priority/ranking (e.g., "Where are the top 3 places staff come from? 1st, 2nd, 3rd")
                  </p>
                </div>
              </>
            )}
            
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="includeInTitle"
                checked={includeInTitle}
                onChange={(e) => setIncludeInTitle(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="includeInTitle">
                Use selected choices to update the survey title
              </label>
              <p className="text-muted" style={{ fontSize: 'var(--font-xs)', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                When a member selects their answer, it will be added to the survey's display title to help identify it (e.g., "Adults, Children").
              </p>
            </div>

            {/* Column Headings for MultipleChoice tabular display */}
            <div className="mb-3">
              <label className="form-label semibold-text">Column Headings (Optional)</label>
              <p className="text-muted" style={{ fontSize: 'var(--font-sm)' }}>
                Add column headers if displaying choices in a table format. Example: "Option", "Description"
              </p>
              <div className="mb-2">
                <div className="input-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., Training Source"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value) {
                          setColumnHeadings([...columnHeadings, value]);
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <Button 
                    variant="secondary"
                    onClick={(e) => {
                      const input = e.target.parentElement.querySelector('input');
                      const value = input.value.trim();
                      if (value) {
                        setColumnHeadings([...columnHeadings, value]);
                        input.value = '';
                      }
                    }}
                  >
                    Add Heading
                  </Button>
                </div>
                {columnHeadings.length > 0 && (
                  <div className="border rounded p-2" style={{ backgroundColor: '#f8f9fa' }}>
                    <small className="text-muted d-block mb-1">Column headers (in order):</small>
                    {columnHeadings.map((heading, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center mb-1">
                        <span className="survey-builder-column-header">
                          {index + 1}. {heading}
                        </span>
                        <button
                          type="button"
                          aria-label={`Remove column heading ${index + 1}`}
                          className="btn btn-sm btn-outline-danger"
                          style={{ fontSize: 'var(--font-lg)', lineHeight: '1rem', padding: '0 0.5rem' }}
                          onClick={() => setColumnHeadings(columnHeadings.filter((_, i) => i !== index))}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {/* Display Layout Option - Horizontal vs Vertical */}
            <div className="mb-3 form-check">
              <input
                type="checkbox"
                className="form-check-input"
                id="displayAsHorizontalMC"
                checked={displayAsHorizontal}
                onChange={(e) => setDisplayAsHorizontal(e.target.checked)}
              />
              <label className="form-check-label" htmlFor="displayAsHorizontalMC">
                <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Display horizontally</span> (side by side)
              </label>
              <p className="text-muted" style={{ fontSize: 'var(--font-xs)', marginTop: '0.25rem', marginLeft: '1.5rem' }}>
                When checked, displays choices/checkboxes side-by-side in a row. Uncheck for traditional vertical layout (stacked).
              </p>
            </div>
          </>
        )}

        {questionType === "singleInput" && (
          <>
            <div className="mb-3">
              <div className="form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="displayAsInformational"
                  checked={displayAsInformational}
                  onChange={(e) => setDisplayAsInformational(e.target.checked)}
                />
                <label className="form-check-label" htmlFor="displayAsInformational">
                  <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Display as informational text only (no input field)</span>
                </label>
              </div>
              <p className="text-muted ms-4" style={{ fontSize: 'var(--font-sm)', marginTop: '0.25rem' }}>
                Shows the question text and description without any input field. Perfect for explanatory text or definitions.
              </p>
            </div>

            {!displayAsInformational && (
              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="displayAsHorizontal"
                    checked={displayAsHorizontal}
                    onChange={(e) => setDisplayAsHorizontal(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="displayAsHorizontal">
                    <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Display horizontally (table row style)</span>
                  </label>
                </div>
                <p className="text-muted ms-4" style={{ fontSize: 'var(--font-sm)', marginTop: '0.25rem' }}>
                  When checked, displays question text on the left and input field on the right in a table row. Uncheck for traditional vertical layout (question above, input below).
                </p>
              </div>
            )}

            <div className="mb-3">
              <label className="form-label semibold-text">Input Type</label>
              <select
                className="form-select"
                value={inputType}
                onChange={(e) => setInputType(e.target.value)}
              >
                <option value="">-- Select Input Type --</option>
                <option value="text">Text</option>
                <option value="number">Number</option>
                <option value="email">Email</option>
                <option value="boolean">Yes/No (Toggle)</option>
              </select>
            </div>

            {/* Column Headings for single-row table (number only, 2+ columns required) */}
            {inputType === "number" && (
              <div className="mb-3">
                <label className="form-label semibold-text">Column Headings (Optional)</label>
                <p className="text-muted" style={{ fontSize: 'var(--font-sm)' }}>
                  Add column headings to display this as a single-row table with multiple columns. Leave empty for a single input field.
                </p>
                {columnHeadings.length === 1 && (
                  <div className="alert alert-warning py-2 px-3 mb-2" style={{ fontSize: 'var(--font-base)' }}>
                    ⚠️ You must add at least <span style={{ fontFamily: "'Neutraface-Book', Arial, sans-serif" }}>2 column headings</span>, or remove this one. A single column heading causes save/load errors.
                  </div>
                )}
                <div className="d-flex gap-2 mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="Enter column heading (e.g., 'Headcount Adult')"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value) {
                          setColumnHeadings([...columnHeadings, value]);
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <button
                    type="button"
                    className="btn btn-outline-secondary"
                    onClick={(e) => {
                      const input = e.target.previousElementSibling;
                      const value = input.value.trim();
                      if (value) {
                        setColumnHeadings([...columnHeadings, value]);
                        input.value = '';
                      }
                    }}
                  >
                    Add
                  </button>
                </div>
                {columnHeadings.length > 0 && (
                  <div className="mt-2">
                    <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif", fontSize: 'var(--font-sm)' }}>Current columns:</span>
                    <div className="d-flex flex-wrap gap-2 mt-1">
                      {columnHeadings.map((heading, index) => (
                        <span
                          key={index}
                          className="badge"
                          style={{
                            backgroundColor: '#e8d5d5',
                            color: '#2e1719',
                            padding: '0.4rem 0.6rem',
                            fontSize: 'var(--font-sm)',
                            display: 'flex',
                            alignItems: 'center',
                            gap: '0.5rem'
                          }}
                        >
                          {heading}
                          <button
                            type="button"
                            onClick={() => setColumnHeadings(columnHeadings.filter((_, i) => i !== index))}
                            style={{ background: 'none', border: 'none', color: '#dc3545', cursor: 'pointer', padding: '0', fontSize: '1rem', lineHeight: '1' }}
                          >
                            ×
                          </button>
                        </span>
                      ))}
                    </div>
                  </div>
                )}
              </div>
            )}

            {/* Display as row label option for single-row table */}
            {columnHeadings.length > 1 && (
              <div className="mb-3">
                <div className="form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="displayAsRowLabelCheck"
                    checked={displayAsRowLabel}
                    onChange={(e) => setDisplayAsRowLabel(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="displayAsRowLabelCheck">
                    <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Display question as row label in table</span>
                  </label>
                </div>
                <p className="text-muted ms-4" style={{ fontSize: 'var(--font-sm)', marginTop: '0.25rem' }}>
                  When checked, the question text will appear on the left side of the table row instead of above the table.
                </p>
              </div>
            )}
          </>
        )}

        {questionType === "inputList" && (
          <>
            <div className="mb-3">
              <label className="form-label semibold-text">Input Type</label>
              <select
                className="form-select"
                value={inputType}
                onChange={(e) => setInputType(e.target.value)}
              >
                <option value="">-- Select Input Type --</option>
                <option value="number">Number</option>
                <option value="text">Text</option>
                <option value="boolean">Yes/No (Toggle)</option>
              </select>
              {!inputType && (
                <div style={{ 
                  marginTop: '0.5rem', 
                  padding: '0.5rem', 
                  backgroundColor: '#FFE5D4', 
                  border: '1px solid #f16d09',
                  borderRadius: '4px',
                  fontSize: 'var(--font-sm)',
                  color: '#2e1719'
                }}>
                  ⚠️ Please select an input type - this is required!
                </div>
              )}
            </div>
            
            <div className="mb-3">
              <label className="form-label semibold-text">Add Field Labels (one at a time)</label>
              <p className="text-muted" style={{ fontSize: 'var(--font-sm)' }}>
                These are the labels for each input field. Examples: "Under 20", "20-24", "Clinical lead", "Nurse consultant"
              </p>
              <p style={{ 
                fontSize: 'var(--font-xs)', 
                color: '#f16d09', 
                marginBottom: '0.5rem',
                fontFamily: "'Neutraface-Demi', Arial, sans-serif"
              }}>
                💡 Tip: For totals validation, add your "Total" field LAST
              </p>

              <div className="input-group mb-2">
                <input
                  type="text"
                  className="form-control"
                  value={currentSubQuestion}
                  onChange={(e) => setCurrentSubQuestion(e.target.value)}
                  placeholder={inputType === "number" ? "e.g., Under 18" : "e.g., Name"}
                  onKeyDown={(e) => e.key === 'Enter' && (e.preventDefault(), addSubQuestion())}
                />
                <Button onClick={addSubQuestion}>Add Item</Button>
              </div>
              
              {/* Show added subquestions */}
              {subQuestions.length > 0 && (
                <div className="border rounded p-2">
                  <small className="text-muted d-block mb-1">Added items:</small>
                  {subQuestions.map((sq, idx) => (
                    <div key={sq.id ?? idx} className="d-flex justify-content-between align-items-end mb-1">
                      <p style={{ fontSize: "1.25rem" }}>{sq.label || sq.Label}</p>
                      <div className="d-flex gap-1">
                        <input
                          type="text"
                          className="form-control form-control-sm"
                          value={sq.label || sq.Label}
                          onChange={(e) => {
                            const newLabel = e.target.value;
                            const updatedSubQuestions = [...subQuestions];
                            updatedSubQuestions[idx] = { ...updatedSubQuestions[idx], label: newLabel, Label: newLabel };
                            setSubQuestions(updatedSubQuestions);
                          }}
                          placeholder="Edit item label"
                        />
                        <div style={{ width: '0.5rem' }} /> {/* Small gap between input and delete button */}
                        <button
                          type="button"
                          aria-label={`Remove item ${idx + 1}`}
                          className="btn btn-sm btn-outline-danger"
                          style={{ fontSize: "3rem", lineHeight: "1rem", paddingBottom: ".5rem" }}
                          onClick={() => removeSubQuestion(idx)}
                        >
                          ×
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </div>
            
            {/* Layout Direction */}
            <div className="mb-3">
              <label className="form-label semibold-text">Layout Direction</label>
              <p className="text-muted" style={{ fontSize: 'var(--font-sm)' }}>
                Choose how the input fields are displayed
              </p>
              <div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="layoutDirection"
                    id="layoutVertical"
                    value="vertical"
                    checked={layoutDirection === "vertical"}
                    onChange={(e) => setLayoutDirection(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="layoutVertical">
                    <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Vertical</span> - Items stacked in rows (traditional table format)
                  </label>
                </div>
                <div className="form-check">
                  <input
                    className="form-check-input"
                    type="radio"
                    name="layoutDirection"
                    id="layoutHorizontal"
                    value="horizontal"
                    checked={layoutDirection === "horizontal"}
                    onChange={(e) => setLayoutDirection(e.target.value)}
                  />
                  <label className="form-check-label" htmlFor="layoutHorizontal">
                    <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Horizontal</span> - Items displayed side-by-side (compact single row)
                  </label>
                </div>
              </div>
            </div>
            
            {/* Column Headings for multi-column display */}
            <div className="mb-3">
              <label className="form-label semibold-text">Column Headings (Optional)</label>
              <p className="text-muted" style={{ fontSize: 'var(--font-sm)' }}>
                {columnHeadings.length === 0 ? (
                  <>Add column headers to create a multi-column grid. Example: "Headcount", "WTE"</>
                ) : (
                  <><span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif", color: '#f16d09' }}>Multi-column mode:</span> Each row item will have {columnHeadings.length} input fields (one per column)</>
                )}
              </p>
              <div className="mb-2">
                <div className="input-group mb-2">
                  <input
                    type="text"
                    className="form-control"
                    placeholder="e.g., Age Band"
                    onKeyDown={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        const value = e.target.value.trim();
                        if (value) {
                          setColumnHeadings([...columnHeadings, value]);
                          e.target.value = '';
                        }
                      }
                    }}
                  />
                  <Button 
                    variant="secondary"
                    onClick={(e) => {
                      const input = e.target.parentElement.querySelector('input');
                      const value = input.value.trim();
                      if (value) {
                        setColumnHeadings([...columnHeadings, value]);
                        input.value = '';
                      }
                    }}
                  >
                    Add Heading
                  </Button>
                </div>
                {columnHeadings.length > 0 && (
                  <div className="border rounded p-2" style={{ backgroundColor: '#f8f9fa' }}>
                    <small className="text-muted d-block mb-1">Column headers (in order):</small>
                    {columnHeadings.map((heading, index) => (
                      <div key={index} className="d-flex justify-content-between align-items-center mb-1">
                        <span className="survey-builder-column-header">
                          {index + 1}. {heading}
                        </span>
                        <button
                          type="button"
                          aria-label={`Remove column heading ${index + 1}`}
                          className="btn btn-sm btn-outline-danger"
                          style={{ fontSize: '3rem', lineHeight: '2rem', padding: '0 0.5rem' }}
                          onClick={() => setColumnHeadings(columnHeadings.filter((_, i) => i !== index))}
                        >
                          ×
                        </button>
                      </div>
                    ))}
                  </div>
                )}
              </div>
            </div>
            
            {inputType === "number" && (
              <>
                <div className="mb-3 form-check">
                  <input
                    type="checkbox"
                    className="form-check-input"
                    id="showTotal"
                    checked={showTotal}
                    onChange={(e) => setShowTotal(e.target.checked)}
                  />
                  <label className="form-check-label" htmlFor="showTotal">
                    Show calculated total
                  </label>
                </div>
                
                {showTotal && (
                  <>
                    {/* Show Grand Total checkbox only for multi-column questions */}
                    {columnHeadings.length > 0 && (
                      <div className="mb-3 form-check" style={{ marginLeft: '1.5rem' }}>
                        <input
                          type="checkbox"
                          className="form-check-input"
                          id="showGrandTotal"
                          checked={showGrandTotal}
                          onChange={(e) => {
                            const isChecked = e.target.checked;
                            setShowGrandTotal(isChecked);
                            // If unchecking, clear selected columns
                            if (!isChecked) {
                              setGrandTotalColumns([]);
                            } else {
                              // If checking, default to all columns selected
                              setGrandTotalColumns(columnHeadings.map((_, idx) => idx));
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor="showGrandTotal">
                          Show custom total (sum of selected columns)
                        </label>
                        <p className="text-muted" style={{ fontSize: 'var(--font-sm)', marginLeft: '1.5rem', marginTop: '0.25rem' }}>
                          Adds a "Custom Total" field that sums only the selected column totals. Choose which columns to include below.
                        </p>
                      </div>
                    )}
                    
                    {/* Column selector for Grand Total */}
                    {showGrandTotal && columnHeadings.length > 0 && (
                      <div className="mb-3" style={{ marginLeft: '3rem', padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                        <label className="form-label semibold-text" style={{ fontSize: 'var(--font-sm)' }}>
                          Select columns to include in Custom Total:
                        </label>
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', gap: '0.5rem' }}>
                          {columnHeadings.map((heading, idx) => (
                            <div key={idx} className="form-check">
                              <input
                                type="checkbox"
                                className="form-check-input"
                                id={`grandTotalCol-${idx}`}
                                checked={grandTotalColumns.includes(idx)}
                                onChange={(e) => {
                                  if (e.target.checked) {
                                    setGrandTotalColumns([...grandTotalColumns, idx].sort((a, b) => a - b));
                                  } else {
                                    setGrandTotalColumns(grandTotalColumns.filter(i => i !== idx));
                                  }
                                }}
                              />
                              <label className="form-check-label" htmlFor={`grandTotalCol-${idx}`}>
                                {heading}
                              </label>
                            </div>
                          ))}
                        </div>
                        {grandTotalColumns.length === 0 && (
                          <p className="text-danger" style={{ fontSize: 'var(--font-xs)', marginTop: '0.5rem', marginBottom: 0 }}>
                            ⚠️ Please select at least one column for the Grand Total
                          </p>
                        )}
                      </div>
                    )}
                    
                    <div className="mb-3">
                      <label className="form-label semibold-text">Total Field Label (Optional)</label>
                      <p className="text-muted" style={{ fontSize: 'var(--font-sm)' }}>
                        Custom label for the total row. Example: "Medical Total", "Nursing Total"
                      </p>
                      <input
                        type="text"
                        className="form-control"
                        value={totalFieldLabel}
                        onChange={(e) => setTotalFieldLabel(e.target.value)}
                        placeholder="e.g., Medical Total"
                      />
                    </div>
                    
                    <div className="mb-3">
                      <label className="form-label semibold-text">Total Field Note (Optional)</label>
                      <p className="text-muted" style={{ fontSize: 'var(--font-sm)' }}>
                        Add a note/comment to the Total field label. Example: "(should equal total staff count)"
                      </p>
                      <input
                        type="text"
                        className="form-control"
                        value={totalFieldNote}
                        onChange={(e) => setTotalFieldNote(e.target.value)}
                        placeholder="e.g., (should equal clinical headcount)"
                      />
                    </div>
                  </>
                )}
              </>
            )}
          </>
        )}
        
            </div>
          </section>
        )}

        {/* Validation and Conditional sections */}
        <div style={{
          display: 'grid',
          gridTemplateColumns: questionType === "referenceValue" ? '1fr' : '1fr 1fr',
          gap: '1rem',
          marginBottom: '1rem'
        }}>
          
        {/* Validation Rules Section */}
        {questionType !== "referenceValue" && (
        <section className="card" style={{ border: '2px solid #f16d09' }} aria-labelledby="validation-rules-heading">
          <div className="card-header" style={{ 
            backgroundColor: '#ffe5d4',
            borderBottom: '2px solid #f16d09',
            padding: '0.75rem 1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%' }}>
              <h2 id="validation-rules-heading" style={{ 
                fontSize: 'var(--font-lg)', 
                fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                color: '#2e1719',
                margin: 0,
                fontWeight: 'normal'
              }}>
                Step 4: Validation Rules (Optional)
              </h2>
              {constraints.length > 0 && (
                <span style={{
                  backgroundColor: '#f16d09',
                  color: 'white',
                  padding: '0.25rem 0.6rem',
                  borderRadius: '12px',
                  fontSize: 'var(--font-sm)',
                  fontFamily: "'Neutraface-Demi', Arial, sans-serif"
                }}>
                  {constraints.length} {constraints.length === 1 ? 'rule' : 'rules'}
                </span>
              )}
            </div>
          </div>
          <Accordion className="border-0">
            <Accordion.Item eventKey="0" className="border-0">
              <Accordion.Header style={{ backgroundColor: 'transparent' }}>
                <span style={{ fontSize: 'var(--font-base)', color: '#666' }}>
                  Click to {constraints.length > 0 ? 'edit' : 'add'} validation rules
                </span>
              </Accordion.Header>
            <Accordion.Body>
              <p style={{ fontSize: 'var(--font-sm)', color: '#666', marginBottom: '1rem' }}>
                Add validation rules to ensure data quality and consistency
              </p>

              <div className="alert alert-warning d-flex gap-2 align-items-start py-2 px-3 mb-3" style={{ fontSize: 'var(--font-sm)' }}>
                <span style={{ fontSize: '1rem', marginTop: '1px' }}>💡</span>
                <span>
                  <strong>Save first!</strong> Questions only appear in the validation pickers below once the survey has been saved to the database.
                  If you've just added a question and can't see it in a list, save the survey and re-open the question to add the validation.
                </span>
              </div>
              
              {/* Required checkbox */}
              <div className="mb-3 form-check">
                <input
                  type="checkbox"
                  className="form-check-input"
                  id="isRequired"
                  checked={isRequired}
                  onChange={(e) => {
                    setIsRequired(e.target.checked);
                    if (e.target.checked) {
                      if (!constraints.includes("required")) {
                        setConstraints([...constraints, "required"]);
                      }
                    } else {
                      setConstraints(constraints.filter(c => c !== "required"));
                    }
                  }}
                />
                <label className="form-check-label semibold-text" htmlFor="isRequired">
                  Required field
                </label>
              </div>

              {/* Min/Max values for number inputs */}
              {questionType === "singleInput" && inputType === "number" && (
                <div className="card mb-3" style={{ border: '2px solid #6c757d' }}>
                  <div className="card-header" style={{ backgroundColor: '#f8f9fa', borderBottom: '2px solid #6c757d' }}>
                    <span style={{ fontSize: 'var(--font-base)', fontFamily: "'Neutraface-Demi', Arial, sans-serif", color: '#495057' }}>
                      📏 Min / Max Value
                    </span>
                  </div>
                  <div className="card-body">
                  <div className="mb-3">
                    <label className="form-label" style={{ fontSize: 'var(--font-base)' }}>Minimum Value</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        value={minValue}
                        onChange={(e) => setMinValue(e.target.value)}
                        placeholder="e.g., 0"
                      />
                      <Button onClick={() => addConstraint("min")} variant="secondary">
                        Add Min
                      </Button>
                    </div>
                  </div>

                  <div className="mb-3">
                    <label className="form-label" style={{ fontSize: 'var(--font-base)' }}>Maximum Value</label>
                    <div className="input-group">
                      <input
                        type="number"
                        className="form-control"
                        value={maxValue}
                        onChange={(e) => setMaxValue(e.target.value)}
                        placeholder="e.g., 100"
                      />
                      <Button onClick={() => addConstraint("max")} variant="secondary">
                        Add Max
                      </Button>
                    </div>
                  </div>
                  </div>
                </div>
              )}

              {/* Sum equals validation for inputList */}
              {questionType === "inputList" && (
                <>
                  <div className="card mb-3" style={{ border: '2px solid #28a745' }}>
                    <div className="card-header" style={{ backgroundColor: '#d4edda', borderBottom: '2px solid #28a745' }}>
                      <span style={{ fontSize: 'var(--font-base)', fontFamily: "'Neutraface-Demi', Arial, sans-serif", color: '#155724' }}>
                        ➕ Sum Must Equal Another Question's Total
                      </span>
                    </div>
                    <div className="card-body">
                    <p style={{ fontSize: 'var(--font-sm)', color: '#666', marginBottom: '0.5rem' }}>
                      ⭐ Common workforce pattern: This question's total must equal another question's value or total.
                    </p>
                    <p style={{ fontSize: 'var(--font-xs)', color: '#f16d09', marginBottom: '0.5rem', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                      Example: "Clinical workforce age" total must equal "Total clinical staff" (from previous question)
                    </p>
                    
                    {/* Mode toggle */}
                    <div className="mb-3">
                      <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="sumEqualsMode" id="sumEqualsModeQuestion"
                          value="question" checked={sumEqualsMode === "question"} onChange={() => setSumEqualsMode("question")} />
                        <label className="form-check-label" htmlFor="sumEqualsModeQuestion" style={{ fontSize: 'var(--font-sm)' }}>
                          Compare against another question
                        </label>
                      </div>
                      <div className="form-check form-check-inline">
                        <input className="form-check-input" type="radio" name="sumEqualsMode" id="sumEqualsModeFixed"
                          value="fixed" checked={sumEqualsMode === "fixed"} onChange={() => setSumEqualsMode("fixed")} />
                        <label className="form-check-label" htmlFor="sumEqualsModeFixed" style={{ fontSize: 'var(--font-sm)' }}>
                          Compare against a fixed value
                        </label>
                      </div>
                    </div>

                    {/* Progressive disclosure: Only show row-level validation for InputList questions */}
                    {questionType === "inputList" && subQuestions.length > 0 && (
                          <div className="mb-3">
                            <label className="form-label" style={{ fontSize: 'var(--font-sm)', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                              Validation scope:
                            </label>
                            <div className="mb-2">
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="sumEqualsValidationScope"
                                  id="sumEqualsEntire"
                                  value="entire"
                                  checked={sumEqualsValidationScope === "entire"}
                                  onChange={(e) => setSumEqualsValidationScope(e.target.value)}
                                />
                                <label className="form-check-label" htmlFor="sumEqualsEntire" style={{ fontSize: 'var(--font-sm)' }}>
                                  Entire question total (sum of all rows)
                                </label>
                              </div>
                              <div className="form-check">
                                <input
                                  className="form-check-input"
                                  type="radio"
                                  name="sumEqualsValidationScope"
                                  id="sumEqualsItem"
                                  value="item"
                                  checked={sumEqualsValidationScope === "item"}
                                  onChange={(e) => setSumEqualsValidationScope(e.target.value)}
                                />
                                <label className="form-check-label" htmlFor="sumEqualsItem" style={{ fontSize: 'var(--font-sm)' }}>
                                  Specific row/item (validate one row independently)
                                </label>
                              </div>
                            </div>

                            {/* Row selector appears only when "item" scope is selected */}
                            {sumEqualsValidationScope === "item" && (
                              <div className="mb-2">
                                <label className="form-label" style={{ fontSize: 'var(--font-sm)', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                                  Which row to validate:
                                </label>
                                <select
                                  className="form-select mb-2"
                                  value={sumEqualsItemIndex}
                                  onChange={(e) => setSumEqualsItemIndex(e.target.value)}
                                >
                                  {subQuestions.map((sq, idx) => (
                                    <option key={idx} value={idx}>
                                      Row {idx + 1}: {sq.Label || sq.label || `Item ${idx + 1}`}
                                    </option>
                                  ))}
                                </select>
                              </div>
                            )}
                          </div>
                        )}

                    {sumEqualsMode === "question" && (
                        <div className="mb-2">
                          <label className="form-label" style={{ fontSize: 'var(--font-sm)', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                            Select the question containing the number to match against:
                          </label>
                          <select
                            className="form-select mb-2"
                            value={sumEqualsQuestionId}
                            onChange={(e) => {
                              setSumEqualsQuestionId(e.target.value);
                              const selectedQ = getAllPreviousQuestions().find(q => q.id === parseInt(e.target.value));
                              if (selectedQ) {
                                setSumEqualsQuestionType(selectedQ.type);
                                try {
                                  const typeParams = typeof selectedQ.typeParameters === 'string' ? JSON.parse(selectedQ.typeParameters || '{}') : (selectedQ.typeParameters || {});
                                  const layoutData = typeof selectedQ.layout === 'string' ? JSON.parse(selectedQ.layout) : (selectedQ.layout || {});
                                  const isInputList = selectedQ.type === 'inputList' || selectedQ.type === 'InputList';
                                  if (isInputList && typeParams.Questions) {
                                    setSumEqualsQuestionItems(typeParams.Questions);
                                    setSumEqualsHasGrandTotal(layoutData.showGrandTotal || false);
                                  } else {
                                    setSumEqualsQuestionItems([]);
                                    setSumEqualsHasGrandTotal(false);
                                  }
                                } catch (e) {
                                  setSumEqualsQuestionItems([]);
                                  setSumEqualsHasGrandTotal(false);
                                }
                              } else {
                                setSumEqualsQuestionType("");
                              }
                            }}
                          >
                            <option value="">-- Select Question --</option>
                            {getAllPreviousQuestions().map(q => (
                              <option key={q.id} value={q.id}>
                                {q.stepTitle}: {q.text.substring(0, 50)}{q.text.length > 50 ? '...' : ''}
                              </option>
                            ))}
                          </select>

                          {/* Show reference type dropdown if selected question is inputList */}
                          {sumEqualsQuestionId && (sumEqualsQuestionType === 'inputList' || sumEqualsQuestionType === 'InputList') && sumEqualsQuestionItems.length > 0 && (
                            <div className="mb-2">
                              <label className="form-label" style={{ fontSize: 'var(--font-sm)', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                                Match against:
                              </label>
                              <select
                                className="form-select mb-2"
                                value={sumEqualsReferenceType}
                                onChange={(e) => setSumEqualsReferenceType(e.target.value)}
                              >
                                <option value="total">Total (auto-calculated sum of all items)</option>
                                {sumEqualsHasGrandTotal && (
                                  <option value="grandtotal">Custom Total (sum of selected columns only)</option>
                                )}
                                {sumEqualsQuestionItems.map((item, idx) => (
                                  <option key={idx} value={`item-${idx}`}>
                                    Specific item: {typeof item === 'string' ? item : (item.Label || item.label || `Item ${idx + 1}`)}
                                  </option>
                                ))}
                              </select>
                            </div>
                          )}

                          <Button onClick={() => addConstraint("sum_equals")} variant="secondary">
                            Add Sum Validation
                          </Button>
                        </div>
                    )}

                    {sumEqualsMode === "fixed" && (
                      <div className="mb-2">
                        <label className="form-label" style={{ fontSize: 'var(--font-sm)', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                          Enter the fixed value this sum must equal:
                        </label>
                        <div className="input-group">
                          <input
                            type="number"
                            className="form-control"
                            value={sumEqualsFixedValue}
                            onChange={(e) => setSumEqualsFixedValue(e.target.value)}
                            placeholder="e.g., 100"
                          />
                          <Button onClick={() => addConstraint("sum_equals_fixed")} variant="secondary">
                            Add Fixed Sum Validation
                          </Button>
                        </div>
                      </div>
                    )}
                    </div>
                  </div>
                </>
              )}

              {/* Formula Sum Validation - combine multiple source values */}
              {questionType === "inputList" && (
                <div className="card mb-3" style={{ border: '2px solid #6f42c1' }}>
                  <div className="card-header" style={{ backgroundColor: '#ede7f6', borderBottom: '2px solid #6f42c1' }}>
                    <span style={{ fontSize: 'var(--font-base)', fontFamily: "'Neutraface-Demi', Arial, sans-serif", color: '#6f42c1' }}>
                      🔢 Sum Must Equal a Formula of Multiple Sources
                    </span>
                  </div>
                  <div className="card-body">
                  <p style={{ fontSize: 'var(--font-sm)', color: '#666', marginBottom: '0.5rem' }}>
                    Build a formula combining totals or items from multiple questions — the result must equal this question's total.
                  </p>
                  <p style={{ fontSize: 'var(--font-xs)', color: '#f16d09', marginBottom: '0.5rem', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                    Example: "Clinical headcount" total must equal (Total from Q3) + (Item 2 from Q5)
                  </p>

                  {!cfFormula.building ? (
                    <Button onClick={() => cfFormula.setBuilding(true)} variant="info" size="sm">
                      + Build Formula Validation
                    </Button>
                  ) : (
                    <>
                      {/* Source picker panel */}
                      <div className="mb-3 p-3" style={{ backgroundColor: '#fff3e0', borderRadius: '4px', border: '1px solid #f16d09' }}>
                        <label className="form-label " style={{ fontSize: 'var(--font-sm)' }}>Step 1 — Register your sources (add as many as needed):</label>
                        <select
                          className="form-select mb-2"
                          value={cfFormula.sourceQuestionId}
                          onChange={(e) => {
                            cfFormula.setSourceQuestionId(e.target.value);
                            const selectedQ = getAllPreviousQuestions().find(q => q.id === parseInt(e.target.value));
                            if (selectedQ) {
                              // Store question type
                              cfFormula.setSourceQuestionType(selectedQ.type);
                              try {
                                const tp = typeof selectedQ.typeParameters === 'string' ? JSON.parse(selectedQ.typeParameters || '{}') : (selectedQ.typeParameters || {});
                                const ld = typeof selectedQ.layout === 'string' ? JSON.parse(selectedQ.layout) : (selectedQ.layout || {});
                                const isInputList = selectedQ.type === 'inputList' || selectedQ.type === 'InputList';
                                if (isInputList && tp.Questions) {
                                  cfFormula.setSourceItems(tp.Questions);
                                  cfFormula.setSourceHasGrandTotal(ld.showGrandTotal || false);
                                } else {
                                  cfFormula.setSourceItems([]);
                                  cfFormula.setSourceHasGrandTotal(false);
                                }
                                cfFormula.setSourceReferenceType("total");
                              } catch { cfFormula.setSourceItems([]); cfFormula.setSourceHasGrandTotal(false); }
                            } else {
                              cfFormula.setSourceQuestionType("");
                            }
                          }}
                        >
                          <option value="">-- Select Question --</option>
                          {getAllPreviousQuestions().map(q => (
                            <option key={q.id} value={q.id}>
                              {q.stepTitle}: {q.text.substring(0, 50)}{q.text.length > 50 ? '...' : ''}
                            </option>
                          ))}
                        </select>

                        {cfFormula.sourceQuestionId && (cfFormula.sourceQuestionType === 'inputList' || cfFormula.sourceQuestionType === 'InputList') && cfFormula.sourceItems.length > 0 && (
                          <select
                            className="form-select mb-2"
                            value={cfFormula.sourceReferenceType}
                            onChange={(e) => cfFormula.setSourceReferenceType(e.target.value)}
                          >
                            <option value="total">Total (sum of all items)</option>
                            {cfFormula.sourceHasGrandTotal && <option value="grandtotal">Custom Total</option>}
                            {cfFormula.sourceItems.map((item, idx) => (
                              <option key={idx} value={`item-${idx}`}>
                                Item {idx + 1}: {typeof item === 'string' ? item : (item.Label || item.label || `Item ${idx + 1}`)}
                              </option>
                            ))}
                          </select>
                        )}

                        <Button
                          onClick={addCfSource}
                          variant="secondary"
                          size="sm"
                        >
                          + Register Source
                        </Button>
                      </div>

                      {/* FormulaBuilder — shown once at least one source added */}
                      {cfFormula.sources.length > 0 && (
                   // Constraint Formula
                      <FormulaBuilder
                        getDisplayText={getCfFormulaDisplay}
                        elements={cfFormula.sources}
                        getElementLabel={(source, index) => getSourceLabel(source, index, `Q${source.questionId}`)}
                        elementSectionLabel="Step 2 — Build formula..."
                        onAddElement={(_, idx) => cfFormula.setFormulaParts(prev => [...prev, { type: 'source', sourceIndex: idx }])}
                        operators="arithmeticWithComparison"
                        onAddOperator={addCfOperator}
                        onClear={clearCf}
                        onCancel={() => { cfFormula.setBuilding(false); cfFormula.setFormulaParts([]); cfFormula.setSourceQuestionId(""); cfFormula.setSourceItems([]); }}
                        onConfirm={() => addConstraint("sum_formula_equals")}
                        confirmLabel="✓ Add Formula Validation"
                      />
                                            )}
                    </>
                  )}
                  </div>
                </div>
              )}

              {/* Answer Must Equal Another Question - for singleInput cross-question validation */}
              {questionType === "singleInput" && (
                <div className="card mb-3" style={{ border: '2px solid #20c997' }}>
                  <div className="card-header" style={{ backgroundColor: '#e8faf4', borderBottom: '2px solid #20c997' }}>
                    <span style={{ fontSize: 'var(--font-base)', fontFamily: "'Neutraface-Demi', Arial, sans-serif", color: '#0d6e4f' }}>
                      🔗 Answer Must Equal Another Question
                    </span>
                  </div>
                  <div className="card-body">
                  <p style={{ fontSize: 'var(--font-sm)', color: '#666', marginBottom: '0.5rem' }}>
                    ⭐ Workforce validation pattern: Ensures this answer matches a value from an earlier question.
                  </p>
                  <p style={{ fontSize: 'var(--font-xs)', color: '#f16d09', marginBottom: '0.5rem', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                    Example: "Medical staff headcount 2025" must equal "Medical establishment total" from earlier step
                  </p>
                  <div className="mb-2">
                    <label className="form-label" style={{ fontSize: 'var(--font-sm)', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                      Select the question to match against:
                    </label>
                    <select
                      className="form-select mb-2"
                      value={equalsQuestionId}
                      onChange={(e) => {
                        setEqualsQuestionId(e.target.value);
                        // Check if selected question is inputList to show reference type dropdown
                        const selectedQ = getAllPreviousQuestions().find(q => q.id === parseInt(e.target.value));
                        if (selectedQ) {
                          // Store question type
                          setEqualsQuestionType(selectedQ.type);
                          // Parse typeParameters to check if inputList
                          try {
                            const typeParams = typeof selectedQ.typeParameters === 'string' ? JSON.parse(selectedQ.typeParameters || '{}') : (selectedQ.typeParameters || {});
                            const isInputList = selectedQ.type === 'inputList' || selectedQ.type === 'InputList';
                            if (isInputList && typeParams.Questions) {
                              // Store items for reference type selection
                              setEqualsQuestionItems(typeParams.Questions);
                            } else {
                              setEqualsQuestionItems([]);
                            }
                          } catch (_e) {
                            setEqualsQuestionItems([]);
                          }
                        } else {
                          setEqualsQuestionType("");
                        }
                      }}
                    >
                      <option value="">-- Select Question --</option>
                      {getAllPreviousQuestions().map(q => (
                        <option key={q.id} value={q.id}>
                          {q.stepTitle}: {q.text.substring(0, 50)}{q.text.length > 50 ? '...' : ''}
                        </option>
                      ))}
                    </select>
                    
                    {/* Show reference type dropdown if selected question is inputList */}
                    {equalsQuestionId && (equalsQuestionType === 'inputList' || equalsQuestionType === 'InputList') && equalsQuestionItems.length > 0 && (
                      <div className="mb-2">
                        <label className="form-label" style={{ fontSize: 'var(--font-sm)', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                          Match against:
                        </label>
                        <select
                          className="form-select mb-2"
                          value={equalsQuestionReferenceType}
                          onChange={(e) => setEqualsQuestionReferenceType(e.target.value)}
                        >
                          <option value="total">Total (auto-calculated sum of all items)</option>
                          {equalsQuestionItems.map((item, idx) => (
                            <option key={idx} value={`item-${idx}`}>
                              Specific item: {typeof item === 'string' ? item : (item.Label || item.label || `Item ${idx + 1}`)}
                            </option>
                          ))}
                        </select>
                      </div>
                    )}
                    
                    <Button onClick={() => addConstraint("equals_question")} variant="secondary">
                      Add Match Validation
                    </Button>
                  </div>
                  </div>
                </div>
              )}

              {/* Answer Must Match Formula - for singleInput formula validation with comparison operators */}
              {questionType === "singleInput" && (
                <div className="card mb-3" style={{ border: '2px solid #fd7e14' }}>
                  <div className="card-header" style={{ backgroundColor: '#fff3e0', borderBottom: '2px solid #fd7e14' }}>
                    <span style={{ fontSize: 'var(--font-base)', fontFamily: "'Neutraface-Demi', Arial, sans-serif", color: '#7b3d00' }}>
                      🧮 Answer Must Match Formula with Multiple Sources
                    </span>
                  </div>
                  <div className="card-body">
                  <p style={{ fontSize: 'var(--font-sm)', color: '#666', marginBottom: '0.5rem' }}>
                    Build a formula combining values from multiple questions — this answer must satisfy the comparison.
                  </p>
                  <p style={{ fontSize: 'var(--font-xs)', color: '#f16d09', marginBottom: '0.5rem', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                    Example: "Medical headcount 2025" must be &gt;= (Total from Q3) + (Item 2 from Q5)
                  </p>

                  {!sifFormula.building ? (
                    <Button onClick={() => sifFormula.setBuilding(true)} variant="info" size="sm">
                      + Build Formula Validation
                    </Button>
                  ) : (
                    <>
                      {/* Source picker panel */}
                      <div className="mb-3 p-3" style={{ backgroundColor: '#fff3e0', borderRadius: '4px', border: '1px solid #f16d09' }}>
                        <label className="form-label " style={{ fontSize: 'var(--font-sm)' }}>Step 1 — Register your sources (add as many as needed):</label>
                        <select
                          className="form-select mb-2"
                          value={sifFormula.sourceQuestionId}
                          onChange={(e) => {
                            sifFormula.setSourceQuestionId(e.target.value);
                            const selectedQ = getAllPreviousQuestions().find(q => q.id === parseInt(e.target.value));
                            if (selectedQ) {
                              sifFormula.setSourceQuestionType(selectedQ.type);
                              try {
                                const tp = typeof selectedQ.typeParameters === 'string' ? JSON.parse(selectedQ.typeParameters || '{}') : (selectedQ.typeParameters || {});
                                const ld = typeof selectedQ.layout === 'string' ? JSON.parse(selectedQ.layout) : (selectedQ.layout || {});
                                const isInputList = selectedQ.type === 'inputList' || selectedQ.type === 'InputList';
                                if (isInputList && tp.Questions) {
                                  sifFormula.setSourceItems(tp.Questions);
                                  sifFormula.setSourceHasGrandTotal(ld.showGrandTotal || false);
                                } else {
                                  sifFormula.setSourceItems([]);
                                  sifFormula.setSourceHasGrandTotal(false);
                                }
                                sifFormula.setSourceReferenceType("total");
                              } catch { sifFormula.setSourceItems([]); sifFormula.setSourceHasGrandTotal(false); }
                            } else {
                              sifFormula.setSourceQuestionType("");
                            }
                          }}
                        >
                          <option value="">-- Select Question --</option>
                          {getAllPreviousQuestions().map(q => (
                            <option key={q.id} value={q.id}>
                              {q.stepTitle}: {q.text.substring(0, 50)}{q.text.length > 50 ? '...' : ''}
                            </option>
                          ))}
                        </select>

                        {sifFormula.sourceQuestionId && (sifFormula.sourceQuestionType === 'inputList' || sifFormula.sourceQuestionType === 'InputList') && sifFormula.sourceItems.length > 0 && (
                          <select
                            className="form-select mb-2"
                            value={sifFormula.sourceReferenceType}
                            onChange={(e) => sifFormula.setSourceReferenceType(e.target.value)}
                          >
                            <option value="total">Total (sum of all items)</option>
                            {sifFormula.sourceHasGrandTotal && <option value="grandtotal">Custom Total</option>}
                            {sifFormula.sourceItems.map((item, idx) => (
                              <option key={idx} value={`item-${idx}`}>
                                Item {idx + 1}: {typeof item === 'string' ? item : (item.Label || item.label || `Item ${idx + 1}`)}
                              </option>
                            ))}
                          </select>
                        )}

                        <Button
                          onClick={addSifSource}
                          variant="secondary"
                          size="sm"
                        >
                          + Register Source
                        </Button>
                      </div>

                      {/* FormulaBuilder — shown once at least one source added */}
                      {sifFormula.sources.length > 0 && (
                        <FormulaBuilder
                          getDisplayText={getSifFormulaDisplay}
                          elements={sifFormula.sources}
                          getElementLabel={(source, index) => getSourceLabel(source, index, `Q${source.questionId}`)}
                          elementSectionLabel="Step 2 — Build formula..."
                          onAddElement={(_, idx) => sifFormula.setFormulaParts(prev => [...prev, { type: 'source', sourceIndex: idx }])}
                          operators="arithmeticWithComparison"
                          onAddOperator={addSifOperator}
                          onClear={clearSif}
                          onCancel={() => { sifFormula.setBuilding(false); sifFormula.setFormulaParts([]); sifFormula.setSourceQuestionId(""); sifFormula.setSourceItems([]); }}
                          onConfirm={() => addConstraint("formula_equals")}
                          confirmLabel="✓ Add Formula Validation"
                        />
                      )}
                    </>
                  )}
                  </div>
                </div>
              )}

              {/* Column Formula Validation - Only show for multi-column inputList with number type */}
              {(questionType === "inputList" || questionType === "singleInput") && columnHeadings.length > 0 && inputType === "number" && (
                <div className="card mb-3" style={{ border: '2px solid #17a2b8' }}>
                  <div className="card-header" style={{ backgroundColor: '#e7f5f8' }}>
                    <span style={{ 
                      fontSize: 'var(--font-base)', 
                      fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                      color: '#17a2b8'
                    }}>
                      📊 Column Formula Validation
                    </span>
                  </div>
                  <div className="card-body">
                    <p className="text-muted mb-3" style={{ fontSize: 'var(--font-sm)' }}>
                      Build a mathematical formula to validate relationships between columns. Perfect for "Staff2024 + Joined - Left = Staff2025" type validations.
                    </p>
                    
                    {!buildingFormula ? (
                      <Button 
                        onClick={() => setBuildingFormula(true)} 
                        variant="info"
                        size="sm"
                      >
                        + Build Column Formula
                      </Button>
                    ) : (
                      <FormulaBuilder
                        getDisplayText={getFormulaDisplay}
                        elements={subQuestions.flatMap((sq, rowIdx) =>
                          columnHeadings.map((col, colIdx) => ({
                            label: `${sq.label || sq.Label || `Row ${rowIdx + 1}`} — ${col}`,
                            flatIndex: rowIdx * columnHeadings.length + colIdx
                          }))
                        )}
                        getElementLabel={(cell) => cell.label}
                        elementSectionLabel="Add Column:"
                        onAddElement={(cell) => addFormulaColumn(cell.flatIndex)}
                        operators="arithmeticWithComparison"
                        onAddOperator={addFormulaOperator}
                        onClear={clearFormula}
                        onCancel={() => { setBuildingFormula(false); setFormulaParts([]); }}
                        onConfirm={() => addConstraint("column_formula")}
                        confirmLabel="✓ Confirm Finished Formula"
                      />
                    )}
                  </div>
                </div>
              )}

              {/* Display added constraints */}
              {constraints.length > 0 && (
                <div className="mt-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <p style={{ 
                    fontSize: 'var(--font-base)', 
                    fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                    marginBottom: '0.75rem'
                  }}>
                    Active Validation Rules:
                  </p>
                  {constraints.map((constraint, index) => (
                    <div 
                      key={index} 
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '0.5rem',
                        marginBottom: '0.5rem',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <span style={{ fontSize: 'var(--font-base)' }}>
                        {parseConstraint(constraint, columnHeadings, subQuestions.map(sq => sq.Label || sq.label || ''))}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove validation rule ${index + 1}`}
                        className="btn btn-sm"
                        style={{ 
                          color: '#dc3d17',
                          fontSize: 'var(--font-xl)',
                          lineHeight: '1rem',
                          padding: '0 0.5rem'
                        }}
                        onClick={() => removeConstraint(constraint)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        </section>
        )}

        {/* Conditional Logic Section */}
        <section className="card" style={{ border: '2px solid #FFC60B' }} aria-labelledby="conditional-logic-heading">
          <div className="card-header" style={{ 
            backgroundColor: '#FFF3CD',
            borderBottom: '2px solid #FFC60B',
            padding: '0.75rem 1rem'
          }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: '0.75rem', width: '100%' }}>
              <h2 id="conditional-logic-heading" style={{ 
                fontSize: 'var(--font-lg)', 
                fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                color: '#2e1719',
                margin: 0,
                fontWeight: 'normal'
              }}>
                {questionType === "referenceValue" ? "Step 4: Conditional Logic (Optional)" : "Step 5: Conditional Logic (Optional)"}
              </h2>
              {conditions.length > 0 && (
                <span style={{
                  backgroundColor: '#FFC60B',
                  color: '#2e1719',
                  padding: '0.25rem 0.6rem',
                  borderRadius: '12px',
                  fontSize: 'var(--font-sm)',
                  fontFamily: "'Neutraface-Demi', Arial, sans-serif"
                }}>
                  {conditions.length} {conditions.length === 1 ? 'condition' : 'conditions'}
                </span>
              )}
            </div>
          </div>
          <Accordion className="border-0">
            <Accordion.Item eventKey="0" className="border-0">
              <Accordion.Header style={{ backgroundColor: 'transparent' }}>
                <span style={{ fontSize: 'var(--font-base)', color: '#666' }}>
                  Click to {conditions.length > 0 ? 'edit' : 'add'} conditional logic
                </span>
              </Accordion.Header>
            <Accordion.Body>
              <p style={{ fontSize: 'var(--font-sm)', color: '#666', marginBottom: '1rem' }}>
                Show or hide this question based on answers to previous questions
              </p>

              <div className="mb-3">
                <label className="form-label" style={{ fontSize: 'var(--font-base)' }}>
                  Show this question if...
                </label>
                
                <select
                  className="form-select mb-2"
                  value={conditionQuestionId}
                  onChange={(e) => {
                    const selectedId = e.target.value;
                    setConditionQuestionId(selectedId);
                    
                    if (selectedId) {
                      // Get question details to determine type and items
                      const selectedQ = getAllPreviousQuestions().find(q => q.id === parseInt(selectedId));
                      if (selectedQ) {
                        // Parse items/choices if InputList or MultipleChoice
                        try {
                          const typeParams = typeof selectedQ.typeParameters === 'string'
                            ? JSON.parse(selectedQ.typeParameters)
                            : selectedQ.typeParameters || {};
                          
                          if (selectedQ.type === 'inputList' || selectedQ.type === 'InputList') {
                            setConditionQuestionItems(typeParams.Questions || typeParams.questions || []);
                            setConditionReferenceMode("direct"); // Default to direct for InputList
                          } else if (selectedQ.type === 'multipleChoice' || selectedQ.type === 'MultipleChoice') {
                            setConditionQuestionItems(typeParams.Choices || typeParams.choices || typeParams.Questions || typeParams.questions || []);
                            setConditionReferenceMode("direct"); // Default to direct
                          } else {
                            setConditionQuestionItems([]);
                            setConditionReferenceMode("direct");
                          }
                        } catch (e) {
                          setConditionQuestionItems([]);
                        }
                      }
                    } else {
                      setConditionQuestionItems([]);
                      setConditionReferenceMode("direct");
                    }
                    
                    // Reset dependent fields
                    setConditionItemIndex("");
                    setConditionSumIndices([]);
                  }}
                >
                  <option value="">-- Select Question --</option>
                  {getAllPreviousQuestions().map(q => (
                    <option key={q.id} value={q.id}>
                      {q.stepTitle}: {q.text.substring(0, 50)}{q.text.length > 50 ? '...' : ''}
                    </option>
                  ))}
                </select>

                {/* Show "Use as:" dropdown for InputList and MultipleChoice */}
                {conditionQuestionId && conditionQuestionItems.length > 0 && (
                  <div className="mb-2">
                    <label className="form-label" style={{ fontSize: 'var(--font-sm)' }}>
                      Use as:
                    </label>
                    <select
                      className="form-select mb-2"
                      value={conditionReferenceMode}
                      onChange={(e) => {
                        setConditionReferenceMode(e.target.value);
                        setConditionItemIndex("");
                        setConditionSumIndices([]);
                      }}
                    >
                      <option value="direct">Direct value (whole question)</option>
                      <option value="item">Specific item/choice</option>
                      <option value="sum">Sum of selected items/choices</option>
                    </select>
                  </div>
                )}

                {/* Show item selector for "item" mode */}
                {conditionReferenceMode === "item" && conditionQuestionItems.length > 0 && (
                  <div className="mb-2">
                    <label className="form-label" style={{ fontSize: 'var(--font-sm)' }}>
                      Select item/choice:
                    </label>
                    <select
                      className="form-select mb-2"
                      value={conditionItemIndex}
                      onChange={(e) => setConditionItemIndex(e.target.value)}
                    >
                      <option value="">-- Select Item --</option>
                      {conditionQuestionItems.map((item, idx) => (
                        <option key={idx} value={idx}>
                          {typeof item === 'string' ? item : (item.Label || item.label || `Item ${idx + 1}`)}
                        </option>
                      ))}
                    </select>
                    {/* Helper note for MultipleChoice conditionals */}
                    {(() => {
                      const conditionalQuestionType = getAllPreviousQuestions().find(q => q.id === parseInt(conditionQuestionId))?.type;
                      return conditionalQuestionType === "multipleChoice" || conditionalQuestionType === "MultipleChoice";
                    })() && (
                      <div className="alert alert-info py-2 px-3 mb-2" style={{ fontSize: 'var(--font-sm)' }}>
                        <strong>💡 Tip:</strong> For MultipleChoice items, use <strong>"equals toggle Yes"</strong> to check if a choice is ticked.
                      </div>
                    )}
                  </div>
                )}

                {/* Show checkboxes for "sum" mode */}
                {conditionReferenceMode === "sum" && conditionQuestionItems.length > 0 && (
                  <div className="mb-2 p-2" style={{ backgroundColor: '#f8f9fa', borderRadius: '4px', border: '1px solid #dee2e6' }}>
                    <label className="form-label" style={{ fontSize: 'var(--font-sm)', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                      Select items/choices to sum:
                    </label>
                    {(() => {
                      return null;
                    })()}
                    {conditionQuestionItems.map((item, idx) => (
                      <div key={idx} className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          id={`condition-sum-${idx}`}
                          checked={conditionSumIndices.includes(idx)}
                          onChange={(e) => {
                            if (e.target.checked) {
                              setConditionSumIndices([...conditionSumIndices, idx]);
                            } else {
                              setConditionSumIndices(conditionSumIndices.filter(i => i !== idx));
                            }
                          }}
                        />
                        <label className="form-check-label" htmlFor={`condition-sum-${idx}`} style={{ fontSize: 'var(--font-sm)' }}>
                          {typeof item === 'string' ? item : (item.Label || item.label || `Item ${idx + 1}`)}
                        </label>
                      </div>
                    ))}
                  </div>
                )}

                <select
                  className="form-select mb-2"
                  value={conditionOperator}
                  onChange={(e) => setConditionOperator(e.target.value)}
                >
                  <option value="equals">equals</option>
                  <option value="not_equals">does not equal</option>
                  <option value="greater_than">is greater than</option>
                  <option value="less_than">is less than</option>
                </select>

                <div className="mb-2">
                  <label className="form-label" style={{ fontSize: 'var(--font-sm)', marginBottom: '0.25rem' }}>
                    Value Type:
                  </label>
                  <select
                    className="form-select mb-2"
                    value={conditionValueType}
                    onChange={(e) => {
                      setConditionValueType(e.target.value);
                      // Clear value when changing type
                      setConditionValue("");
                    }}
                  >
                    <option value="" disabled>-- Select Type --</option>
                    <option value="text">Text/String</option>
                    <option value="number">Number</option>
                    <option value="boolean">Yes/No (Toggle)</option>
                  </select>
                </div>

                {conditionValueType === "boolean" ? (
                  <select
                    className="form-select mb-2"
                    value={conditionValue}
                    onChange={(e) => setConditionValue(e.target.value)}
                  >
                    <option value="">-- Select Value --</option>
                    <option value="true">Yes / True</option>
                    <option value="false">No / False</option>
                  </select>
                ) : (
                  <input
                    type={conditionValueType === "number" ? "number" : "text"}
                    className="form-control mb-2"
                    value={conditionValue}
                    onChange={(e) => setConditionValue(e.target.value)}
                    placeholder={conditionValueType === "number" ? "Enter number" : "Enter value"}
                  />
                )}

                <Button onClick={addCondition} variant="secondary">
                  Add Condition
                </Button>
              </div>

              {/* Display added conditions */}
              {conditions.length > 0 && (
                <div className="mt-3 p-3" style={{ backgroundColor: '#f8f9fa', borderRadius: '8px' }}>
                  <p style={{ 
                    fontSize: 'var(--font-base)', 
                    fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                    marginBottom: '0.75rem'
                  }}>
                    Active Conditions:
                  </p>
                  {conditions.map((condition, index) => (
                    <div 
                      key={index} 
                      style={{ 
                        display: 'flex', 
                        justifyContent: 'space-between', 
                        alignItems: 'center',
                        padding: '0.5rem',
                        marginBottom: '0.5rem',
                        backgroundColor: 'white',
                        borderRadius: '4px',
                        border: '1px solid #e0e0e0'
                      }}
                    >
                      <span style={{ fontSize: 'var(--font-base)' }}>
                        {parseCondition(condition)}
                      </span>
                      <button
                        type="button"
                        aria-label={`Remove condition ${index + 1}`}
                        className="btn btn-sm"
                        style={{ 
                          color: '#dc3d17',
                          fontSize: 'var(--font-xl)',
                          lineHeight: '1rem',
                          padding: '0 0.5rem'
                        }}
                        onClick={() => removeCondition(condition)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </Accordion.Body>
          </Accordion.Item>
        </Accordion>
        </section>
        
        </div>
        {/* End two-column layout */}

        {/* Live Preview Section */}
        {questionText && (
          <section className="card mb-4" style={{ border: '2px solid #492c28' }} aria-labelledby="live-preview-heading">
            <div className="card-header" style={{ 
              backgroundColor: '#e8d5d5',
              borderBottom: '2px solid #492c28',
              padding: '0.75rem 1rem'
            }}>
              <h2 id="live-preview-heading" style={{ 
                fontSize: 'var(--font-lg)', 
                fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                color: '#2e1719',
                margin: 0,
                fontWeight: 'normal'
              }}>
                Live Preview
              </h2>
            </div>
            <div className="card-body">
              <p style={{ fontSize: 'var(--font-sm)', color: '#666', marginBottom: '1rem' }}>
                This is how members will see this question
              </p>
              
              <SurveyQuestionPreview
                questionText={questionText}
                questionDescription={questionDescription}
                isRequired={isRequired}
                questionType={questionType}
                inputType={inputType}
                subQuestions={subQuestions}
                multipleSelectAllowed={multipleSelectAllowed}
                isOrderedSelection={isOrderedSelection}
                multipleSelectLimit={multipleSelectLimit}
                constraints={constraints}
                conditions={conditions}
                sectionHeading={sectionHeading}
                columnHeadings={columnHeadings}
                displayAsRowLabel={displayAsRowLabel}
                displayAsHeading={displayAsHeading}
                displayAsInformational={displayAsInformational}
                displayAsHorizontal={displayAsHorizontal}
                showTotal={showTotal}
                showGrandTotal={showGrandTotal}
                grandTotalColumns={grandTotalColumns}
                totalFieldLabel={totalFieldLabel}
                totalFieldNote={totalFieldNote}
                referenceSources={refFormula.sources}
                referenceDisplayMode={referenceDisplayMode}
                referenceRowLabel={referenceRowLabel}
                referenceColumns={referenceColumns.map((column, index) => ({
                  Heading: column.Heading || `Column ${index + 1}`,
                  ReferenceSources: column.ReferenceSources || [],
                  ReferenceFormula: buildReferenceFormulaString(column.ReferenceFormulaParts || [])
                }))}
                referenceSourceNames={refFormula.sources.map((source, idx) => {
                  // Get question name for each source
                  // Handle both PascalCase (from backend) and camelCase (from frontend) property names
                  const questionId = source.QuestionId || source.questionId;
                  const referenceType = source.ReferenceType || source.referenceType;
                  
                  let globalNumber = 0;
                  for (const step of steps) {
                    for (const q of step.questions || []) {
                      globalNumber++;
                      if (q.id === parseInt(questionId)) {
                        const typeLabel = referenceType === 'grandtotal' ? ' (Custom Total)'
                          : referenceType === 'total' ? ' (Total)'
                          : referenceType?.startsWith('item-') ? ` (Item ${parseInt(referenceType.split('-')[1]) + 1})`
                          : '';
                        return `Q${globalNumber}: ${q.text.substring(0, 30)}${q.text.length > 30 ? '...' : ''}${typeLabel}`;
                      }
                    }
                  }
                  return `Source ${idx + 1}`;
                })}
                referenceFormula={refFormula.sources.length > 0 && refFormula.formulaParts.length > 0 ? refFormula.formulaParts.map(part => part.value).join('') : ''}
                parseConstraint={parseConstraint}
                parseCondition={parseCondition}
              />
            </div>
          </section>
        )}

          <div style={{ padding: '1rem', backgroundColor: '#f8f9fa', borderRadius: '8px', marginTop: '1rem' }}>
            {/* Helpful note about buttons */}
            <div style={{ 
              fontSize: 'var(--font-sm)', 
              color: '#666', 
              marginBottom: '0.75rem',
              padding: '0.5rem',
              backgroundColor: '#fff',
              border: '1px solid #dee2e6',
              borderRadius: '4px'
            }}>
              💡 <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Note:</span> "Clear Question Form" removes all data from the form but does NOT delete saved questions. Questions are only saved when you click "{editingQuestionId ? 'Update' : 'Add'} And Save Question".
            </div>
            
            {/* Button row with flex gap for spacing */}
            <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
              <Button 
                onClick={() => addQuestion()}
                disabled={isAddingQuestion}
              >
                {isAddingQuestion 
                  ? "Saving..." 
                  : (editingQuestionId ? "Update And Save Question" : "Add And Save Question")
                }
              </Button>
              
              <Button 
                variant="warning" 
                onClick={clearQuestionForm}
              >
                Clear Question Form
              </Button>
              
              {editingQuestionId && (
                <Button 
                  variant="secondary" 
                  onClick={clearQuestionForm}
                >
                  Cancel Edit
                </Button>
              )}
            </div>
          </div>
        </>
    )}

      {/* Questions List */}
      {steps.length > 0 && (
         <section className="card mb-4" style={{ border: '2px solid #492c28' }} aria-labelledby="survey-check-edit-heading">
            <div className="card-header" style={{ 
              backgroundColor: '#e8d5d5',
              borderBottom: '2px solid #492c28',
              padding: '0.75rem 1rem'
            }}>
              <h2 id="survey-check-edit-heading" style={{ 
                fontSize: 'var(--font-lg)', 
                fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                color: '#2e1719',
                margin: 0,
                fontWeight: 'normal'
              }}>
              Check and Edit your survey here before publishing it              </h2>
            </div>
          <p style={{ margin: '0.5rem 1rem 1rem 1rem' }}>To edit a question or move it to another step, use the edit button (change the step name and click 'update question' to move). Use the arrow buttons to reorder steps or questions. You can delete steps (which will also delete their questions) or move the last question out of a step (the empty step will be automatically removed).</p>
          <p style={{color: "red", margin: '0.5rem 1rem 1rem 1rem'}}>NOTE: If you edit questions with validations or conditions please check that the question numbers (validations) and names (conditionals) are correct.  If they show as '?' instead of a question number in the 'badge' (yellow or orange background) then you need to recalculate the formula again to ensure it is correct. If your question has a red border and no number then you need to reenter the validations and/or conditional formulas.  
          
          </p>
          {steps.map((step, stepIndex) => (
            <div key={stepIndex} className="mb-3 p-3 border rounded">
              <div className="d-flex justify-content-between align-items-start mb-2">
                <div className="flex-grow-1">
                  <div className="d-flex align-items-center gap-2 mb-1">
                    <h4 className="mb-0" style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>{`Step name:  ${step.title}`}</h4>
                    <div className="d-flex gap-1">
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => moveStep(stepIndex, 'up')}
                      disabled={stepIndex === 0}
                      title="Move step up"
                      aria-label={`Move step ${stepIndex + 1} up`}
                      style={{ 
                        opacity: stepIndex === 0 ? 0.5 : 1,
                        cursor: stepIndex === 0 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ↑
                    </button>
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-secondary"
                      onClick={() => moveStep(stepIndex, 'down')}
                      disabled={stepIndex === steps.length - 1}
                      title="Move step down"
                      aria-label={`Move step ${stepIndex + 1} down`}
                      style={{ 
                        opacity: stepIndex === steps.length - 1 ? 0.5 : 1,
                        cursor: stepIndex === steps.length - 1 ? 'not-allowed' : 'pointer'
                      }}
                    >
                      ↓
                    </button>
                  </div>
                  </div>
                  {step.description && step.description.trim() && (
                    <div className="text-muted" style={{ fontSize: '0.9rem', marginTop: '0.25rem', paddingLeft: '0.5rem' }}>
                      <span style={{ fontWeight: 'bold' }}>Description:</span> {step.description}
                    </div>
                  )}
                  {(!step.description || !step.description.trim()) && (
                    <div className="text-muted fst-italic" style={{ fontSize: '0.85rem', marginTop: '0.25rem', paddingLeft: '0.5rem' }}>
                      No description set
                    </div>
                  )}
                </div>
                <div className="d-flex gap-2" style={{ marginLeft: '1rem' }}>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={async () => {
                      const newName = prompt("Enter new name for this step:", step.title);
                      if (newName && newName.trim() && newName !== step.title) {
                        // Check if this name already exists in another step
                        const duplicateStep = steps.find((s, idx) => 
                          idx !== stepIndex && 
                          (s.title === newName.trim() || s.name === newName.trim())
                        );
                        
                        if (duplicateStep) {
                          // Show confirmation modal for duplicate name
                          showConfirm({
                            title: "Duplicate Step Name",
                            message: `Another step already has the name "${newName.trim()}". Are you sure you want to use this name? This could be confusing for users.`,
                            variant: "warning",
                            onConfirm: async () => {
                              try {
                                await secureHelpers.put(`/api/steps/${step.id}`, {
                                  id: step.id,
                                  surveyId: currentSurveyId,
                                  name: newName.trim(),
                                  description: step.description || null,
                                  order: step.order
                                });
                                
                                // Update local state
                                const newSteps = [...steps];
                                newSteps[stepIndex].title = newName.trim();
                                newSteps[stepIndex].name = newName.trim();
                                setSteps(newSteps);
                                
                                showToast({
                                  title: "Success",
                                  message: "Step renamed successfully",
                                  variant: "success"
                                });
                              } catch (error) {
                                showToast({
                                  title: "Error",
                                  message: `Failed to rename step: ${error.message}`,
                                  variant: "error"
                                });
                              }
                            }
                          });
                        } else {
                          // No duplicate, proceed with rename
                          try {
                            await secureHelpers.put(`/api/steps/${step.id}`, {
                              id: step.id,
                              surveyId: currentSurveyId,
                              name: newName.trim(),
                              description: step.description || null,
                              order: step.order
                            });
                            
                            // Update local state
                            const newSteps = [...steps];
                            newSteps[stepIndex].title = newName.trim();
                            newSteps[stepIndex].name = newName.trim();
                            setSteps(newSteps);
                            
                            showToast({
                              title: "Success",
                              message: "Step renamed successfully",
                              variant: "success"
                            });
                          } catch (error) {
                            showToast({
                              title: "Error",
                              message: `Failed to rename step: ${error.message}`,
                              variant: "error"
                            });
                          }
                        }
                      }
                    }}
                    title="Rename step"
                    aria-label={`Rename step ${stepIndex + 1}`}
                  >
                    Rename
                  </button>
                  <button
                    type="button"
                    className="btn btn-sm btn-outline-secondary"
                    onClick={async () => {
                      const newDescription = prompt("Enter description for this step (or leave empty to remove):", step.description || "");
                      if (newDescription !== null) {
                        try {
                          await secureHelpers.put(`/api/steps/${step.id}`, {
                            id: step.id,
                            surveyId: currentSurveyId,
                            name: step.title || step.name,
                            description: newDescription.trim() || null,
                            order: step.order
                          });
                          
                          // Update local state
                          const newSteps = [...steps];
                          newSteps[stepIndex].description = newDescription.trim() || null;
                          setSteps(newSteps);
                          
                          showToast({
                            title: "Success",
                            message: "Step description updated successfully",
                            variant: "success"
                          });
                        } catch (error) {
                          showToast({
                            title: "Error",
                            message: `Failed to update step description: ${error.message}`,
                            variant: "error"
                          });
                        }
                      }
                    }}
                    title="Edit step description"
                    aria-label={`Edit description for step ${stepIndex + 1}`}
                  >
                    Edit Description
                  </button>
                <button
                  type="button"
                  className="btn btn-sm btn-outline-danger"
                  onClick={() => deleteStep(stepIndex)}
                  title="Delete step"
                  aria-label={`Delete step ${stepIndex + 1}`}
                >
                  Delete
                </button>
                </div>
              </div>
              {step.questions.length === 0 ? (
                <p className="text-muted">No questions added yet</p>
              ) : (
                <div>
                  {step.questions.map((q, qIndex) => {
                    // Calculate global question number across all steps
                    let globalQuestionNumber = 0;
                    for (let i = 0; i < stepIndex; i++) {
                      globalQuestionNumber += steps[i].questions.length;
                    }
                    globalQuestionNumber += qIndex + 1;
                    
                    // Parse typeParameters and layout - handle if already parsed or still string
                    let params = {};
                    let layoutData = {};
                    
                    try {
                      params = typeof q.typeParameters === 'string' ? JSON.parse(q.typeParameters) : 
                               q.typeParameters || {};
                    } catch (e) {
                      console.warn('Failed to parse typeParameters:', q.typeParameters, e);
                    }
                    
                    try {
                      layoutData = typeof q.layout === 'string' ? JSON.parse(q.layout) : q.layout || {};
                    } catch (e) {
                      console.warn('Failed to parse layout:', q.layout, e);
                    }
                    
                    // Type is already a string from the backend
                    const typeName = q.type; // Already "MultipleChoice", "singleInput", or "inputList"
                    
                    return (
                      <div key={qIndex} className="card mb-2" style={hasBrokenRef(q) ? { border: '2px solid #dc3545' } : {}}>
                        <div className="card-body">
                          <div className="d-flex justify-content-between space-between align-items-start">
                            <div style={{ flex: 1 }}>
                              <div style={{ marginBottom: '0.75rem' }}>
                                <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif", fontSize: 'var(--font-lg)', color: 'var(--bs-dark)' }}>
                                  Q{globalQuestionNumber}: {q.text}
                                </span>
                                {hasBrokenRef(q) && (
                                  <span style={{ color: '#dc3545', fontSize: 'var(--font-xs)', marginLeft: '0.5rem', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                                    ⚠️ Has broken references
                                  </span>
                                )}
                                {q.description && (
                                  <p style={{ fontSize: 'var(--font-sm)', color: '#666', marginTop: '0.25rem', marginBottom: 0 }}>
                                    {q.description}
                                  </p>
                                )}
                              </div>

                              {/* Question Type and Input Type */}
                              <div style={{ marginBottom: '0.5rem' }}>
                                <span style={{ 
                                  display: 'inline-block',
                                  backgroundColor: '#e8d5d5', 
                                  color: 'var(--bs-dark)',
                                  padding: '0.25rem 0.6rem',
                                  borderRadius: '4px',
                                  fontSize: 'var(--font-xs)',
                                  fontFamily: "'Neutraface-Demi', Arial, sans-serif",
                                  marginRight: '0.5rem'
                                }}>
                                  {typeName === "singleInput" ? "Single Input" : 
                                   typeName === "multipleChoice" ? "Multiple Choice" : 
                                   typeName === "inputList" || typeName === "InputList" ? "Input List" :
                                   typeName === "referenceValue" || typeName === "ReferenceValue" ? "Reference Value" : typeName}
                                </span>
                                {(() => {
                                  const inputTypeValue = params.InputType || params.inputType || params.Questions?.[0]?.InputType || params.Questions?.[0]?.inputType;
                                  if (inputTypeValue) {
                                    return (
                                      <span style={{ 
                                        display: 'inline-block',
                                        backgroundColor: '#f8f9fa', 
                                        color: 'var(--bs-dark)',
                                        padding: '0.25rem 0.6rem',
                                        borderRadius: '4px',
                                        fontSize: 'var(--font-xs)',
                                        border: '1px solid #ddd'
                                      }}>
                                        {inputTypeValue === "boolean" ? "Yes/No" : inputTypeValue}
                                      </span>
                                    );
                                  }
                                })()}
                              </div>

                              {/* Constraints */}
                              {q.constraints && q.constraints.length > 0 && (
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <span style={{ fontSize: 'var(--font-xs)', fontFamily: "'Neutraface-Demi', Arial, sans-serif", marginRight: '0.5rem' }}>
                                    Validation:
                                  </span>
                                  {q.constraints.map((constraint, idx) => {
                                    let qHeadings = [];
                                    let qRowLabels = [];
                                    try {
                                      const qLayout = typeof q.layout === 'string' ? JSON.parse(q.layout) : (q.layout || {});
                                      qHeadings = qLayout.columnHeadings || [];
                                    } catch (_) {}
                                    try {
                                      const qParams = typeof q.typeParameters === 'string' ? JSON.parse(q.typeParameters) : (q.typeParameters || {});
                                      const allItems = qParams.Questions || [];
                                      if (qHeadings.length > 0) {
                                        // Multi-column: items stored as "RowLabel - ColHeading".
                                        // Take every colCount-th item (first column only) and strip the suffix.
                                        const colCount = qHeadings.length;
                                        qRowLabels = allItems
                                          .filter((_, i) => i % colCount === 0)
                                          .map(item => {
                                            const raw = typeof item === 'string' ? item : (item.Label || item.label || '');
                                            for (const h of qHeadings) {
                                              if (raw.endsWith(` - ${h}`)) return raw.slice(0, -(h.length + 3));
                                            }
                                            return raw;
                                          });
                                      } else {
                                        qRowLabels = allItems.map(item =>
                                          typeof item === 'string' ? item : (item.Label || item.label || '')
                                        );
                                      }
                                    } catch (_) {}
                                    return (
                                    <span 
                                      key={idx}
                                      style={{
                                        display: 'inline-block',
                                        backgroundColor: '#FCED21',
                                        color: 'var(--bs-dark)',
                                        padding: '0.2rem 0.5rem',
                                        borderRadius: '12px',
                                        fontSize: 'var(--font-xs)',
                                        marginRight: '0.5rem',
                                        marginBottom: '0.25rem'
                                      }}
                                    >
                                      {parseConstraint(constraint, qHeadings, qRowLabels)}
                                    </span>
                                    );
                                  })}
                                </div>
                              )}

                              {/* Conditions */}
                              {q.conditions && q.conditions.length > 0 && (
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <span style={{ fontSize: 'var(--font-xs)', fontFamily: "'Neutraface-Demi', Arial, sans-serif", marginRight: '0.5rem' }}>
                                    Conditional:
                                  </span>
                                  <span style={{ fontSize: 'var(--font-xs)', color: '#888', fontStyle: 'italic', marginRight: '0.5rem' }}>Hover for more info</span>
                                  {q.conditions.map((condition, idx) => (
                                    <TipBadge
                                      key={idx}
                                      tooltip={parseCondition(condition)}
                                      badgeStyle={{
                                        display: 'inline-block',
                                        backgroundColor: '#FFC60B',
                                        color: 'var(--bs-dark)',
                                        padding: '0.2rem 0.5rem',
                                        borderRadius: '12px',
                                        fontSize: 'var(--font-sm)',
                                        marginRight: '0.5rem',
                                        marginBottom: '0.25rem',
                                        cursor: 'default'
                                      }}
                                    >
                                      {parseConditionShort(condition)}
                                    </TipBadge>
                                  ))}
                                </div>
                              )}

                              {/* Triggers — shown on the SOURCE question to signal it drives visibility of others */}
                              {conditionTargetMap[q.id]?.length > 0 && (
                                <div style={{ marginBottom: '0.5rem' }}>
                                  <span style={{ fontSize: 'var(--font-xs)', fontFamily: "'Neutraface-Demi', Arial, sans-serif", marginRight: '0.5rem' }}>
                                    Triggers:
                                  </span>
                                  <span style={{ fontSize: 'var(--font-xs)', color: '#888', fontStyle: 'italic', marginRight: '0.5rem' }}>Hover for more info</span>
                                  {conditionTargetMap[q.id].map(depQNum => (
                                    <TipBadge
                                      key={depQNum}
                                      tooltip={`Q${depQNum} is only shown based on this question's answer`}
                                      badgeStyle={{
                                        display: 'inline-block',
                                        backgroundColor: '#d4edda',
                                        color: '#155724',
                                        border: '1px solid #c3e6cb',
                                        padding: '0.2rem 0.5rem',
                                        borderRadius: '12px',
                                        fontSize: 'var(--font-xs)',
                                        marginRight: '0.5rem',
                                        marginBottom: '0.25rem',
                                        cursor: 'default'
                                      }}
                                    >
                                      ↓ Q{depQNum}
                                    </TipBadge>
                                  ))}
                                </div>
                              )}

                              {/* Column Headings */}
                              {layoutData.columnHeadings && layoutData.columnHeadings.length > 0 && (
                                <div style={{ marginBottom: '0.5rem', fontSize: 'var(--font-sm)', color: '#666' }}>
                                  <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Columns:</span> {layoutData.columnHeadings.join(" | ")}
                                </div>
                              )}

                              {/* Total Field Note */}
                              {layoutData.totalFieldNote && (
                                <div style={{ marginBottom: '0.5rem', fontSize: 'var(--font-sm)', color: '#f16d09', fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>
                                  Total note: {layoutData.totalFieldNote}
                                </div>
                              )}

                              {/* Choices or Items */}
                              {(() => {
                                // Handle both Choices (PascalCase) and choices (lowercase)
                                const choicesArray = params.Choices || params.choices || [];
                                if (typeName === "multipleChoice" && choicesArray.length > 0) {
                                  return (
                                    <div style={{ fontSize: 'var(--font-sm)', color: '#666' }}>
                                      <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Choices:</span> {choicesArray.map(c => c.Label || c.label).join(", ")}
                                      {(params.MultipleSelectAllowed || params.multipleSelectAllowed) && <span style={{ color: '#f16d09', marginLeft: '0.5rem' }}>(Multiple select)</span>}
                                    </div>
                                  );
                                } else if ((typeName === "referenceValue" || typeName === "ReferenceValue") && (params.ReferenceDisplayMode === "tableRow" || layoutData.referenceDisplayMode === "tableRow") && (params.ReferenceColumns || layoutData.referenceColumns)?.length > 0) {
                                  const summaryReferenceColumns = params.ReferenceColumns || layoutData.referenceColumns || [];
                                  const allSavedQuestions = steps.flatMap(savedStep => savedStep.questions || []);
                                  return (
                                    <div style={{ fontSize: 'var(--font-sm)', color: '#666' }}>
                                      <div>
                                        <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Reference row label:</span> {params.ReferenceRowLabel || layoutData.referenceRowLabel || q.text}
                                      </div>
                                      <div>
                                        <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Columns:</span> {summaryReferenceColumns.map(column => column.Heading).join(" | ")}
                                      </div>
                                      <div>
                                        <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Column sources:</span> {summaryReferenceColumns.map((column, index) => `${column.Heading || `Column ${index + 1}`}: ${(column.ReferenceSources || []).length} source${(column.ReferenceSources || []).length !== 1 ? 's' : ''}`).join(", ")}
                                      </div>
                                      {summaryReferenceColumns.map((column, index) => (
                                        <div key={index} style={{ marginTop: '0.25rem' }}>
                                          <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>{column.Heading || `Column ${index + 1}`} adds:</span>{" "}
                                          {(column.ReferenceSources || []).length > 0
                                            ? (column.ReferenceSources || []).map(source => getSavedSourceLabel(source, allSavedQuestions)).join(" + ")
                                            : "No sources selected"}
                                        </div>
                                      ))}
                                    </div>
                                  );
                                } else if ((typeName === "inputList" || typeName === "InputList") && params.Questions && params.Questions.length > 0) {
                                  return (
                                    <div style={{ fontSize: 'var(--font-sm)', color: '#666' }}>
                                      <span style={{ fontFamily: "'Neutraface-Demi', Arial, sans-serif" }}>Fields:</span> {params.Questions.map(i => i.Label || i.label).join(", ")}
                                      {layoutData.showTotal && <span style={{ color: '#f16d09', marginLeft: '0.5rem' }}>(+ Total)</span>}
                                      {layoutData.showGrandTotal && layoutData.columnHeadings && layoutData.grandTotalColumns && 
                                       layoutData.grandTotalColumns.length > 0 && 
                                       layoutData.grandTotalColumns.length < layoutData.columnHeadings.length && (
                                        <span style={{ color: '#f16d09', marginLeft: '0.5rem', fontWeight: 'bold' }}>
                                          (Grand Total: {layoutData.grandTotalColumns.map(idx => layoutData.columnHeadings[idx]).join(' + ')})
                                        </span>
                                      )}
                                    </div>
                                  );
                                }
                              })()}
                            </div>
                            
                            <div className="d-flex gap-2" style={{ flexShrink: 0 }}>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  moveQuestion(stepIndex, qIndex, 'up');
                                }}
                                disabled={qIndex === 0}
                                title="Move up"
                                aria-label={`Move question ${qIndex + 1} up`}
                                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                              >
                                ↑
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-secondary"
                                onClick={(e) => {
                                  e.preventDefault();
                                  e.stopPropagation();
                                  moveQuestion(stepIndex, qIndex, 'down');
                                }}
                                disabled={qIndex === step.questions.length - 1}
                                title="Move down"
                                aria-label={`Move question ${qIndex + 1} down`}
                                style={{ cursor: 'pointer', pointerEvents: 'auto' }}
                              >
                                ↓
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-primary"
                                onClick={() => editQuestion(stepIndex, qIndex)}
                              >
                                Edit
                              </button>
                              <button
                                type="button"
                                className="btn btn-sm btn-danger"
                                onClick={() => removeQuestion(stepIndex, qIndex)}
                              >
                                Remove
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              )}
            </div>
          ))}
        </section>
      )}

      {steps.length > 0 }
      <div className="d-flex gap-2 align-items-center">
        <Button 
          onClick={saveSurvey} 
          disabled={!currentSurveyId || !surveyTitle.trim()}
        >
          Save Survey
        </Button>
       
        <Button 
          onClick={previewSurvey} 
          disabled={!currentSurveyId || !surveyTitle.trim()}
        >
           Preview Survey
        </Button>
        <Button 
          onClick={openPublishDialog} 
          disabled={!currentSurveyId || !surveyTitle.trim() || isSaving}
          variant="success"
        >
          {isSaving ? "Saving..." : "Go to Publish Settings"}
        </Button>
        {surveySaved && (
          <small className="text-success">
             {`Survey ${surveyTitle} has been saved`}
          </small>
        )}
       <Button
  onClick={() => {
    if (confirm("This will clear the form but won't delete the saved survey. Continue?")) {
      // ✅ CLEAR ALL STATE COMPLETELY
      setQuestions([]);
      setSteps([]);
      setSurveyTitle("");
      setSurveyInstructions("");
      setCurrentSurveyId(null);
      setSurveySaved(false);
      setSurveyTags([]);
      setSurveyRoles([]);
      
      // ✅ Clear ALL question-related state
      setSubQuestions([]);
      setQuestionText("");
      setQuestionDescription("");
      setSelectedStep(null);
      setIsEditing(false);
      //setEditingQuestionId(null);
      setEditingOriginalStepIndex(null);
      setConstraints([]);
      setConditions([]);
      setIsRequired(false);
      setInputType("");
      setMultipleSelectAllowed(false);
      setIsOrderedSelection(false);
      setMultipleSelectLimit("");
      setIncludeInTitle(false);
      setColumnHeadings([]);
      setSectionHeading("");
      setShowTotal(false);
      setShowGrandTotal(false);
      setGrandTotalColumns([]);
      setTotalFieldLabel("");
      setTotalFieldNote("");
      setDisplayAsRowLabel(false);
      setDisplayAsHeading(false);
      setDisplayAsInformational(false);
      setDisplayAsHorizontal(true);
      refFormula.setSources([]);
      refFormula.setFormulaParts([]);
      refFormula.setBuilding(false);
      setFormulaParts([]);
      setBuildingFormula(false);
      
    }
  }}
  variant="secondary"
  style={{ marginLeft: "auto" }}
>
  Clear Form
</Button>
      </div>

{/* Floating Navigation Menu */}
{showFloatingMenu && (
  <nav
    aria-label="Survey builder shortcuts"
    style={{
    position: 'fixed',
    bottom: '2rem',
    right: '2rem',
    display: 'flex',
    flexDirection: 'column',
    gap: '0.5rem',
    zIndex: 1000
  }}>
    <FloatingNavButton
      id="survey"
      icon={<FaFileAlt />}
      label="Survey Details"
      onClick={scrollAdminToTop}
      expandedButton={expandedButton}
      setExpandedButton={setExpandedButton}
    />
    <FloatingNavButton
      id="steps"
      icon={<FaListOl />}
      label="Add Step"
      onClick={() => {
        const stepSection = document.getElementById('add-survey-steps-heading');
        if (stepSection) {
          stepSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }}
      expandedButton={expandedButton}
      setExpandedButton={setExpandedButton}
    />
    <FloatingNavButton
      id="questions"
      icon={<FaQuestionCircle />}
      label="Add Question"
      onClick={() => {
        const questionSection = document.getElementById('build-question-details-heading');
        if (questionSection) {
          questionSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }
      }}
      expandedButton={expandedButton}
      setExpandedButton={setExpandedButton}
    />
    <FloatingNavButton
      id="top"
      icon={<FaArrowUp />}
      label="Back to Top"
      onClick={scrollAdminToTop}
      expandedButton={expandedButton}
      setExpandedButton={setExpandedButton}
    />
  </nav>
)}

{/* Publish Dialog - Modal */}

<PublishSurveyDialog
        show={showPublishDialog}
        onClose={() => setShowPublishDialog(false)}
        surveyTitle={surveyTitle}
        surveyRoles={surveyRoles}
        surveyId={currentSurveyId}
        onPublish={publishSurvey}
        hospices={hospices}
        selectedHospices={selectedHospices}
        setSelectedHospices={setSelectedHospices}
        publishToAll={publishToAll}
        setPublishToAll={setPublishToAll}
        startDate={startDate}
        setStartDate={setStartDate}
        endDate={endDate}
        setEndDate={setEndDate}
        autoClose={autoClose}
        setAutoClose={setAutoClose}
      />
      
      {/* Toast Notifications */}
      <Toast 
        show={toast.show}
        title={toast.title}
        message={toast.message}
        onClose={hideToast}
        variant={toast.variant}
        delay={toast.delay}
        position={toast.position}
      />
      
      {/* Confirmation Modal */}
      <ConfirmModal
        show={confirmModal.show}
        title={confirmModal.title}
        message={confirmModal.message}
        onConfirm={confirmModal.onConfirm}
        onCancel={hideConfirm}
        variant={confirmModal.variant}
        style={{ zIndex: 20000 }}
      />

      {/* Feature-test survey seeder — only visible during local development */}
      {import.meta.env.DEV && (
        <div style={{ margin: '2rem', padding: '1rem', border: '2px dashed #dc3545', borderRadius: '8px', backgroundColor: '#fff5f5' }}>
          <p style={{ color: '#dc3545', fontFamily: "'Neutraface-Demi', Arial, sans-serif", marginBottom: '0.5rem' }}>
            🧪 Dev tool — not visible in production
          </p>
          <Button variant="danger" onClick={() => seedTestSurvey(secureHelpers)}>
            Seed Feature-Test Survey
          </Button>
        </div>
      )}
    </div>
  );

}
